import { LinkedItem, Morphs, Textures, Zone } from '@types'
import type { AnyID, Textures as TexturesType } from '@types'
import {
  getRegletaEffectiveMm,
  isKARBCatalog,
  isKBRCatalog,
  isKbrGolaRinconeroCatalog,
  isKbrRinconeroFamilyCatalog,
  kbrGlbBodySitsOnFloor,
  shouldSkipAutoZocalo,
} from '@utils/catalog/kbrCatalog'
import { ensureSidebarMorphs, itemHasRegletaControl } from '@utils/catalog/ensureSidebarMorphs'
import { isZocaloZone } from '@utils/debug/compareLinkedItemMaterials'
import { isRinconeroCatalog, parseRinconeroCatalogCode } from '@utils/sceneObjects/snappedSides'
import {
  isMalGolaFurnitureUnit,
  snapMalGolaAltoMm,
} from '@context/utils/linked/linkedUnitVariants'
import { Box3, Matrix4, Mesh, Object3D, Quaternion, Vector3 } from 'three'

/**
 * Encimera, costado, zócalo y frontal sin morphs en API (modificarAncho/Fondo/Alto en cabecera).
 * Sidebar y escala 3D como unidad universal (grupo scale).
 */
export const FREE_DIMENSION_TPC_NO_MORPH_CATALOG_IDS = [
  'ENCIMERA B3200',
  'TPC COSTADO ENCIMERA',
  'ZOCALO 4000',
  'ZOCALOTIRA',
  'ZOCALO TIRA',
  'FRONTAL',
] as const

/**
 * Encimeras TPC: ancho/fondo libres (escala grupo); morph Alto del API con min/max (p. ej. 10–30 mm).
 */
export const FREE_DIMENSION_ENCIMERA_ANCHO_CATALOG_IDS = [
  'ENCIMERA ANCHO 100',
  'ENCIMERA ANCHO 120',
  'ENCIMERA ANCHO 150',
  'ENCIMERA ANCHO 20',
  'ENCIMERA ANCHO 200',
  'ENCIMERA ANCHO 30',
  'ENCIMERA ANCHO 40',
  'ENCIMERA ANCHO 50',
  'ENCIMERA ANCHO 60',
  'ENCIMERA ANCHO 80',
  'ENCIMERA ANCHO 90',
] as const

/**
 * Laterales cascada: ancho fijo por catálogo; alto por morph Alto API; fondo libre por escala grupo.
 */
export const FREE_DIMENSION_LATERAL_CASCADA_ANCHO_CATALOG_IDS = [
  'LATERAL CASCADA 20',
  'LATERAL CASCADA 30',
  'LATERAL CASCADA 40',
  'LATERAL CASCADA 50',
  'LATERAL CASCADA 60',
  'LATERAL CASCADA 80',
  'LATERAL CASCADA 90',
  'LATERAL CASCADA 100',
  'LATERAL CASCADA 120',
  'LATERAL CASCADA 150',
  'LATERAL CASCADA 200',
] as const

/** TPC sin morphs + encimeras ancho (todas en FREE_DIMENSION_LINKED_CATALOG_IDS). */
export const FREE_DIMENSION_NO_MORPH_TPC_CATALOG_IDS = [
  ...FREE_DIMENSION_TPC_NO_MORPH_CATALOG_IDS,
  ...FREE_DIMENSION_ENCIMERA_ANCHO_CATALOG_IDS,
  ...FREE_DIMENSION_LATERAL_CASCADA_ANCHO_CATALOG_IDS,
] as const

/**
 * Unidades sin morphs en getUnidad (morphs: null).
 * Ancho/Fondo/Alto se simulan escalando el grupo 3D (todas las mallas igual).
 */
export const FREE_DIMENSION_LINKED_CATALOG_IDS = [
  ...FREE_DIMENSION_NO_MORPH_TPC_CATALOG_IDS,
  'CB9060',
  'CC14060',
  'CC15060',
  'CC16060',
  'CC23060',
  'CC24060',
  'LA3035',
  'LA3235',
  'LA3535',
  'LA3735',
  'LA4035',
  'LA4235',
  'LA4535',
  'LA4735',
  'LA5035',
  'LA5235',
  'LA8035',
  'LA8235',
  'LA9035',
  'LA9235',
  'LB8060',
  'LC13860',
  'LC14060',
  'LC14860',
  'LC15060',
  'LC22060',
  'LC23060',
  'RA805',
  'RA825',
  'RA905',
  'RA925',
  'RB805',
  'RB905',
  'RC1405',
  'RC1505',
  'RC2205',
  'RC2305',
] as const

/** Offset alto regletas RB/RC (store = catálogo + 100). Laterales LA/LB/LC/CC/CB/RA: 0. */
export const FREE_DIMENSION_ALTO_OFFSET_MM = 100

/** LB8060 / CB9060: lateral MBJ + encimera (sync ancho/fondo hijo; alto store = catálogo 900). */
export const FREE_DIMENSION_LB8060_STYLE_CATALOG_IDS = ['LB8060', 'CB9060'] as const

/** Columnas CC: store +100 y escala layout×(cat+100)/(scaleBase×cat). */
export const FREE_DIMENSION_CC_COLUMN_CATALOG_IDS = [
  'CC14060',
  'CC15060',
  'CC16060',
  'CC23060',
  'CC24060',
] as const

/** Alturas (cm) de laterales CC disponibles en catálogo (CC14060 → 140, …). */
const CC_LATERAL_HEIGHTS_CM = FREE_DIMENSION_CC_COLUMN_CATALOG_IDS.map(
  (id) => parseCcLateralCatalogId(id)?.heightCm ?? 0,
).filter((heightCm) => heightCm > 0)

/** Formato CC{HHH}{DD}: p.ej. CC23060 = 230 cm alto, 60 cm fondo. */
export function parseCcLateralCatalogId(
  catalogID: string,
): { heightCm: number; depthCm: number } | null {
  const match = catalogID.match(/^CC(\d{3})(\d{2})$/i)
  if (!match) return null
  return {
    heightCm: parseInt(match[1], 10),
    depthCm: parseInt(match[2], 10),
  }
}

/**
 * Alto de catálogo propio (mm) de costados columna CC/LC (CC24060 → 2400).
 * 0 si no es CC/LC. Usar para no comprimir el lateral al alto de un MBJ anfitrión.
 */
export function getColumnLateralOwnCatalogAltoMm(item?: {
  catalogID?: string | number
  codigo?: string | number
  referencia?: string | number
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
} | null): number {
  if (!item) return 0
  const code = getLinkedItemCatalogCode(item)
  if (!code) return 0
  const catY = Number(item.freeDimensionCatalogMm?.y ?? 0)
  const cc = parseCcLateralCatalogId(code)
  if (cc) return catY > 0 ? Math.round(catY) : cc.heightCm * 10
  const lc = code.match(/^LC(\d{3})(\d{2})$/i)
  if (lc) {
    const fromId = parseInt(lc[1], 10) * 10
    return catY > 0 ? Math.round(catY) : fromId
  }
  return 0
}

/** Ajusta la altura (cm) al lateral CC válido más cercano en catálogo. */
export function resolveCcLateralHeightCm(heightCm: number): number {
  if (CC_LATERAL_HEIGHTS_CM.length === 0) return heightCm

  let closest = CC_LATERAL_HEIGHTS_CM[0]
  let minDiff = Math.abs(heightCm - closest)
  for (const validHeight of CC_LATERAL_HEIGHTS_CM) {
    const diff = Math.abs(heightCm - validHeight)
    if (diff < minDiff) {
      minDiff = diff
      closest = validHeight
    }
  }
  return closest
}

/** Construye el catalogID CC a partir de la altura (cm) del mueble. */
export function buildCcLateralCatalogID(heightCm: number): string {
  const resolvedHeightCm = resolveCcLateralHeightCm(heightCm)
  const depthCm =
    parseCcLateralCatalogId(FREE_DIMENSION_CC_COLUMN_CATALOG_IDS[0])?.depthCm ?? 60
  return `CC${resolvedHeightCm}${depthCm}`
}

/** Alturas (cm) de laterales LA disponibles en catálogo (LA9035 → 90, …). */
export const MAL_LATERAL_HEIGHTS_CM = [30, 35, 37, 40, 42, 45, 47, 50, 52, 80, 82, 90, 92] as const

/** Kit alto campana (KACB*, textura Campana, etc.). */
export function isMalCampanaLinkedItem(
  item: {
    catalogID?: string | number
    codigo?: string | number
    description?: string
    long?: string
    textures?: Array<{ acabado?: string; zone?: string; zoneDisplayName?: string }>
  } | null | undefined,
): boolean {
  if (!item) return false

  const catalog = getLinkedItemCatalogCode(item)
  if (catalog.startsWith('KACB')) return true

  const desc = `${item.description ?? ''} ${item.long ?? ''}`.toLowerCase()
  if (desc.includes('campana')) return true

  return (item.textures ?? []).some((texture) => {
    const labels = [texture.acabado, texture.zone, texture.zoneDisplayName].map((value) =>
      String(value ?? '').toLowerCase(),
    )
    return labels.some((label) => label.includes('campana'))
  })
}

/**
 * Altura (cm) para elegir lateral LA en MAL.
 *
 * Campanas:
 * - ~850 mm (KACB8560): lateral 90 cm (LA9035) — regla de producto.
 * - ~820 mm (KACB82902): lateral 82 cm (LA8235) — misma altura del cuerpo.
 */
export function getMalLateralHeightCm(
  item: {
    height?: number
    catalogID?: string | number
    codigo?: string | number
    description?: string
    long?: string
    textures?: Array<{ acabado?: string; zone?: string; zoneDisplayName?: string }>
  } | null | undefined,
): number {
  const itemHeightMm = Math.round(item?.height || 0)
  const heightInCm = Math.round(itemHeightMm / 10)

  if (isMalCampanaLinkedItem(item)) {
    // KACB8560 / kits ~85 cm → LA9035
    if (itemHeightMm >= 840 && itemHeightMm <= 860) {
      return 90
    }
    // KACB82902 (820 mm) y similares: lateral a la altura del cuerpo → LA8235
  }

  return heightInCm
}

/**
 * Resuelve catalogID LA{altura}35 para un MAL según altura (cm).
 * Siempre el LA más cercano (luego se escala al alto/fondo del mueble).
 */
export function resolveMalLateralCatalogID(heightInCm: number): string | null {
  if (!Number.isFinite(heightInCm) || heightInCm <= 0) return null
  let closest = MAL_LATERAL_HEIGHTS_CM[0]
  let minDiff = Math.abs(heightInCm - closest)

  for (const validHeight of MAL_LATERAL_HEIGHTS_CM) {
    const diff = Math.abs(heightInCm - validHeight)
    if (diff < minDiff) {
      minDiff = diff
      closest = validHeight
    }
  }

  return `LA${closest}35`
}

/** Otros LA por si el preferido 404 (mismo fondo 35). */
export function getMalLateralCatalogFallbacks(preferredCatalogID: string): string[] {
  const preferred = preferredCatalogID.toUpperCase()
  return MAL_LATERAL_HEIGHTS_CM.map((h) => `LA${h}35`).filter((id) => id !== preferred)
}

/**
 * Resuelve el lateral para muebles bajos (MBJ).
 * Solo IDs que existen en catálogo (LB8060 / CB9060). El alto/fondo exactos
 * del mueble se aplican después con applyLateralSceneDimensionsFromFurniture
 * (scale/morph), así un fondo 500 o alto 1200 no inventa un CB inexistente.
 */
export function resolveMbjLateralCatalogID(heightMm: number, depthMm: number): string | null {
  const height = Math.round(heightMm)
  if (height <= 0) return null
  // depthMm se usa al escalar el lateral; el GLB base es siempre 60 cm.
  void depthMm
  if (height > 950) return 'CB9060'
  return 'LB8060'
}

/** Catálogos MBJ de respaldo si el preferido no carga. */
export function getMbjLateralCatalogFallbacks(preferredCatalogID: string): string[] {
  const preferred = preferredCatalogID.toUpperCase()
  const pool = ['LB8060', 'CB9060']
  return pool.filter((id) => id !== preferred)
}

/**
 * Alto (mm) para resolver el catalogID del lateral y para sync al abrir/editar.
 * Contrato nuevo (zócalo hijo): store del mueble = solo cuerpo → el lateral
 * (CB9060/LB8060/CC24060…) debe cubrir cuerpo + zócalo (p.ej. 800→900, 2300→2400).
 * Legacy: store ya = mueble completo → no sumar.
 */
export function getLateralCatalogHeightMm(
  item: {
    height?: number
    tipoNormalizado?: string
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    hasIndependentZocalo?: boolean
    independentZocaloHeightMm?: number
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    morphs?: Morphs[]
  } | null | undefined,
): number {
  const bodyHeight = Math.round(item?.height || 0)
  if (bodyHeight <= 0) return 0

  if (usesFreeDimensionAltoOffset(item)) {
    return bodyHeight
  }

  const type = String(item?.tipoNormalizado ?? '').toUpperCase()
  const hasIndepZocalo =
    item?.hasIndependentZocalo === true || shouldUseIndependentZocaloUnit(item)

  if (type === 'MBJ' || type === 'COL') {
    if (hasIndepZocalo) {
      const lift = getIndependentZocaloBodyLiftMm(item)
      // Store = cuerpo → lateral hasta encimera/tope visual (cuerpo + zócalo).
      if (lift > 0 && storeHeightIsIndependentZocaloBodyOnly(item)) {
        return bodyHeight + lift
      }
      return bodyHeight
    }
    // COL legacy sin tira hija: el lateral cubría cuerpo + hueco de zócalo.
    if (type === 'COL') return bodyHeight + FREE_DIMENSION_ALTO_OFFSET_MM
    return bodyHeight
  }

  return bodyHeight
}

/**
 * Alto visual (mm) del mesh MAL en escena.
 * - Dentro del rango de morph Alto: scaleY≈1 y el tamaño lo da el morph → usar store.height.
 * - Fuera de rango / glb ≠ catálogo: glbY×scaleY puede superar el store
 *   (p. ej. KACB catalog 750 / glb 800 / height 850 → ~907).
 * Nunca devolver menos que store.height (si no el lateral queda corto).
 */
export function getMalFurnitureVisualAltoMm(furniture: {
  height?: number
  width?: number
  thickness?: number
  morphs?: Morphs[]
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
  catalogID?: string | number
  codigo?: string | number
  tipoNormalizado?: string
  hasIndependentZocalo?: boolean
  independentZocaloHeightMm?: number
} | null | undefined): number {
  if (!furniture) return 0
  const storeH = Math.round(furniture.height || 0)
  if (!isMalFreeDimensionUnit(furniture) || !usesFreeDimensionScaling(furniture)) {
    return storeH
  }
  const glbY = furniture.freeDimensionGlbBaseMm?.y ?? 0
  const ratios = getMbjColFreeDimensionScaleRatios(furniture)
  if (glbY > 0 && ratios.y > 0) {
    const scaledGlb = Math.round(glbY * ratios.y)
    return Math.max(storeH, scaledGlb)
  }
  return storeH
}

/**
 * Alto/fondo que debe tener el lateral al colocarlo junto a un mueble ya dimensionado.
 * (Catálogo LA/LB/CC se elige por proximidad; luego se escala a estos mm exactos.)
 *
 * Usa siempre la geometría viva del LinkedItem (height / thickness), sin suelos de
 * catálogo: un mueble de fondo 300 debe producir un lateral de fondo 300.
 * Campana KACB*: el pick es LA90xx a propósito — no escalar el lateral al cuerpo 850
 * (si no queda ~100 mm más bajo que el kit / que el LA9035 de catálogo).
 */
export function getLateralSceneTargetDimensionsMm(furniture: {
  height?: number
  thickness?: number
  tipoNormalizado?: string
  catalogID?: string | number
  codigo?: string | number
  hasMorphs?: boolean
  hasIndependentZocalo?: boolean
  independentZocaloHeightMm?: number
  morphs?: Morphs[]
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
  width?: number
  description?: string
  long?: string
  textures?: Array<{ acabado?: string; zone?: string; zoneDisplayName?: string }>
} | null | undefined): { altoMm: number; fondoMm: number } {
  const tipo = String(furniture?.tipoNormalizado ?? '').toUpperCase()
  let altoMm =
    tipo === 'MAL'
      ? getMalFurnitureVisualAltoMm(furniture)
      : getLateralCatalogHeightMm(furniture)

  // Campana: altura de lateral = la del LA elegido (p.ej. 900), no la del cuerpo (850).
  if (tipo === 'MAL' && isMalCampanaLinkedItem(furniture)) {
    const pickCm = getMalLateralHeightCm(furniture)
    const pickMm = pickCm > 0 ? pickCm * 10 : 0
    if (pickMm > 0) {
      altoMm = Math.max(altoMm, pickMm)
    }
  }

  const fondoMm = Math.round(furniture?.thickness || 0)
  return { altoMm, fondoMm }
}

/** Columnas RA: store +100 y escala layout×(cat+100)/(scaleBase×cat). */
export const FREE_DIMENSION_RA_COLUMN_CATALOG_IDS = [
  'RA805',
  'RA825',
  'RA905',
  'RA925',
] as const

/** LC23060: store = catálogo − 100 (sidebar sigue en catálogo). */
export const FREE_DIMENSION_ALTO_OFFSET_MINUS_100_CATALOG_IDS = ['LC23060'] as const

const DIMENSION_MORPH_NAMES = ['Ancho', 'Fondo', 'Alto'] as const

export type FreeDimensionMm = { x: number; y: number; z: number }
export type FreeDimensionGlbBaseMm = FreeDimensionMm
export type FreeDimensionCatalogMm = FreeDimensionMm
export type FreeDimensionScaleBaseMm = FreeDimensionMm

export function getLinkedItemCatalogCode(
  item: { catalogID?: string | number; codigo?: string | number } | null | undefined,
): string {
  if (!item) return ''
  const id = item.catalogID ?? item.codigo
  return id != null ? String(id).trim().toUpperCase() : ''
}

/** Código de catálogo (PE / sufijos de acabado no cuentan). */
function normalizeFurnitureCatalogCode(value?: string | number | null): string {
  let code = String(value ?? '')
    .trim()
    .toUpperCase()
  if (!code) return ''
  if (code.startsWith('PE ')) code = code.slice(3).trim()
  code = code.replace(/\s+(LI|LA|RU|R2)(\s+GO)?$/i, '').replace(/\s+GO$/i, '').trim()
  return code
}

/**
 * Molduras/remates GV*: tira Zocalo 4000 como un MBJ.
 * El origen del GLB no suele estar en la base: el lift es 100 − AABB.min.y
 * para asentar el mesh sobre la tira (no un lift fijo de 100).
 */
export function isGvMoldingUnit(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    referencia?: string | number
  } | null,
): boolean {
  if (!item) return false
  return (
    normalizeFurnitureCatalogCode(item.catalogID).startsWith('GV') ||
    normalizeFurnitureCatalogCode(item.codigo).startsWith('GV') ||
    normalizeFurnitureCatalogCode(item.referencia).startsWith('GV')
  )
}

export function isStandaloneZocaloTiraCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const compact = getLinkedItemCatalogCode(item).replace(/[\s_-]+/g, '')
  return compact === 'ZOCALOTIRA'
}

export function isFreeDimensionLinkedItem(
  item: { catalogID?: string | number; codigo?: string | number } | null | undefined,
): boolean {
  if (isStandaloneZocaloTiraCatalog(item)) return true
  const code = getLinkedItemCatalogCode(item)
  return (FREE_DIMENSION_LINKED_CATALOG_IDS as readonly string[]).includes(code)
}

/** Mueble alto (MAL): dims libres con o sin morphs; no lleva zócalo tira.
 *  Laterales/costados (LA/RA…) a menudo vienen con tipoNormalizado=MAL en API:
 *  no son el mueble — no deben usar scale pure-group de MAL.
 */
export function isMalFreeDimensionUnit(
  item?: {
    tipoNormalizado?: string
    catalogID?: string | number
    codigo?: string | number
  } | null,
): boolean {
  if (isFreeDimensionLateralOrCostadoCatalog(item)) return false
  return String(item?.tipoNormalizado ?? '').toUpperCase() === 'MAL'
}

/**
 * Calcula freeDimensionCatalogMm / glbBase / scaleBase si faltan
 * (proyectos antiguos o MAL dibujados antes del free-dim).
 * No muta el item: los proxies Immmer del store son no-extensibles fuera de set().
 */
export function resolveFreeDimensionCatalogBases(item: {
  width?: number
  height?: number
  thickness?: number
  morphs?: Morphs[]
  glbSize?: { x?: number; y?: number; z?: number }
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
  freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
}): {
  freeDimensionCatalogMm: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm: FreeDimensionGlbBaseMm
  freeDimensionScaleBaseMm: FreeDimensionScaleBaseMm
  didCreate: boolean
} | null {
  const existing = item.freeDimensionCatalogMm
  if (existing?.x != null && existing.x > 0 && existing?.y != null && existing.y > 0) {
    const glbBase =
      item.freeDimensionGlbBaseMm?.x && item.freeDimensionGlbBaseMm?.y
        ? item.freeDimensionGlbBaseMm
        : {
            x: Math.round((item.glbSize?.x ?? 0) * 1000) || existing.x,
            y: Math.round((item.glbSize?.y ?? 0) * 1000) || existing.y,
            z: Math.round((item.glbSize?.z ?? 0) * 1000) || existing.z || existing.x,
          }
    return {
      freeDimensionCatalogMm: existing,
      freeDimensionGlbBaseMm: glbBase,
      freeDimensionScaleBaseMm:
        item.freeDimensionScaleBaseMm ??
        resolveFreeDimensionScaleBaseMm(existing, glbBase),
      didCreate: false,
    }
  }
  const morphMm = (name: 'Ancho' | 'Alto' | 'Fondo') => {
    const m = item.morphs?.find((morph) => morph.name === name)
    return Number(m?._default ?? m?.default ?? 0)
  }
  const x = morphMm('Ancho') || item.width || 0
  const y = morphMm('Alto') || item.height || 0
  const z = morphMm('Fondo') || item.thickness || 0
  if (x <= 0 || y <= 0) return null
  const freeDimensionCatalogMm: FreeDimensionCatalogMm = {
    x,
    y,
    z: z > 0 ? z : item.thickness || 0,
  }
  const glb = item.glbSize
  const freeDimensionGlbBaseMm: FreeDimensionGlbBaseMm =
    item.freeDimensionGlbBaseMm?.x && item.freeDimensionGlbBaseMm?.y
      ? item.freeDimensionGlbBaseMm
      : {
          x: glb?.x != null && glb.x > 0 ? Math.round(glb.x * 1000) : x,
          y: glb?.y != null && glb.y > 0 ? Math.round(glb.y * 1000) : y,
          z: glb?.z != null && glb.z > 0 ? Math.round(glb.z * 1000) : z || x,
        }
  return {
    freeDimensionCatalogMm,
    freeDimensionGlbBaseMm,
    freeDimensionScaleBaseMm: resolveFreeDimensionScaleBaseMm(
      freeDimensionCatalogMm,
      freeDimensionGlbBaseMm,
    ),
    didCreate: true,
  }
}

/**
 * Sella freeDimensionCatalogMm / glbBase si faltan.
 * Si el item está congelado (Zustand/Immer fuera de set()), no lanza:
 * solo devuelve false. Usar resolveFreeDimensionCatalogBases + lectura local
 * en paths de render, o llamar esto dentro de set() / sobre objetos planos.
 */
export function ensureFreeDimensionCatalogBases(item: {
  width?: number
  height?: number
  thickness?: number
  morphs?: Morphs[]
  glbSize?: { x?: number; y?: number; z?: number }
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
  freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
}): boolean {
  const resolved = resolveFreeDimensionCatalogBases(item)
  if (!resolved?.didCreate) return false
  try {
    item.freeDimensionCatalogMm = resolved.freeDimensionCatalogMm
    if (!item.freeDimensionGlbBaseMm?.x || !item.freeDimensionGlbBaseMm?.y) {
      item.freeDimensionGlbBaseMm = resolved.freeDimensionGlbBaseMm
    }
    item.freeDimensionScaleBaseMm = resolved.freeDimensionScaleBaseMm
    return true
  } catch {
    // Items del store Zustand/Immer son inmutables fuera de set()
    return false
  }
}

/** Catálogos regleta/columna sin morphs de malla: escala por grupo como universal (ancho/alto/fondo en store). */
export function usesFreeDimensionScaling(
  item: {
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    claveMenu?: string | null
    menuPrincipal?: string | null
  } | null | undefined,
): boolean {
  if (isFreeDimensionLinkedItem(item)) return true
  // MBJ/COL con zócalo independiente: cuerpo con dims libres (escala grupo).
  if (shouldUseIndependentZocaloUnit(item)) return true
  // MAL: Ancho/Fondo/Alto libres (con o sin morphs de malla).
  if (isMalFreeDimensionUnit(item)) return true
  // Lavadora / lavavajillas / vinoteca: dims por escala de grupo, sin tira de zócalo.
  return isApplianceMenuUnit(item)
}

/** Catálogo del zócalo como unidad hija (ya en free-dim TPC). */
export const INDEPENDENT_ZOCALO_CATALOG_ID = 'ZOCALO 4000'

/** Claves sceneGLB para el zócalo (API: "Zocalo 4000", lookup: "ZOCALO 4000"). */
export function independentZocaloSceneGlbKeys(
  codigo?: string | number | null,
): string[] {
  const keys = new Set<string>()
  keys.add(INDEPENDENT_ZOCALO_CATALOG_ID)
  if (codigo != null && String(codigo).trim()) {
    const raw = String(codigo).replace(/\s+/g, ' ').trim()
    keys.add(raw)
    keys.add(raw.toUpperCase())
  }
  return Array.from(keys)
}

/** Alto fijo del zócalo independiente (mm). El cuerpo del padre crece encima. */
export const INDEPENDENT_ZOCALO_HEIGHT_MM = 100

/** Alto efectivo del zócalo independiente (mm). */
export function getIndependentZocaloHeightMm(item?: {
  independentZocaloHeightMm?: number
} | null): number {
  const stored = Number(item?.independentZocaloHeightMm ?? 0)
  return stored > 0 ? stored : INDEPENDENT_ZOCALO_HEIGHT_MM
}

/**
 * Retranqueo frontal (mm) respecto al plano del frente del módulo.
 * Alinea la tira con el zócalo integrado del GLB (hasta que se elimine del mesh).
 */
export const INDEPENDENT_ZOCALO_FRONT_RECESS_MM = 30

/** Fondo de puerta MBJ (KBB 600) a medida de catálogo 93×93. */
const TWO_DOOR_L_MBJ_FONDO_MM = 600
const TWO_DOOR_L_CATALOG_ARM_MM = 930

function getTwoDoorLCatalogArmMm(parent: {
  catalogID?: string | number
  freeDimensionCatalogMm?: { x?: number; z?: number } | null
}): number {
  const parsed = parseRinconeroCatalogCode(parent.catalogID)
  if (parsed?.widthMm && parsed.widthMm > 0) return parsed.widthMm
  const catalog = Number(parent.freeDimensionCatalogMm?.z || parent.freeDimensionCatalogMm?.x || 0)
  return catalog > 0 ? catalog : TWO_DOOR_L_CATALOG_ARM_MM
}

/** Plano de puerta 600@930, escalado al ancho/fondo vivo. */
function getTwoDoorLScaledDoorPlaneMm(
  parent: {
    catalogID?: string | number
    width?: number
    thickness?: number
    freeDimensionCatalogMm?: { x?: number; z?: number } | null
  } | null | undefined,
  axis: 'x' | 'z',
): number {
  if (!parent) return TWO_DOOR_L_MBJ_FONDO_MM
  const catalogArm = getTwoDoorLCatalogArmMm(parent)
  const live = axis === 'z' ? Number(parent.thickness ?? 0) : Number(parent.width ?? 0)
  if (!(catalogArm > 0) || !(live > 0)) return TWO_DOOR_L_MBJ_FONDO_MM
  return TWO_DOOR_L_MBJ_FONDO_MM * (live / catalogArm)
}

/**
 * Extra mm del zócalo frontal (bajo la puerta) en rinconeros.
 * Alarga esa tira hacia la esquina L y desplaza el retorno “hacia atrás”
 * (brazo ciego) para empatar con el zócalo del MBJ colindante.
 */
export const INDEPENDENT_ZOCALO_RINCONERO_FRONT_EXTEND_MM = 30

/** option del hijo zócalo de retorno (L) en rinconeros; el frontal usa 9900. */
export const INDEPENDENT_ZOCALO_RETURN_OPTION = 9901

/**
 * Layout del zócalo en L para rinconeros (KBRB8010040, KBRB8010040I, etc.).
 * Sin I: puerta a la izquierda (frontX=0). Con I: puerta a la derecha.
 *
 * Frontal: bajo la puerta + extend hacia la esquina (empuja el retorno).
 * Retorno: en el extremo del frontal ya extendido (brazo ciego).
 *
 * Misma Y=0 / alto / retranqueo 30 mm que el zócalo de los MBJ vecinos.
 * Frontal: plano puerta = carcasa − regletaMaximo (720−120=600).
 * Retorno: crece con la regleta (puerta + regleta), alineado a la encimera.
 */
export type RinconeroIndependentZocaloLayout = {
  isIzquierda: boolean
  doorWidthMm: number
  /** Ancho de la tira frontal (= puerta + extend esquina). */
  frontStripWidthMm: number
  /** Fondo efectivo de la puerta (mm), no el OBB completo. */
  frontPlaneMm: number
  frontX: number
  returnLengthMm: number
  returnX: number
  returnZ: number
  returnRotY: number
}

/** Fondo de carcasa rinconero, p.ej. 720 = plano puerta 600 + regletaMaximo 120. */
export function getRinconeroCarcassDepthMm(parent: {
  thickness?: number
  regletaMaximo?: number | null
  freeDimensionCatalogMm?: { z?: number } | null
  freeDimensionGlbBaseMm?: { z?: number } | null
} | null | undefined): number {
  const storeT = Number(parent?.thickness ?? 0)
  const overhang = Number(parent?.regletaMaximo ?? 0)
  const catalogZ = Number(parent?.freeDimensionCatalogMm?.z ?? 0)
  const glbZ = Number(parent?.freeDimensionGlbBaseMm?.z ?? 0)
  const hint =
    catalogZ > 0 && glbZ > 0 && Math.abs(catalogZ - glbZ) <= 2
      ? catalogZ
      : glbZ > 0
        ? glbZ
        : catalogZ
  // Store ya es carcasa (720 ≈ GLB). Sumar regletaMax otra vez → 840 y el
  // zócalo/gola se adelantan ~120 mm respecto a la puerta 600.
  if (storeT > 0 && overhang > 0) {
    const asDoorPlus = storeT + overhang
    if (hint > 0) {
      if (Math.abs(storeT - hint) <= 2) return storeT
      if (Math.abs(asDoorPlus - hint) <= 2) return asDoorPlus
      return Math.abs(storeT - hint) <= Math.abs(asDoorPlus - hint) ? storeT : asDoorPlus
    }
    return asDoorPlus
  }
  if (storeT > 0) return storeT
  if (hint > 0) return hint
  return storeT
}

/**
 * Solo Gola (KGBR* / GO): Fondo del sidebar = Z de la puerta.
 * El GLB nativo tiene la puerta a (carcasa − regletaMax) p.ej. 600 y el bbox a 720.
 * scaleZ = Fondo / puertaNativa: 600 → 1, 720 → 1.2.
 * KBRB8010040 y resto clásicos: null (morph Fondo + scale de catálogo, como antes).
 */
export function getRinconeroFondoGroupScaleZ(parent: {
  catalogID?: string | number
  thickness?: number
  regletaMaximo?: number | null
  freeDimensionCatalogMm?: { z?: number } | null
  freeDimensionGlbBaseMm?: { z?: number } | null
} | null | undefined): number | null {
  if (!parent || !isKbrGolaRinconeroCatalog(parent.catalogID)) return null
  if (!isRinconeroCatalog(parent.catalogID)) return null
  const storeT = Number(parent.thickness ?? 0)
  const glbZ = Number(parent.freeDimensionGlbBaseMm?.z ?? parent.freeDimensionCatalogMm?.z ?? 0)
  if (!(storeT > 0) || !(glbZ > 0)) return null
  const overhang = Number(parent.regletaMaximo ?? 0)
  const doorNative = overhang > 0 && overhang < glbZ ? glbZ - overhang : glbZ
  if (!(doorNative > 0)) return null
  const s = storeT / doorNative
  if (!Number.isFinite(s) || s <= 0.05 || s > 20) return 1
  return s
}

/**
 * Plano Z de la puerta del rinconero (zócalo frontal bajo la puerta).
 * Carcasa = fondo de catálogo/GLB (720). Puerta = carcasa − regletaMaximo
 * (720−120=600), alineada con MBJ vecinos. No usa la regleta actual.
 *
 * KBRB8093A / KBRB80932: a catálogo las puertas están a 600 (como el KBB),
 * no a 930. Si Ancho/Fondo cambian, el plano escala con el cuerpo.
 */
export function getRinconeroDoorFrontPlaneMm(parent: {
  width?: number
  thickness?: number
  catalogID?: string | number
  regleta?: number | null
  regletaMaximo?: number | null
  freeDimensionCatalogMm?: { x?: number; z?: number } | null
  freeDimensionGlbBaseMm?: { z?: number } | null
} | null | undefined): number {
  if (parseRinconeroCatalogCode(parent?.catalogID)?.isTwoDoorL) {
    return getTwoDoorLScaledDoorPlaneMm(parent, 'z')
  }
  const carcassMm = getRinconeroCarcassDepthMm(parent)
  if (!(carcassMm > 0)) return 0
  const overhang = Number(parent?.regletaMaximo ?? 0)
  if (overhang > 0 && overhang < carcassMm) return carcassMm - overhang
  return carcassMm
}

/**
 * Alcance Z hasta la punta de la regleta (desde el origen local del rinconero).
 * Puerta + regleta actual — NO carcasa + regleta (eso deja un hueco = regletaMaximo).
 * p.ej. 720 carcasa, max 120, regleta 60 → 600+60=660 (no 720+60=780).
 */
export function getRinconeroRegletaFrontReachMm(parent: {
  thickness?: number
  regleta?: number | null
  regletaMaximo?: number | null
  catalogID?: string | number
  morphs?: Array<{ name: string; min: number; max: number }>
  freeDimensionCatalogMm?: { z?: number } | null
  freeDimensionGlbBaseMm?: { z?: number } | null
} | null | undefined): number {
  const doorMm = getRinconeroDoorFrontPlaneMm(parent)
  const carcassMm = getRinconeroCarcassDepthMm(parent)
  const regletaMm = getRegletaEffectiveMm(parent)
  const baseMm = doorMm > 0 ? doorMm : carcassMm
  return Math.max(0, baseMm + regletaMm)
}

/**
 * Parsea puerta/mano del catálogo rinconero (p.ej. KBRB8010040I → 400 mm, I).
 * Sin match estándar: ~40 % del ancho (ciego).
 *
 * Zócalo en L (ciego KBRB8010040):
 * - Frontal: bajo la puerta + FRONT_EXTEND hacia la esquina.
 * - Retorno: anclado al extremo del frontal; largo = puerta + regleta.
 *
 * KBRB8093A / KBRB80932 (L 2 puertas 93×93):
 * A catálogo: frontal 600 mm al plano KBB (z=555); retorno x=360.
 * Si Ancho/Fondo cambian, el plano 600@930 escala con el cuerpo.
 */
export function getRinconeroIndependentZocaloLayout(
  parent: {
    catalogID?: string | number
    width?: number
    thickness?: number
    regleta?: number | null
    regletaMaximo?: number | null
    morphs?: Array<{ name: string; min: number; max: number }>
    snappedSides?: { left?: AnyID; right?: AnyID }
    freeDimensionCatalogMm?: { x?: number; z?: number } | null
    freeDimensionGlbBaseMm?: { z?: number } | null
  } | null | undefined,
  zocaloNativeThicknessMm: number = 15,
  items?: Map<AnyID, GvSnapRunItem> | null,
): RinconeroIndependentZocaloLayout | null {
  if (!parent || !isRinconeroCatalog(parent.catalogID)) return null
  const parentW = Number(parent.width ?? 0)
  const parentT = Number(parent.thickness ?? 0)
  if (!(parentW > 0) || !(parentT > 0)) return null

  const parsed = parseRinconeroCatalogCode(parent.catalogID)
  const t = zocaloNativeThicknessMm > 0 ? zocaloNativeThicknessMm : 15
  const recess = INDEPENDENT_ZOCALO_FRONT_RECESS_MM
  const isTwoDoorL =
    parsed?.isTwoDoorL === true ||
    (!parsed?.doorWidthMm &&
      Number(parent.regletaMaximo ?? 0) <= 0 &&
      parentW >= 800 &&
      parentT >= 800)

  if (isTwoDoorL) {
    const scaledFrontPlaneMm = getTwoDoorLScaledDoorPlaneMm(parent, 'z')
    const neighborFondo = getTwoDoorLNeighborFondoMm(parent, items)
    const catalogArm = getTwoDoorLCatalogArmMm(parent)
    const stillCatalogDepth = Math.abs(parentT - catalogArm) <= 5
    const frontPlaneMm =
      stillCatalogDepth && neighborFondo > 0 ? neighborFondo : scaledFrontPlaneMm
    const frontStripWidthMm = getTwoDoorLScaledDoorPlaneMm(parent, 'x')
    const frontZ = Math.max(0, frontPlaneMm - t - recess)
    const isIzquierda = Boolean(parsed?.isIzquierda)
    // 2ª puerta en x = (ancho − plano puerta), mira al hueco interior.
    // Retranqueo 30 mm hacia el armazón. A 930×930: x=360, z=555.
    const door2Mm = Math.max(t, parentW - frontStripWidthMm)
    const returnX = isIzquierda
      ? Math.max(0, parentW - door2Mm - recess)
      : door2Mm + recess
    const frontX = isIzquierda ? Math.max(0, parentW - frontStripWidthMm) : 0
    return {
      isIzquierda,
      doorWidthMm: frontStripWidthMm,
      frontStripWidthMm,
      frontPlaneMm,
      frontX,
      returnLengthMm: Math.max(t, parentT - frontZ),
      returnX,
      returnZ: parentT,
      returnRotY: Math.PI / 2,
    }
  }

  let doorWidthMm = parsed?.doorWidthMm ?? 0
  if (!(doorWidthMm > 0) || doorWidthMm >= parentW) {
    doorWidthMm = Math.max(200, Math.round(parentW * 0.4))
  }
  doorWidthMm = Math.min(doorWidthMm, parentW)

  const isIzquierda = Boolean(parsed?.isIzquierda)
  const frontExtendMm = INDEPENDENT_ZOCALO_RINCONERO_FRONT_EXTEND_MM
  const frontStripWidthMm = Math.min(parentW, doorWidthMm + frontExtendMm)
  const frontX = isIzquierda ? Math.max(0, parentW - frontStripWidthMm) : 0
  const returnX = isIzquierda
    ? Math.max(0, frontX - t)
    : frontStripWidthMm

  // Gola: Fondo = puerta; retorno = puerta + regleta viva.
  if (isKbrGolaRinconeroCatalog(parent.catalogID)) {
    const returnFrontMm = parentT + getRegletaEffectiveMm(parent)
    return {
      isIzquierda,
      doorWidthMm,
      frontStripWidthMm,
      frontPlaneMm: parentT,
      frontX,
      returnLengthMm: Math.max(t, returnFrontMm),
      returnX,
      returnZ: Math.max(0, returnFrontMm),
      returnRotY: Math.PI / 2,
    }
  }

  // KBR clásico (KBRB8010040, sin G/GO): plano puerta = carcasa − max; retorno = puerta + regleta.
  const frontPlaneMm = getRinconeroDoorFrontPlaneMm(parent)
  const returnFrontMm = getRinconeroRegletaFrontReachMm(parent)
  return {
    isIzquierda,
    doorWidthMm,
    frontStripWidthMm,
    frontPlaneMm,
    frontX,
    returnLengthMm: Math.max(t, returnFrontMm),
    returnX,
    returnZ: Math.max(0, returnFrontMm),
    returnRotY: Math.PI / 2,
  }
}

/** Fondo del MBJ colindante (400–750). 0 si no hay: el plano va con el cuerpo. */
function getTwoDoorLNeighborFondoMm(
  parent: { snappedSides?: { left?: AnyID; right?: AnyID } },
  items?: Map<AnyID, GvSnapRunItem> | null,
): number {
  if (!items?.size) return 0
  for (const side of ['left', 'right'] as const) {
    const id = parent.snappedSides?.[side]
    if (id == null || id === '') continue
    const n = items.get(id)
    if (!n || n.type === 'walls' || n.type === 'partition') continue
    const nt = Number(n.thickness ?? 0)
    if (nt >= 400 && nt <= 750) return nt
  }
  return 0
}

export function isDecoLinkedItem(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    referencia?: string
    description?: string
  } | null,
): boolean {
  if (!item) return false
  const catalog = getLinkedItemCatalogCode(item)
  const ref = String(item.referencia ?? '').toUpperCase()
  const desc = String(item.description ?? '').toUpperCase()
  return catalog.includes('DECO') || ref.includes('DECO') || desc.includes('DECO')
}

export function isZocaloCatalogOrDescription(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    description?: string
    typeOfSon?: string | null
  } | null,
): boolean {
  if (!item) return false
  const code = getLinkedItemCatalogCode(item)
  if (code.includes('ZOCALO') || code === INDEPENDENT_ZOCALO_CATALOG_ID) return true
  if (/z[oó]calo/i.test(item.description ?? '')) return true
  if (item.typeOfSon && /z[oó]calo/i.test(String(item.typeOfSon))) return true
  return false
}

/**
 * Unidad MBJ/COL (no el propio zócalo) que debe llevar zócalo hijo
 * y dimensiones libres en el cuerpo.
 * - MBJ (incl. KBB…DECO): tira Zocalo 4000.
 * - COL no-DECO: tira Zocalo 4000.
 * - COL DECO (p. ej. KCB…DECO): sin zócalo independiente.
 * - Electrodomésticos (ELECTRODOMÉSTICOSMENU): nunca tira.
 * - Laterales/costados (LA/LB/LC/CC/CB/RA, p. ej. LB8060): no — el GLB ya
 *   incluye zócalo; si se aplicara lift+body, el panel quedaría ~100 mm corto
 *   respecto a la encimera (store 900 → cuerpo 800).
 */
export function shouldUseIndependentZocaloUnit(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    claveMenu?: string | null
    menuPrincipal?: string | null
  } | null,
): boolean {
  if (!item) return false
  if (isZocaloCatalogOrDescription(item)) return false
  // LB8060 etc. son tipo MBJ pero no llevan tira Zocalo 4000.
  if (isFreeDimensionLateralOrCostadoCatalog(item)) return false
  // RB* (regleta bajo): no tira Zocalo 4000. Excepción: solo RB805 (cuerpo 800 + tira).
  if (getLinkedItemCatalogCode(item).startsWith('RB') && !isRb805Catalog(item)) {
    return false
  }
  // Aparato Lavavajillas*/Lavadora*: no tira. Kits KBPL/KGBL/KBLV sí (mueble).
  if (isApplianceMenuUnit(item)) return false
  if (shouldSkipAutoZocalo(item.catalogID) || shouldSkipAutoZocalo(item.codigo) || shouldSkipAutoZocalo(item.referencia)) {
    return false
  }
  // GV* (moldura/remate): tira Zocalo 4000 + lift de cuerpo, igual que MBJ.
  if (isGvMoldingUnit(item)) return true
  // KBR*: tira Zocalo 4000 (recta o L en rinconera); el mesh integrado se oculta.
  if (
    isKBRCatalog(item.catalogID) ||
    isKBRCatalog(item.codigo) ||
    isKBRCatalog(item.referencia)
  ) {
    return true
  }
  const tipo = String(item.tipoNormalizado ?? '').toUpperCase()
  // Columnas DECO: no llevan zócalo tira (solo bajos MBJ DECO / COL normales).
  if (tipo === 'COL' && isDecoLinkedItem(item)) return false
  if (item.hasIndependentZocalo === true) return true
  return tipo === 'MBJ' || tipo === 'COL'
}

/**
 * MBJ/COL (zócalo hijo) y MAL: dibujo como unidad clásica (morphs GLB si existen) +
 * escala de grupo solo para lo que la sidebar pida fuera del rango del morph.
 * La base de escala es catálogo/morph, NUNCA el AABB del GLB (si no el cuerpo
 * queda más estrecho que el zócalo: p.ej. 300/400).
 */
export function usesPureGroupFreeDimensionScale(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    claveMenu?: string | null
    menuPrincipal?: string | null
  } | null,
): boolean {
  if (isApplianceMenuUnit(item)) return false
  // LA/RA/CC… no son mueble MAL aunque el API marque tipoNormalizado=MAL.
  if (isFreeDimensionLateralOrCostadoCatalog(item)) return false
  // RB/RC: aunque RC lleve zócalo, Ancho = width/glb (50/100). Pure-group
  // morph-clamp dejaría scaleX=1 y se verían a 100 mm sin morphs de malla.
  if (isRbFreeDimensionRegleta(item)) return false
  return shouldUseIndependentZocaloUnit(item) || isMalFreeDimensionUnit(item)
}

/**
 * Escala grupo MBJ/COL: dimStore / clamp(dim, morphMin, morphMax) si hay morph,
 * si no dimStore / catálogo. Dentro del rango → scale 1 (solo morphs, como antes).
 */
/**
 * Alto (mm) que debe alcanzar el mesh del cuerpo (sin zócalo).
 * Catálogo nuevo: store = cuerpo → height (p.ej. KBB8030: 800).
 * Legacy migratorio: store = mueble completo → height − lift.
 */
export function getIndependentZocaloBodyScaleTargetMm(
  item?: IndependentZocaloParentDims | null,
): number {
  const h = item?.height ?? 0
  if (h <= 0) return 0
  const lift = getIndependentZocaloBodyLiftMm(item)
  if (lift <= 0) return h
  return storeHeightIsIndependentZocaloBodyOnly(item) ? h : Math.max(0, h - lift)
}

export function getMbjColFreeDimensionScaleRatios(item: {
  width?: number
  height?: number
  thickness?: number
  morphs?: Morphs[]
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
  catalogID?: string | number
  codigo?: string | number
  tipoNormalizado?: string
  hasIndependentZocalo?: boolean
  independentZocaloHeightMm?: number
  kbrGlbBodyMinYMm?: number
  regletaMaximo?: number | null
}): { x: number; y: number; z: number } {
  const axisScale = (
    storeMm: number | undefined,
    morphName: 'Ancho' | 'Alto' | 'Fondo',
    catalogMm: number | undefined,
  ): number => {
    const target = storeMm != null && storeMm > 0 ? storeMm : 0
    if (target <= 0) return 1
    const morph = item.morphs?.find((m) => m.name === morphName)
    // Eje modificable con rango GLB: morph de malla + scale solo el exceso sobre max.
    if (morph?.modifyable !== false && morphHasValidRange(morph)) {
      const clamped = clampMorphMm(target, morph!.min, morph!.max)
      return clamped > 0 ? target / clamped : 1
    }
    // Eje bloqueado / sin rango (KCB360 modificarAncho=false): scale = layout/catálogo.
    // p.ej. width 551 / cat 600. Nunca usar morph.default (551) como base → scale 1
    // y el zócalo a 551 queda más corto que el cuerpo a 600.
    const base =
      (catalogMm != null && catalogMm > 0 ? catalogMm : 0) ||
      (morph?._default != null && morph._default > 0 ? Number(morph._default) : 0) ||
      target
    return base > 0 ? target / base : 1
  }

  const cat = item.freeDimensionCatalogMm
  const lift = getIndependentZocaloBodyLiftMm(item)
  const glbY = item.freeDimensionGlbBaseMm?.y ?? 0
  const catalogY = cat?.y ?? 0

  let scaleY: number
  // KBR: el Alto extra se aplica en malla con pivote Y=100. scaleY de grupo
  // desde Y=0 sube el hueco del zócalo (panel flotando al poner 1100).
  // KBR con GLB solo cuerpo: como MBJ/KGBR (lift + scale de cuerpo, rama de abajo).
  if ((isKBRCatalog(item.catalogID) || isKBRCatalog(item.codigo)) && !kbrGlbBodySitsOnFloor(item)) {
    scaleY = 1
  } else if (
    isKbrGolaRinconeroCatalog(item.catalogID) ||
    isKbrGolaRinconeroCatalog(item.codigo)
  ) {
    // KGBR: el morph Alto no mueve el tirador (sy de grupo = 1 dentro de rango).
    // Misma escala que Ancho: height / catálogo. En pose de dibujo (800/800) = 1.
    const bodyTarget = getIndependentZocaloBodyScaleTargetMm(item) || (item.height ?? 0)
    const bodyBase = catalogY > 0 ? catalogY : bodyTarget
    scaleY =
      bodyBase > 0 && Math.abs(bodyTarget - bodyBase) <= 2 ? 1 : bodyBase > 0 ? bodyTarget / bodyBase : 1
    if (!Number.isFinite(scaleY) || scaleY <= 0.05 || scaleY > 20) scaleY = 1
  } else if (isGvMoldingUnit(item) && (item.height ?? 0) > 0) {
    // GV: Alto = scale de grupo con pivote en la base (AABB.min.y). No morph de
    // malla: el origen del GLB está arriba y recortaría dejando hueco sobre la tira.
    const bodyTarget = item.height ?? 0
    const bodyBase =
      (glbY > 0 && catalogY > 0 && Math.abs(glbY - catalogY) <= 2 ? glbY : 0) ||
      (catalogY > 0 ? catalogY : 0) ||
      glbY ||
      bodyTarget
    scaleY = bodyBase > 0 ? bodyTarget / bodyBase : 1
  } else if (lift > 0 && (item.height ?? 0) > 0 && (glbY > 0 || catalogY > lift)) {
    // Cuerpo: (height − zócalo) / base del morph (o catálogo).
    // NO usar glbY>bodyTarget → scaleY 800/900: el pivote es Y=0, y si el mesh
    // tiene minY>0 (hueco del zócalo integrado) comprime y abre un hueco.
    const bodyTarget = getIndependentZocaloBodyScaleTargetMm(item)
    const morphAlto = item.morphs?.find((m) => m.name === 'Alto')
    if (morphHasValidRange(morphAlto)) {
      const clamped = clampMorphMm(bodyTarget, morphAlto!.min, morphAlto!.max)
      scaleY = clamped > 0 ? bodyTarget / clamped : 1
    } else {
      const glbIsFullCatalogHeight =
        glbY > 0 && catalogY > 0 && Math.abs(glbY - catalogY) <= 2
      const bodyBase = glbIsFullCatalogHeight
        ? glbY
        : (catalogY > lift ? catalogY - lift : 0) || glbY || bodyTarget
      scaleY = bodyBase > 0 ? bodyTarget / bodyBase : 1
    }
  } else {
    scaleY = axisScale(item.height, 'Alto', catalogY)
  }

  return {
    x: axisScale(item.width, 'Ancho', cat?.x),
    y: scaleY,
    z: getRinconeroFondoGroupScaleZ(item) ?? axisScale(item.thickness, 'Fondo', cat?.z),
  }
}

/**
 * Escala Y extra del cuerpo KBR por encima del morph (Alto > 900).
 * Aplicar en un grupo con origen en la junta del zócalo (100 mm), nunca desde Y=0.
 */
export function getKbrIndependentZocaloBodyScaleY(
  item?: {
    height?: number
    catalogID?: string | number
    codigo?: string | number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    independentZocaloHeightMm?: number
    kbrGlbBodyHeightMm?: number
  } | null,
): number {
  if (!item || !(isKBRCatalog(item.catalogID) || isKBRCatalog(item.codigo))) return 1
  const z = getIndependentZocaloHeightMm(item)
  // KBRB8010040: GLB cuerpo 700 (Y 100–800). Store/catálogo 800. Encimera 900.
  // scale = height/700 con pivote en la junta → tope 100+height. Al dibujar 800/700
  // (tope 900). No usar catálogo 800 como base: scale 1 dejaba tope 800 y la
  // encimera flotando 100 mm.
  const desiredBody = Number(item.height ?? 0)
  if (!(desiredBody > 0) || !(z > 0)) return 1
  const morph = item.morphs?.find((m) => m.name === 'Alto')
  const catalogBody = Number(item.freeDimensionCatalogMm?.y ?? 0)
  const measuredBody = Number(item.kbrGlbBodyHeightMm ?? 0)
  const bboxBody = Number(item.freeDimensionGlbBaseMm?.y ?? 0)
  const nativeBody =
    (measuredBody > 0 ? measuredBody : 0) ||
    (bboxBody > 0 && Math.abs(bboxBody - (catalogBody + z)) > 2 ? bboxBody : 0) ||
    (catalogBody > 0 ? catalogBody : 0) ||
    (Number(morph?._default ?? 0) > 0 ? Number(morph?._default) : 0) ||
    desiredBody
  const sy = desiredBody / nativeBody
  if (!Number.isFinite(sy) || sy <= 0.05 || sy > 20) return 1
  return sy
}

export function isIndependentZocaloLinkedSon(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    description?: string
    typeOfSon?: string | null
    isIndependentZocalo?: boolean
  } | null,
): boolean {
  if (!item) return false
  if (item.isIndependentZocalo === true) return true
  return isZocaloCatalogOrDescription(item)
}

/**
 * Elevación Y (mm) del mesh del cuerpo encima del zócalo independiente.
 * Solo afecta al grupo del GLB padre (render); no mueve la matriz mundo del ítem.
 * Laterales/costados: 0 aunque el store tenga hasIndependentZocalo (unidades ya
 * dibujadas antes de excluirlos de shouldUseIndependentZocaloUnit).
 */
export function getIndependentZocaloBodyLiftMm(
  item?: {
    hasIndependentZocalo?: boolean
    independentZocaloHeightMm?: number
    independentZocaloSonId?: string | null
    catalogID?: string | number
    codigo?: string | number
    referencia?: string | number
    description?: string
    claveMenu?: string | null
    menuPrincipal?: string | null
    freeDimensionGlbMinYMm?: number
    kbrGlbBodyMinYMm?: number
  } | null,
): number {
  if (!item || isFreeDimensionLateralOrCostadoCatalog(item)) return 0
  if (isApplianceMenuUnit(item)) return 0
  // RB* salvo RB805: sin lift; el cuerpo rellena el hueco (store 900).
  if (getLinkedItemCatalogCode(item).startsWith('RB') && !isRb805Catalog(item)) {
    return 0
  }
  // KBR*: mismo contrato que KGBR/KBB (store = cuerpo, tope = height + 100).
  // El GLB KBR trae el cuerpo ya a Y≈100 (sobre la losa), así que el render
  // NO suma este lift al grupo (kbrScaleAboutZocalo en LinkedItem3D/2D):
  // escala el cuerpo con pivote en la junta. Con kbrGlbBodySitsOnFloor sí se aplica.
  const hasZocalo =
    item.hasIndependentZocalo === true ||
    (typeof item.independentZocaloSonId === 'string' && item.independentZocaloSonId.length > 0)
  if (!hasZocalo) return 0
  const zocaloH = getIndependentZocaloHeightMm(item)
  // GV*: el origen del GLB no está en la base (a menudo centro o techo).
  // Asentar AABB.min.y sobre la tira: lift = 100 − minY. Si minY=0 (como KBB) → 100.
  if (isGvMoldingUnit(item)) {
    const minY = Number(item.freeDimensionGlbMinYMm)
    if (Number.isFinite(minY)) return zocaloH - minY
  }
  return zocaloH
}

type IndependentZocaloParentDims = {
  hasIndependentZocalo?: boolean
  independentZocaloHeightMm?: number
  kbrGlbBodyMinYMm?: number
  height?: number
  width?: number
  thickness?: number
  morphs?: Morphs[]
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
  catalogID?: string | number
  codigo?: string | number
  tipoNormalizado?: string
}

/**
 * ¿El alto store representa solo el cuerpo (hay que sumar lift a hijos) o el mueble
 * completo desde el suelo (API/catálogo ya incluye el hueco del zócalo)?
 *
 * Catálogo nuevo (KBB8030): API `alto` = cuerpo (800), GLB cuerpo ≈ 800, lift 100.
 * Store = cuerpo → scaleY usa height tal cual; tope visual / encimera = height + lift (900).
 * Los hijos API siguen en coords suelo con hueco de zócalo (encimera posicionY 900).
 *
 * Legacy al abrir: height JSON sigue en 900 (mueble completo) mientras el asset actual
 * refresca catalog/GLB a cuerpo 800/800. Hay que mirar `height` antes de asumir cuerpo.
 *
 * No usa scaleY (evitar dependencia circular con getMbjColFreeDimensionScaleRatios).
 */
export function storeHeightIsIndependentZocaloBodyOnly(
  item?: IndependentZocaloParentDims | null,
): boolean {
  const lift = getIndependentZocaloBodyLiftMm(item)
  if (lift <= 0 || !item) return false
  const h = item.height ?? 0
  if (h <= 0) return false
  // KBR*: API alto = cuerpo (800, igual que su gemelo KGBR). El GLB puede traer el
  // cuerpo a 700 (KBRB8010040) u 800 (KBRB80932): se escala a `height` con pivote
  // en la junta (getKbrIndependentZocaloBodyScaleY). Tope/encimera = height + 100.
  if (isKBRCatalog(item.catalogID) || isKBRCatalog(item.codigo)) {
    return true
  }

  const glbY = item.freeDimensionGlbBaseMm?.y ?? 0
  const catalogY = item.freeDimensionCatalogMm?.y ?? 0
  if (glbY <= 0) {
    // Sin bbox: API publica alto de cuerpo.
    return true
  }
  // Catálogo = cuerpo + zócalo (datos migratorios) → store mide el mueble completo.
  if (catalogY > 0 && Math.abs(catalogY - (glbY + lift)) <= 2) return false

  // KARB: API alto=900 y AABB GLB=900 (incluye losa). No es KBB 800/800 (solo cuerpo).
  // Si 900/900 se trata como cuerpo, se suma lift otra vez y flota 100 mm.
  // KGBR* Gola: catálogo/GLB 800 como KGBB — el cuerpo sí es solo cuerpo.
  if (
    (isKARBCatalog(item.catalogID) || isKARBCatalog(item.codigo)) &&
    catalogY > 0 &&
    Math.abs(catalogY - glbY) <= 2 &&
    Math.abs(catalogY - h) <= 2
  ) {
    return false
  }

  // Catálogo ≈ GLB cuerpo (asset nuevo / KBF, KBB…): store SIEMPRE = cuerpo.
  // Legacy JSON 900 se normaliza a 800 en reconcileStaleLayoutDimsToNewCatalog.
  // NO usar height≈glb+lift → "completo": al poner Alto=900 (o > punto medio) el
  // fregadero perdía ~100 mm de hueco bajo la encimera (parecía flotar +zócalo).
  if (catalogY > 0 && Math.abs(catalogY - glbY) <= 2) {
    return true
  }

  return Math.abs(h - glbY) <= Math.abs(h - (glbY + lift))
}

/**
 * Tope de referencia API (mm) para anclar hijos tipo encimera/fregadero.
 * Con store = cuerpo, el API posiciona hijos en coords suelo (cuerpo + zócalo).
 */
function getIndependentZocaloApiFurnitureTopMm(
  parent: IndependentZocaloParentDims | null | undefined,
  catalogBodyMm: number,
): number {
  if (catalogBodyMm <= 0) return 0
  const lift = getIndependentZocaloBodyLiftMm(parent)
  if (lift <= 0) return catalogBodyMm
  return storeHeightIsIndependentZocaloBodyOnly(parent)
    ? catalogBodyMm + lift
    : catalogBodyMm
}

/**
 * Lift a sumar al alto store para obtener el tope visual del mueble
 * (encimera = height + lift cuando store = solo cuerpo).
 *
 * No usar para offset de posicionY API de tiradores/aplicaciones: esas coords
 * ya vienen en espacio suelo (p.ej. bcY 891 / encimera 900 con cuerpo 800).
 * Para eso usar mapApiFloorYThroughIndependentZocaloBodyMm.
 */
export function getIndependentZocaloChildYLiftMm(
  item?: IndependentZocaloParentDims | null,
): number {
  const lift = getIndependentZocaloBodyLiftMm(item)
  if (lift <= 0) return 0
  return storeHeightIsIndependentZocaloBodyOnly(item) ? lift : 0
}

type LinkedApplianceChildRef = {
  typeOfSon?: string | null
  description?: string | null
  tipoNormalizado?: string | null
  catalogID?: string | number
  codigo?: string | number
  referencia?: string
  posicionX?: number | null
  posicionY?: number | null
  posicionZ?: number | null
  /** Y API/catálogo (sin escala free-dim ni ajuste de acabado encimera). */
  baseY?: number | null
  /**
   * Delta Y (mm) por grosor/acabado de encimera (compacto 0, postformada +20…).
   * Se suma en getLinkedApplianceLocalYMm para que el render y el store coincidan
   * tras morph de Alto o cambio de fregadero.
   */
  encimeraAcabadoAjusteY?: number | null
  x?: number
  y?: number
  z?: number
}

function normalizeLinkedMenuClave(clave?: string | null): string {
  return String(clave ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
}

/** ELECTRODOMÉSTICOSMENU y submenús (LAVAVAJILLASELECTROMENU, VINOTECASELECTROMENU…). */
function isApplianceMenuClave(clave?: string | null): boolean {
  const normalized = normalizeLinkedMenuClave(clave)
  if (!normalized) return false
  return normalized.includes('ELECTRODOMESTICO') || normalized.endsWith('ELECTROMENU')
}

/**
 * Kits mueble de lavavajillas: llevan zócalo tira (contrato MBJ), aunque el nombre
 * sea lavavajillas. KBPL / KGBL (pedido) y KBLV (p. ej. KBLVB8045 kit bajo).
 */
export function isDishwasherFurnitureKitCatalog(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    referencia?: string | number
  } | null,
): boolean {
  if (!item) return false
  const prefixes = ['KBPL', 'KGBL', 'KBLV']
  const codes = [
    normalizeFurnitureCatalogCode(item.catalogID),
    normalizeFurnitureCatalogCode(item.codigo),
    normalizeFurnitureCatalogCode(item.referencia),
  ]
  return codes.some((code) => {
    const compact = code.replace(/[\s_-]+/g, '')
    return prefixes.some((prefix) => compact.startsWith(prefix))
  })
}

/**
 * Electrodoméstico de menú: nunca zócalo tira ni contrato MBJ/COL de mueble.
 * Fallback por catálogo cuando aún no hay claveMenu (newGetDataFromGLB / JSON viejo).
 * Los kits KBPL/KGBL/KBLV no entran aquí: son mueble y sí llevan tira.
 */
export function isApplianceMenuUnit(
  item?: {
    claveMenu?: string | null
    menuPrincipal?: string | null
    catalogID?: string | number
    codigo?: string | number
    referencia?: string
    description?: string | null
    typeOfSon?: string | null
  } | null,
): boolean {
  if (!item) return false
  if (isDishwasherFurnitureKitCatalog(item)) return false
  if (isApplianceMenuClave(item.claveMenu) || isApplianceMenuClave(item.menuPrincipal)) {
    return true
  }
  if (isDishwasherOrWasherLinkedItem(item)) return true
  const t = `${item.catalogID ?? ''} ${item.codigo ?? ''} ${item.referencia ?? ''} ${item.description ?? ''}`.toLowerCase()
  return /vinoteca/.test(t)
}

/** Lavavajillas / lavadora (aparato o kit). Reciben el acabado de Puerta. */
export function isDishwasherOrWasherLinkedItem(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    referencia?: string
    description?: string | null
    typeOfSon?: string | null
  } | null,
): boolean {
  if (!item) return false
  const t =
    `${item.catalogID ?? ''} ${item.codigo ?? ''} ${item.referencia ?? ''} ${item.description ?? ''} ${item.typeOfSon ?? ''}`.toLowerCase()
  return /lavavajillas|lavadora|lavaplatos/.test(t)
}

/**
 * Hijos con coords API propias (no van al tope del cuerpo).
 * Fregadero/placa/horno/grifo/instalaciones.
 */
export function linkedSonKeepsApiLocalTransform(
  child?: LinkedApplianceChildRef | null,
): boolean {
  if (!child) return false
  if (String(child.tipoNormalizado ?? '').toUpperCase() === 'INS') return true
  if (isDishwasherOrWasherLinkedItem(child)) return true
  const t = `${child.typeOfSon ?? ''} ${child.description ?? ''} ${child.catalogID ?? ''}`.toLowerCase()
  return /fregadero|placa|horno|grifo|tpcgrifo|micro|instalaci[oó]n|\bagua\b/.test(t)
}

/**
 * Electrodomésticos que crecen en Ancho/Fondo con el mueble (group scale X/Z).
 * Grifo/instalaciones mantienen tamaño físico.
 */
export function linkedSonScalesWithParentFurniture(
  child?: LinkedApplianceChildRef | null,
): boolean {
  if (!child) return false
  const t = `${child.typeOfSon ?? ''} ${child.description ?? ''}`.toLowerCase()
  // Placa: tamaño físico del GLB (no estirar un vitro 800 en un bajo 900).
  return /fregadero|\bhorno|micro/.test(t)
}

/**
 * Solo horno/micro deben estirarse en Alto con el cuerpo.
 * Fregadero/placa: alto nativo; su Y sigue la encimera (no scaleY).
 */
export function linkedSonScalesAltoWithParentFurniture(
  child?: LinkedApplianceChildRef | null,
): boolean {
  if (!child) return false
  const t = `${child.typeOfSon ?? ''} ${child.description ?? ''}`.toLowerCase()
  return /\bhorno|micro/.test(t)
}

/** Fregadero/placa/grifo: anclados al plano de encimera (no al scaleY del cuerpo). */
export function isCountertopApplianceLinkedSon(
  child?: LinkedApplianceChildRef | null,
): boolean {
  if (!child) return false
  const t = `${child.typeOfSon ?? ''} ${child.description ?? ''} ${child.catalogID ?? ''}`.toLowerCase()
  return /fregadero|placa|grifo|tpcgrifo/.test(t)
}

/** Placa de encimera (no fregadero/grifo). */
export function isPlacaLinkedApplianceSon(
  child?: LinkedApplianceChildRef | null,
): boolean {
  if (!child) return false
  const t = `${child.typeOfSon ?? ''} ${child.description ?? ''} ${child.catalogID ?? ''}`.toLowerCase()
  return t.includes('placa')
}

/** 1 mm sobre el tope de piedra: la encimera es un sólido sin recorte y z-fight oculta el cristal. */
export const PLACA_PROUD_ABOVE_ENCIMERA_MM = 1

/** Y local: tope visual de la losa (no height de store) + 1 mm, menos el minY del GLB de la placa. */
export function getLinkedPlacaSitLocalYMm(
  encimeraLocalYMm: number,
  visualThicknessMm: number,
  glbMinYMm = 0,
): number {
  const thickness =
    Number.isFinite(visualThicknessMm) && visualThicknessMm > 0
      ? visualThicknessMm
      : ENCIMERA_API_BASELINE_THICKNESS_MM
  return encimeraLocalYMm + thickness - glbMinYMm + PLACA_PROUD_ABOVE_ENCIMERA_MM
}

/** X local: centra el GLB real en el ancho del mueble (placa18 800 ≠ hueco API de Placa3 152). */
export function getLinkedPlacaCenteredLocalXMm(
  parentWidthMm: number,
  plateWidthMm: number,
  glbMinXMm = 0,
): number | null {
  if (!(parentWidthMm > 0) || !(plateWidthMm > 0)) return null
  if (plateWidthMm > parentWidthMm + 80) return null
  return Math.round((parentWidthMm - plateWidthMm) / 2 - glbMinXMm)
}

export function isFregaderoLinkedSon(
  child?: LinkedApplianceChildRef | null,
): boolean {
  if (!child) return false
  const t = `${child.typeOfSon ?? ''} ${child.description ?? ''} ${child.catalogID ?? ''}`.toLowerCase()
  return t.includes('fregadero')
}

/**
 * Fregadero con escurridor/oreja a la izquierda.
 * Su GLB suele tener el origen en el extremo de la oreja: si se escala en X desde 0,
 * la cubeta se engorda hacia +X (el “bloque” blanco). Hay que pivotear en el borde derecho.
 */
export function isLeftEarFregadero(
  child?: (LinkedApplianceChildRef & {
    referencia?: string | null
    long?: string | null
    code?: string | null
    mano?: string | null
    width?: number
    glbSize?: { x?: number }
  }) | null,
): boolean {
  if (!isFregaderoLinkedSon(child)) return false
  const catalog = String(child?.catalogID ?? child?.code ?? '').toLowerCase()
  const blob = [
    catalog,
    child?.description,
    child?.referencia,
    child?.long,
    child?.mano,
  ]
    .map((s) => String(s ?? '').toLowerCase())
    .join(' ')

  // Oreja/escurridor derecha → no es el caso problemático
  if (
    /(escurridor|oreja).{0,16}(derech|\bd\b)/.test(blob) ||
    /(derech|dcha).{0,16}(escurridor|oreja)/.test(blob) ||
    /(?:^|[\s_-])(od|ed)(?:$|[\s_-])/.test(blob) ||
    /(?:od|ed)$/.test(catalog)
  ) {
    return false
  }

  if (
    /(escurridor|oreja).{0,16}(izq|\bi\b)/.test(blob) ||
    /(izq|izquierd).{0,16}(escurridor|oreja)/.test(blob) ||
    /(?:^|[\s_-])(oi|ei)(?:$|[\s_-])/.test(blob) ||
    /(?:oi|ei)$/.test(catalog)
  ) {
    return true
  }

  return false
}

/** Ancho nativo (mm) del fregadero (store o bbox GLB). */
export function getFregaderoNativeAnchoMm(
  child?: {
    width?: number
    glbSize?: { x?: number }
    freeDimensionGlbBaseMm?: { x?: number }
  } | null,
): number {
  const w = Number(child?.width ?? 0)
  if (w > 0) return w
  const base = child?.freeDimensionGlbBaseMm?.x
  if (base != null && base > 0) return base
  const gx = child?.glbSize?.x
  if (gx != null && gx > 0) return Math.round(gx * 1000)
  return 0
}

/**
 * Pivote local X (mm) para scaleX del fregadero.
 * Oreja izquierda: borde derecho del mesh (cubeta) para no engordar hacia +X.
 */
export function getFregaderoAnchoScalePivotMm(
  child?: Parameters<typeof isLeftEarFregadero>[0],
): number {
  if (!isLeftEarFregadero(child)) return 0
  return getFregaderoNativeAnchoMm(child)
}

/**

 * Escala de fregadero/placa/horno = misma que el GLB cuerpo del padre.
 * Con zócalo independiente: scaleY=(height−zócalo)/(catálogo−zócalo), no height/catálogo
 * (si no el horno no llena el hueco de la puerta/cajón del KBHB).
 */
export function getLinkedApplianceParentScaleRatios(parent: {
  width?: number
  height?: number
  thickness?: number
  morphs?: Morphs[]
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
  freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
  catalogID?: string | number
  codigo?: string | number
  tipoNormalizado?: string
  description?: string
  referencia?: string
  hasIndependentZocalo?: boolean
  independentZocaloHeightMm?: number
  hasMorphs?: boolean
  glbSize?: Vector3
}): { x: number; y: number; z: number } {
  if (!usesFreeDimensionScaling(parent)) return { x: 1, y: 1, z: 1 }
  return getFreeDimensionScaleRatios(parent)
}

/**
 * Y API (origen suelo, tamaño catálogo) → Y local con cuerpo lifted+scaled.
 * Ej. horno 260, lift 100, sy 2.125 (alto 1800) → 100+(160)×2.125 = 440
 * (no 260×1800/900=520: dejaba hueco bajo el horno sobre el cajón).
 * Tirador cajón 251 → ~421 (sobre el frente, no a 1151 con el delta clásico).
 */
export function mapApiFloorYThroughIndependentZocaloBodyMm(
  apiY: number,
  parent: {
    height?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    independentZocaloHeightMm?: number
    hasMorphs?: boolean
    width?: number
    thickness?: number
    glbSize?: Vector3
  },
): number {
  if (!Number.isFinite(apiY)) return 0
  const catH = Number(parent.freeDimensionCatalogMm?.y ?? 0)
  const ph = Number(parent.height ?? 0)
  // Al dibujar (800/800) las coords API ya son correctas (tirador 891, encimera 900).
  if (catH > 0 && ph > 0 && Math.abs(ph - catH) <= 2) {
    return apiY
  }
  const lift = getIndependentZocaloBodyLiftMm(parent)
  let sy = getLinkedApplianceParentScaleRatios(parent).y
  if (catH > 0 && ph > 0 && Math.abs(sy - 1) < 0.002) {
    sy = ph / catH
  }
  if (lift <= 0) {
    return catH > 0 && ph > 0 ? apiY * (ph / catH) : apiY * sy
  }
  if (apiY <= lift) return apiY
  return lift + (apiY - lift) * sy
}

function normalizeEncimeraAcabado(acabado?: string | null): string {
  if (!acabado) return ''
  return acabado
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/**
 * Grosor de encimera (mm) según acabado.
 * Compacto 10 = baseline de las coords API (placa @ 910 con tope mueble 900).
 */
export function getEncimeraAcabadoThicknessMm(acabado?: string | null): number | null {
  const a = normalizeEncimeraAcabado(acabado)
  if (!a) return null
  if (a.includes('porcelanicas 12') || a.includes('porcelanica 12')) return 12
  if (a.includes('porcelanicas 20') || a.includes('porcelanica 20')) return 20
  if (a.includes('postformad')) return 30
  if (a.includes('compacto')) return 10
  return null
}

/** Baseline de catálogo: coords API de placa/fregadero/grifo asumen encimera compacto 10 mm. */
export const ENCIMERA_API_BASELINE_THICKNESS_MM = 10

export type EncimeraVisualRef = {
  height?: number
  morphs?: Morphs[]
  catalogID?: string | number
  codigo?: string | number
  encimeraCatalogMm?: EncimeraCatalogMm
  glbSize?: { x?: number; y?: number; z?: number }
  textures?: { acabado?: string | null; zone?: string | null; zoneDisplayName?: string | null }[]
}

/**
 * Delta Y (mm) respecto al baseline API (compacto 10 mm).
 * Porcelánicas 20 → +10 (placa 910→920); postformada 30 → +20; compacto → 0.
 */
export function getEncimeraAjusteFromThicknessMm(thicknessMm?: number | null): number {
  if (thicknessMm == null || !Number.isFinite(thicknessMm)) return 0
  const t = Math.round(thicknessMm)
  if (t < 8 || t > 40) return 0
  return Math.max(0, t - ENCIMERA_API_BASELINE_THICKNESS_MM)
}

export function getEncimeraAcabadoAjusteYMm(acabado?: string | null): number {
  const thickness = getEncimeraAcabadoThicknessMm(acabado)
  if (thickness != null) return Math.max(0, thickness - ENCIMERA_API_BASELINE_THICKNESS_MM)
  // Acabado desconocido con nombre: legacy asumía ~20 mm (+10).
  return acabado ? 10 : 0
}

/**
 * Ajuste Y desde el hijo encimera: prioriza acabado; si falta, usa morph/alto (12/20/30).
 */
export function resolveEncimeraAcabadoAjusteYMm(encimera?: {
  textures?: { acabado?: string | null; zone?: string | null; zoneDisplayName?: string | null }[] | null
  height?: number | null
} | null): number {
  const acabado =
    getEncimeraTextureEntry(encimera as EncimeraVisualRef | null)?.acabado ??
    encimera?.textures?.[0]?.acabado
  if (acabado) return getEncimeraAcabadoAjusteYMm(acabado)
  const h = Number(encimera?.height ?? 0)
  if (h >= ENCIMERA_API_BASELINE_THICKNESS_MM && h <= 40) {
    return Math.max(0, Math.round(h) - ENCIMERA_API_BASELINE_THICKNESS_MM)
  }
  return 0
}

/** Compacto 10, porcelánica 12/20, postformada 30. */
export function getEncimeraTypeThicknessMm(encimera?: EncimeraVisualRef | null): number | null {
  if (!encimera) return null
  const fromAcabado = getEncimeraAcabadoThicknessMm(getEncimeraTextureEntry(encimera)?.acabado)
  if (fromAcabado != null) return fromAcabado
  const usesAltoMorph =
    itemUsesFreeDimensionAltoApiMorph(encimera) ||
    morphHasValidRange(encimera.morphs?.find((m) => m.name === 'Alto'))
  const h = Number(encimera.height ?? 0)
  if (usesAltoMorph && h >= 8 && h <= 40) return Math.round(h)
  return null
}

/**
 * Y local (mm) de fregadero/placa/horno — recalcular en render como la encimera.
 *
 * Fregadero/placa/grifo: mantienen el hueco al tope de catálogo bajo la encimera actual
 * (ej. apiY 702 con alto catálogo 900 → siempre 198 mm bajo el tope visual).
 * Así, al cambiar Alto del mueble, el fregadero acompaña a la encimera sin deformarse.
 *
 * Horno/micro: Y vía lift+scale del cuerpo (hueco de cajón/puerta).
 */
export function getLinkedApplianceLocalYMm(
  child: LinkedApplianceChildRef,
  parent: Parameters<typeof mapApiFloorYThroughIndependentZocaloBodyMm>[1] & {
    independentZocaloFloorOffsetApplied?: boolean
    y?: number
    id?: string
  },
  encimera?: EncimeraVisualRef | null,
  placaSit?: { glbMinYMm?: number; encimeraAabb?: { maxY: number } | null },
): number {
  const fromEncimera = encimera ? resolveEncimeraAcabadoAjusteYMm(encimera) : NaN
  const ajuste = Number.isFinite(fromEncimera)
    ? fromEncimera
    : Number(child.encimeraAcabadoAjusteY ?? 0)
  const ajusteSafe = Number.isFinite(ajuste) ? ajuste : 0
  if (!usesFreeDimensionScaling(parent)) {
    const apiY = resolveLinkedApplianceApiFloorYMm(child, parent)
    if (!Number.isFinite(apiY)) return (Number(child.y ?? 0) || 0) + ajusteSafe
    return apiY + ajusteSafe
  }

  // Encimera / fregadero / placa / grifo: anclar al tope visual (no scaleY del cuerpo).
  if (isCountertopApplianceLinkedSon(child)) {
    const catalogBody =
      parent.freeDimensionCatalogMm?.y ||
      Number(parent.morphs?.find((m) => m.name === 'Alto')?._default ?? 0) ||
      0
    const catalogTop = getIndependentZocaloApiFurnitureTopMm(parent, catalogBody)
    const encimeraY = getFreeDimensionEncimeraLocalYMm(parent)
    // Evitar estados intermedios al teclear Alto (p. ej. height=8 mientras se escribe 800).
    if (!(encimeraY > 50) || !(catalogTop > 0)) {
      const apiY = resolveLinkedApplianceApiFloorYMm(child, parent)
      return (Number.isFinite(apiY) ? apiY : Number(child.y ?? 0) || 0) + ajusteSafe
    }
    // Placa: tope visual del mueble (alto cuerpo + zócalo) + grosor de encimera.
    // compacto 10 → +10; porcelánica 20 → +20. No reutilizar posicionY de JSON viejo
    // (p.ej. 810 con cuerpo 800) ni el offset API de otra placa.
    if (isPlacaLinkedApplianceSon(child)) {
      const topOffset = encimera
        ? getEncimeraSitTopOffsetMm(encimera, placaSit?.encimeraAabb)
        : ENCIMERA_API_BASELINE_THICKNESS_MM + ajusteSafe
      return getLinkedPlacaSitLocalYMm(encimeraY, topOffset, placaSit?.glbMinYMm ?? 0)
    }
    const apiY = resolveLinkedApplianceApiFloorYMm(child, parent, catalogTop)
    if (!Number.isFinite(apiY)) {
      // Sin Y API fiable: fregadero/grifo al plano de encimera (+10 mm embebido típico).
      return encimeraY + (isCountertopApplianceLinkedSon(child) ? 10 : 0) + ajusteSafe
    }
    const offsetBelowCatalogTop = catalogTop - apiY
    return encimeraY - offsetBelowCatalogTop + ajusteSafe
  }

  const apiY = resolveLinkedApplianceApiFloorYMm(child, parent)
  if (!Number.isFinite(apiY)) return (Number(child.y ?? 0) || 0) + ajusteSafe

  if (getIndependentZocaloBodyLiftMm(parent) > 0) {
    return mapApiFloorYThroughIndependentZocaloBodyMm(apiY, parent) + ajusteSafe
  }
  const catH =
    parent.freeDimensionCatalogMm?.y ||
    Number(parent.morphs?.find((m) => m.name === 'Alto')?._default ?? 0) ||
    0
  const ph = parent.height ?? 0
  const scaled = catH > 0 && ph > 0 ? apiY * (ph / catH) : apiY
  return scaled + ajusteSafe
}

/**
 * Y de catálogo (coords suelo) para electrodomésticos enlazados.
 * NUNCA confiar en `child.y` si parece Y local ya transformada (p.ej. 1630 con tope 900):
 * en proyectos antiguos se persistió el resultado y luego se reutilizó como si fuera API.
 */
export function resolveLinkedApplianceApiFloorYMm(
  child: LinkedApplianceChildRef & {
    linkedID?: number | string | null
    idElementosEnlazados?: number | string | null
    nodo?: number | null
  },
  parent?: {
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    morphs?: Morphs[]
    height?: number
    hasIndependentZocalo?: boolean
    independentZocaloHeightMm?: number
  } | null,
  catalogTopMm?: number,
): number {
  const catalogBody =
    parent?.freeDimensionCatalogMm?.y ||
    Number(parent?.morphs?.find((m) => m.name === 'Alto')?._default ?? 0) ||
    0
  const catalogTop =
    catalogTopMm && catalogTopMm > 0
      ? catalogTopMm
      : parent
        ? getIndependentZocaloApiFurnitureTopMm(parent, catalogBody) ||
          catalogBody ||
          (parent.height ?? 0)
        : 0

  const fromPos = Number(child.posicionY)
  if (Number.isFinite(fromPos) && !isContaminatedApplianceApiFloorYMm(fromPos, catalogTop, child)) {
    return fromPos
  }
  const fromBase = Number(child.baseY)
  if (Number.isFinite(fromBase) && !isContaminatedApplianceApiFloorYMm(fromBase, catalogTop, child)) {
    return fromBase
  }
  // Fregadero/placa/grifo: child.y es Y local (tras morph Alto) — NUNCA reutilizarla
  // como posicionY API (sellaba ~802 y dejaba el fregadero +100 mm al volver a Alto 800).
  if (isCountertopApplianceLinkedSon(child)) {
    return NaN
  }
  const fromY = Number(child.y)
  if (Number.isFinite(fromY) && !isContaminatedApplianceApiFloorYMm(fromY, catalogTop, child)) {
    return fromY
  }
  return NaN
}

/** Y persistida que ya no puede ser posicionY de catálogo (típ. doble aplicación / legacy). */
export function isContaminatedApplianceApiFloorYMm(
  candidateY: number,
  catalogFurnitureTopMm: number,
  child?: LinkedApplianceChildRef | null,
): boolean {
  if (!Number.isFinite(candidateY) || candidateY <= 0) return true
  // Tope mueble ~800–900; valores tipo 1630 (= 2×800+30) son Y locales corruptas.
  if (catalogFurnitureTopMm > 0 && candidateY > catalogFurnitureTopMm + 120) return true
  // Placa: la Y API está en el tope (~catálogo + 10). 810 con tope 900 es Y local de JSON viejo.
  if (
    child &&
    isPlacaLinkedApplianceSon(child) &&
    catalogFurnitureTopMm > 0 &&
    candidateY < catalogFurnitureTopMm - 40
  ) {
    return true
  }
  return false
}

type ApiFloorElemento = {
  idElementosEnlazados?: number
  descripcion?: string | null
  nodo?: number | null
  posicionX?: number | null
  posicionY?: number | null
  posicionZ?: number | null
}

/**
 * Sella posicionX/Y/Z + baseY desde el catálogo del padre (elementosEnlazados).
 * Misma ranura (nodo / tipo) → mismas coords suelo aunque el GLB de placa haya cambiado
 * (Placa3 → placa18).
 * @returns true si se sellaron coords API fiables.
 */
export function sealLinkedApplianceApiFloorCoordsFromParentCatalog(
  child: LinkedApplianceChildRef & {
    linkedID?: number | string | null
    idElementosEnlazados?: number | string | null
    nodo?: number | null
    x?: number
    y?: number
    z?: number
    posicionX?: number | null
    posicionZ?: number | null
  },
  parent: {
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    morphs?: Morphs[]
    height?: number
    hasIndependentZocalo?: boolean
    independentZocaloHeightMm?: number
    catalogID?: string | number
  },
  elementosEnlazados?: ApiFloorElemento[] | null,
): boolean {
  if (!linkedSonKeepsApiLocalTransform(child)) return false

  const catalogBody =
    parent.freeDimensionCatalogMm?.y ||
    Number(parent.morphs?.find((m) => m.name === 'Alto')?._default ?? 0) ||
    0
  const catalogTop =
    getIndependentZocaloApiFurnitureTopMm(parent, catalogBody) ||
    catalogBody ||
    (parent.height ?? 0)

  const list = Array.isArray(elementosEnlazados) ? elementosEnlazados : []
  const linkedKey = Number(
    child.idElementosEnlazados ?? child.linkedID ?? 0,
  )
  const childDesc = `${child.typeOfSon ?? ''} ${child.description ?? ''} ${child.catalogID ?? ''}`.toLowerCase()
  const kindRe = /(placa|fregadero|grifo|tpcgrifo|horno|micro)/i
  const childHasValidApiY =
    Number.isFinite(Number(child.posicionY)) &&
    Number(child.posicionY) > 0 &&
    !isContaminatedApplianceApiFloorYMm(Number(child.posicionY), catalogTop, child)

  let match: ApiFloorElemento | undefined
  let matchBy: 'id' | 'nodo' | 'kind' | null = null
  if (linkedKey > 0) {
    match = list.find((el) => Number(el.idElementosEnlazados) === linkedKey)
    if (match) matchBy = 'id'
  }
  if (!match && child.nodo != null) {
    match = list.find(
      (el) =>
        Number(el.nodo) === Number(child.nodo) &&
        kindRe.test(String(el.descripcion ?? '')) &&
        childDesc.includes(
          (String(el.descripcion ?? '').toLowerCase().match(kindRe)?.[1] ?? ''),
        ),
    )
    if (match) matchBy = 'nodo'
  }
  // Kind fallback only when this child has no own API Y (initial load).
  // Placa: no robar el hueco de otra placa del catálogo (Placa3 x=152 sobre placa18 800 mm).
  if (!match && !childHasValidApiY && !isPlacaLinkedApplianceSon(child)) {
    const kind = childDesc.match(kindRe)?.[1]
    if (kind) {
      match = list.find((el) => String(el.descripcion ?? '').toLowerCase().includes(kind))
      if (match) matchBy = 'kind'
    }
  }

  const fillMissingApiXZ = () => {
    if (isPlacaLinkedApplianceSon(child) && matchBy !== 'id') return
    // NUNCA desde child.x/z (pueden ser locales ya escalados). Solo API del catálogo.
    if (child.posicionX == null && typeof match?.posicionX === 'number') {
      child.posicionX = match.posicionX
    }
    if (child.posicionZ == null && typeof match?.posicionZ === 'number') {
      child.posicionZ = match.posicionZ
    }
  }

  const canOverwriteFromCatalog = matchBy === 'id' || (Boolean(match) && !childHasValidApiY)
  const apiY = Number(match?.posicionY)
  if (
    canOverwriteFromCatalog &&
    Number.isFinite(apiY) &&
    apiY > 0 &&
    !isContaminatedApplianceApiFloorYMm(apiY, catalogTop, child)
  ) {
    child.posicionY = apiY
    child.baseY = apiY
    fillMissingApiXZ()
    return true
  }

  if (childHasValidApiY) {
    child.baseY = Number(child.posicionY)
    fillMissingApiXZ()
    return true
  }

  const existingY = resolveLinkedApplianceApiFloorYMm(child, parent, catalogTop)
  if (Number.isFinite(existingY)) {
    child.posicionY = existingY
    child.baseY = existingY
    fillMissingApiXZ()
    return true
  }

  // Último recurso contra Y corruptas tipo 1630: anclar al tope de catálogo (+10 placa).
  if (catalogTop > 0 && isCountertopApplianceLinkedSon(child)) {
    const fallbackY = catalogTop + 10
    child.posicionY = fallbackY
    child.baseY = fallbackY
    return true
  }

  return false
}

/**
 * Reposiciona fregadero/placa/horno/grifo: X/Z × scale cuerpo; Y vía encimera o lift+scale.
 */
export function applyLinkedApplianceLocalTransformFromApi(
  child: LinkedApplianceChildRef,
  parent: {
    width?: number
    height?: number
    thickness?: number
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
    morphs?: Morphs[]
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    independentZocaloHeightMm?: number
    hasMorphs?: boolean
    glbSize?: Vector3
    id?: string
  },
  encimera?: EncimeraVisualRef | null,
  placaSit?: { glbMinYMm?: number; encimeraAabb?: { maxY: number } | null },
): void {
  if (!linkedSonKeepsApiLocalTransform(child)) return
  const { x: sx, z: sz } = getLinkedApplianceParentScaleRatios(parent)

  if (isPlacaLinkedApplianceSon(child)) {
    const plateW =
      Number((child as { width?: number }).width) ||
      (Number((child as { glbSize?: { x?: number } }).glbSize?.x) > 0
        ? Math.round(Number((child as { glbSize?: { x?: number } }).glbSize?.x) * 1000)
        : 0)
    const centered = getLinkedPlacaCenteredLocalXMm(parent.width ?? 0, plateW)
    if (centered != null) {
      child.x = centered
    } else if (child.posicionX != null && Number.isFinite(Number(child.posicionX))) {
      child.x = Number(child.posicionX)
    }
    if (child.posicionZ != null && Number.isFinite(Number(child.posicionZ))) {
      child.z = Number(child.posicionZ)
    }
    child.y = getLinkedApplianceLocalYMm(child, parent, encimera, placaSit)
    return
  }

  // X/Z solo desde coords API explícitas (posicionX/Z). Si solo reparamos Y
  // (proyectos legacy), no re-escalar un x local ya correcto (placa18 x=50 ≠ Placa3 152).
  if (child.posicionX != null && Number.isFinite(Number(child.posicionX))) {
    child.x = Number(child.posicionX) * sx
  }
  if (child.posicionZ != null && Number.isFinite(Number(child.posicionZ))) {
    child.z = Number(child.posicionZ) * sz
  }
  child.y = getLinkedApplianceLocalYMm(child, parent, encimera, placaSit)
}

/** Y local del zócalo: siempre al suelo (Y=0); el cuerpo se eleva por encima. */
export function getIndependentZocaloLocalYMm(
  item?: {
    hasIndependentZocalo?: boolean
    independentZocaloHeightMm?: number
  } | null,
): number {
  if (!item?.hasIndependentZocalo) return 0
  return 0
}

/**
 * Z local de la tira: delante del módulo, retranqueada ~30 mm (como el zócalo del GLB).
 * Origen del GLB ≈ cara trasera de la tira → cara delantera a parent.thickness − recess.
 * Si el fondo no cabe el retranqueo (GV 25 mm, COL fino), la tira va al frente (no a Z=0).
 */
export function getIndependentZocaloLocalZMm(
  parent: { thickness?: number } | null | undefined,
  zocaloNativeThicknessMm: number,
  recessMm: number = INDEPENDENT_ZOCALO_FRONT_RECESS_MM,
): number {
  const parentT = parent?.thickness ?? 0
  if (parentT <= 0) return 0
  const t = zocaloNativeThicknessMm > 0 ? zocaloNativeThicknessMm : 15
  const recess = recessMm > 0 ? recessMm : 0
  const zWithRecess = parentT - t - recess
  if (zWithRecess >= 0) return zWithRecess
  return Math.max(0, parentT - t)
}

type GvSnapRunItem = {
  id?: string
  type?: string
  thickness?: number
  matrix?: Matrix4
  snappedSides?: { left?: AnyID; right?: AnyID }
}

function walkGvSnapRunNeighbors(
  parent: {
    id?: string
    snappedSides?: { left?: AnyID; right?: AnyID }
  },
  items: Map<AnyID, GvSnapRunItem>,
): GvSnapRunItem[] {
  const out: GvSnapRunItem[] = []
  const seen = new Set<string>()
  if (parent.id != null) seen.add(String(parent.id))
  const queue: Array<{ snappedSides?: { left?: AnyID; right?: AnyID } }> = [parent]
  while (queue.length) {
    const cur = queue.shift()!
    for (const side of ['left', 'right'] as const) {
      const nid = cur.snappedSides?.[side]
      if (nid == null || nid === '') continue
      const key = String(nid)
      if (seen.has(key)) continue
      seen.add(key)
      const n = items.get(nid as AnyID)
      if (!n) continue
      if (n.type === 'walls' || n.type === 'partition' || n.type === 'area') continue
      if (isIndependentZocaloLinkedSon(n as never) || isZocaloCatalogOrDescription(n as never)) {
        continue
      }
      out.push(n)
      queue.push(n)
    }
  }
  return out
}

/**
 * Fondo (mm) contra el que alinear GV: el del tramo left/right (MBJ al lado).
 */
export function getIndependentZocaloAlignThicknessMm(
  parent: {
    id?: string
    thickness?: number
    catalogID?: string | number
    codigo?: string | number
    referencia?: string | number
    snappedSides?: { left?: AnyID; right?: AnyID }
  } | null | undefined,
  items?: Map<AnyID, GvSnapRunItem> | null,
): number {
  const own = Number(parent?.thickness ?? 0)
  if (!parent || !isGvMoldingUnit(parent) || !items?.size) return own
  let best = own
  for (const n of walkGvSnapRunNeighbors(parent, items)) {
    const nt = Number(n.thickness ?? 0)
    if (nt > best) best = nt
  }
  return best > 0 ? best : own
}

const _gvAlignPos = new Vector3()
const _gvAlignQuat = new Quaternion()
const _gvAlignScl = new Vector3()
const _gvAlignZ = new Vector3()
const _gvNbPos = new Vector3()
const _gvNbQuat = new Quaternion()
const _gvNbScl = new Vector3()
const _gvNbZ = new Vector3()

/**
 * Columna/mueble al lado: frente del GV = frente del vecino − 30 mm.
 * Se mueve el padre; el zócalo es hijo y va con él.
 */
export function alignGvParentToNeighborFront(
  parent: {
    id?: string
    thickness?: number
    x?: number
    y?: number
    z?: number
    matrix?: Matrix4
    catalogID?: string | number
    codigo?: string | number
    referencia?: string | number
    snappedSides?: { left?: AnyID; right?: AnyID }
  } | null | undefined,
  items?: Map<AnyID, GvSnapRunItem> | null,
  collidedId?: AnyID,
): void {
  if (!parent?.matrix || !isGvMoldingUnit(parent) || !items?.size) return
  const ownT = Number(parent.thickness ?? 0)
  if (!(ownT > 0)) return

  let neighbor: GvSnapRunItem | undefined
  if (collidedId != null) {
    const hit = items.get(collidedId)
    if (
      hit?.matrix &&
      hit.type !== 'walls' &&
      hit.type !== 'partition' &&
      hit.type !== 'area' &&
      !isIndependentZocaloLinkedSon(hit as never)
    ) {
      neighbor = hit
    }
  }
  if (!neighbor?.matrix) {
    for (const side of ['left', 'right'] as const) {
      const nid = parent.snappedSides?.[side]
      if (nid == null || nid === '') continue
      const n = items.get(nid as AnyID)
      if (
        n?.matrix &&
        n.type !== 'walls' &&
        n.type !== 'partition' &&
        n.type !== 'area' &&
        !isIndependentZocaloLinkedSon(n as never)
      ) {
        neighbor = n
        break
      }
    }
  }
  if (!neighbor?.matrix) return

  const neighborFondo = Number(neighbor.thickness ?? 0)
  if (!(neighborFondo > ownT + 1)) return

  parent.matrix.decompose(_gvAlignPos, _gvAlignQuat, _gvAlignScl)
  _gvAlignZ.set(0, 0, 1).applyQuaternion(_gvAlignQuat)
  neighbor.matrix.decompose(_gvNbPos, _gvNbQuat, _gvNbScl)

  const rel = _gvAlignPos.clone().sub(_gvNbPos).dot(_gvAlignZ)
  const desiredRel = neighborFondo - ownT - INDEPENDENT_ZOCALO_FRONT_RECESS_MM
  const delta = desiredRel - rel
  if (!Number.isFinite(delta) || Math.abs(delta) < 0.5) return

  _gvAlignPos.addScaledVector(_gvAlignZ, delta)
  parent.matrix.setPosition(_gvAlignPos)
  parent.x = _gvAlignPos.x
  parent.z = _gvAlignPos.z
}

/**
 * Si una unidad se colocó con el offset de suelo antiguo (+100), lo revierte
 * para que encimera/tiradores vuelvan a alinearse con el GLB.
 */
export function revertIndependentZocaloFloorOffsetIfNeeded(parent: {
  y?: number
  matrix?: { elements?: number[] }
  independentZocaloHeightMm?: number
  independentZocaloFloorOffsetApplied?: boolean
  hasIndependentZocalo?: boolean
}): boolean {
  if (!parent.independentZocaloFloorOffsetApplied) return false
  const h = getIndependentZocaloHeightMm(parent)
  if (h <= 0) {
    parent.independentZocaloFloorOffsetApplied = false
    return false
  }
  parent.y = Math.max(0, (parent.y ?? 0) - h)
  const el = parent.matrix?.elements
  if (el && el.length >= 14) {
    el[13] = Math.max(0, (Number(el[13]) || 0) - h)
  }
  parent.independentZocaloFloorOffsetApplied = false
  return true
}

/**
 * No subir el padre: rompía la Y de encimera/tiradores (API asumía padre en Y=0).
 * Se mantiene exportado como no-op por compatibilidad con itemActions.
 */
export function raiseParentForIndependentZocalo(_parent: {
  y?: number
  matrix?: { elements?: number[]; setPosition?: (x: number, y: number, z: number) => unknown }
  independentZocaloHeightMm?: number
  independentZocaloFloorOffsetApplied?: boolean
}): void {
  // no-op a propósito
}

/** Ancho nativo inferido del código de catálogo (p.ej. "ZOCALO 4000" → 4000). */
function parseIndependentZocaloCatalogAnchoMm(catalogID?: string | number): number {
  const code = String(catalogID ?? '')
  const m = code.match(/(\d{3,5})\s*$/)
  const n = m ? Number(m[1]) : 0
  return n >= 500 ? n : 0
}

/**
 * Fija bases nativas del zócalo (una sola vez). Necesario porque el hijo no pasa por
 * usesFreeDimensionScaling → newGetDataFromGLB no rellena freeDimensionGlbBaseMm.
 * Sin esto, scaleX caería a morph.default (= ancho padre tras sync) → scaleX=1 y tira a 4000 mm,
 * o peor: se confundía con escalado de bloque.
 */
export function stampIndependentZocaloNativeBases(
  zocalo: {
    width?: number
    height?: number
    thickness?: number
    catalogID?: string | number
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
  },
  opts?: {
    glbSizeMm?: FreeDimensionGlbBaseMm
    backGLBInfo?: { ancho?: number; alto?: number; fondo?: number }
  },
): FreeDimensionGlbBaseMm {
  const existing = zocalo.freeDimensionGlbBaseMm
  if (existing?.x != null && existing.x > 0) {
    if (!zocalo.freeDimensionCatalogMm) {
      zocalo.freeDimensionCatalogMm = { x: existing.x, y: existing.y ?? 0, z: existing.z ?? 0 }
    }
    return existing
  }

  const fromGlb = opts?.glbSizeMm
  const fromBack = opts?.backGLBInfo
  const parsedAncho = parseIndependentZocaloCatalogAnchoMm(zocalo.catalogID)

  // Preferir medida real del GLB; luego catálogo API; luego código.
  // NUNCA usar width store si parece ancho de módulo (ya sync): p.ej. 300–1200.
  const x =
    (fromGlb?.x != null && fromGlb.x > 0 ? fromGlb.x : 0) ||
    (fromBack?.ancho != null && fromBack.ancho > 0 ? fromBack.ancho : 0) ||
    parsedAncho ||
    (zocalo.width != null && zocalo.width >= 2000 ? zocalo.width : 0) ||
    4000
  const y =
    (fromGlb?.y != null && fromGlb.y > 0 ? fromGlb.y : 0) ||
    (fromBack?.alto != null && fromBack.alto > 0 ? fromBack.alto : 0) ||
    (zocalo.height != null && zocalo.height > 0 && zocalo.height < 200 ? zocalo.height : 0) ||
    INDEPENDENT_ZOCALO_HEIGHT_MM
  const z =
    (fromGlb?.z != null && fromGlb.z > 0 ? fromGlb.z : 0) ||
    (fromBack?.fondo != null && fromBack.fondo > 0 && fromBack.fondo < 200 ? fromBack.fondo : 0) ||
    (zocalo.thickness != null && zocalo.thickness > 0 && zocalo.thickness < 200
      ? zocalo.thickness
      : 0) ||
    15

  const base: FreeDimensionGlbBaseMm = { x, y, z }
  zocalo.freeDimensionGlbBaseMm = base
  if (!zocalo.freeDimensionCatalogMm) {
    zocalo.freeDimensionCatalogMm = {
      x: fromBack?.ancho && fromBack.ancho > 0 ? fromBack.ancho : x,
      y: fromBack?.alto && fromBack.alto > 0 ? fromBack.alto : y,
      z: fromBack?.fondo && fromBack.fondo > 0 ? fromBack.fondo : z,
    }
  }

  logIndependentZocaloTransform('stampIndependentZocaloNativeBases', {
    fn: 'stampIndependentZocaloNativeBases',
    phase: 'stamp.nativeBases',
    catalogID: zocalo.catalogID,
    glbSizeMm: fromGlb ?? null,
    backGLBInfo: fromBack
      ? { ancho: fromBack.ancho, alto: fromBack.alto, fondo: fromBack.fondo }
      : null,
    stampedGlbBaseMm: base,
    stampedCatalogMm: zocalo.freeDimensionCatalogMm,
  })

  return base
}

/** Escala X del zócalo: longitud útil de la tira / longitud nativa del GLB. */
export function getIndependentZocaloAnchoScaleX(
  zocalo: {
    width?: number
    catalogID?: string | number
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    morphs?: Morphs[]
  } | null | undefined,
  parent: { width?: number } | null | undefined,
): number {
  // Preferir width de la tira (puerta/retorno en rinconero); si no, ancho del padre.
  const target =
    (zocalo?.width != null && zocalo.width > 0 ? zocalo.width : 0) || (parent?.width ?? 0)
  if (target <= 0) return 1
  // NUNCA usar morph.default como base: tras sync puede valer parent.width → scaleX=1 erróneo.
  const base =
    (zocalo?.freeDimensionGlbBaseMm?.x != null && zocalo.freeDimensionGlbBaseMm.x > 0
      ? zocalo.freeDimensionGlbBaseMm.x
      : 0) ||
    (zocalo?.freeDimensionCatalogMm?.x != null && zocalo.freeDimensionCatalogMm.x > 0
      ? zocalo.freeDimensionCatalogMm.x
      : 0) ||
    parseIndependentZocaloCatalogAnchoMm(zocalo?.catalogID) ||
    4000
  return target / base
}

/**
 * Escala Y del zócalo: alto objetivo / alto nativo del GLB.
 * Si nativo ≈ objetivo → 1.
 */
export function getIndependentZocaloAltoScaleY(
  zocalo: {
    height?: number
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
  } | null | undefined,
  parent?: { independentZocaloHeightMm?: number } | null,
): number {
  const target = getIndependentZocaloHeightMm(parent)
  if (!(target > 0)) return 1
  const base =
    (zocalo?.freeDimensionGlbBaseMm?.y != null && zocalo.freeDimensionGlbBaseMm.y > 0
      ? zocalo.freeDimensionGlbBaseMm.y
      : 0) ||
    (zocalo?.freeDimensionCatalogMm?.y != null && zocalo.freeDimensionCatalogMm.y > 0
      ? zocalo.freeDimensionCatalogMm.y
      : 0) ||
    INDEPENDENT_ZOCALO_HEIGHT_MM
  if (!(base > 0)) return 1
  const s = target / base
  if (!Number.isFinite(s) || s < 0.2 || s > 5) return 1
  return s
}

/**
 * Zócalo = tira: NUNCA escalar fondo/espesor al del padre.
 * Antes: parent.thickness / glbZ (p.ej. 600/15) → bloque deformado.
 */
export function getIndependentZocaloFondoScaleZ(
  _zocalo?: {
    thickness?: number
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    morphs?: Morphs[]
  } | null,
  _parent?: { thickness?: number } | null,
): number {
  return 1
}

/** Espesor nativo del GLB/catálogo del zócalo (mm); no hereda fondo del padre. */
export function getIndependentZocaloNativeThicknessMm(
  zocalo: {
    thickness?: number
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    morphs?: Morphs[]
  } | null | undefined,
): number {
  const fromGlb = zocalo?.freeDimensionGlbBaseMm?.z
  if (fromGlb != null && fromGlb > 0) return fromGlb
  const fromCat = zocalo?.freeDimensionCatalogMm?.z
  if (fromCat != null && fromCat > 0) return fromCat
  const morphFondo = zocalo?.morphs?.find((m) => m.name === 'Fondo')
  const fromMorph = Number(morphFondo?._default ?? morphFondo?.default ?? 0)
  if (fromMorph > 0 && fromMorph < 200) return fromMorph
  const t = Number(zocalo?.thickness ?? 0)
  return t > 0 && t < 200 ? t : 15
}

function logIndependentZocaloTransform(
_phase: string,
  _payload: Record<string, unknown>,
): void {
  // no-op
}

/**
 * Coloca el zócalo al pie (Y=0): alto fijo, espesor nativo, solo Ancho = padre
 * (o tramo de puerta / retorno en rinconeros).
 * No hereda Fondo del padre ni escala Y/Z (tira, no bloque).
 */
export function placeLinkedIndependentZocalo(
  zocalo: {
    x?: number
    y?: number
    z?: number
    width?: number
    height?: number
    thickness?: number
    morphs?: Morphs[]
    isIndependentZocalo?: boolean
    isIndependentZocaloReturn?: boolean
    independentZocaloRotY?: number | null
    visible?: boolean
    matrix?: { elements?: number[] }
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    id?: string
  },
  parent: {
    width?: number
    thickness?: number
    independentZocaloHeightMm?: number
    hasIndependentZocalo?: boolean
    catalogID?: string | number
    id?: string
    snappedSides?: { left?: AnyID; right?: AnyID }
    independentZocaloSonId?: string | null
    childs?: Set<string>
    matrix?: Matrix4
    x?: number
    y?: number
    z?: number
    regleta?: number | null
    regletaMaximo?: number | null
    morphs?: Morphs[]
    freeDimensionCatalogMm?: { z?: number } | null
    freeDimensionGlbBaseMm?: { z?: number } | null
  },
  opts?: {
    glbSizeMm?: FreeDimensionGlbBaseMm
    backGLBInfo?: { ancho?: number; alto?: number; fondo?: number }
    items?: Map<AnyID, GvSnapRunItem>
  },
): void {
  // Bases nativas ANTES de sobrescribir width/thickness con valores de layout.
  stampIndependentZocaloNativeBases(zocalo, opts)

  const zocaloH = getIndependentZocaloHeightMm(parent)
  parent.independentZocaloHeightMm = zocaloH
  const parentW = parent.width ?? 0
  const nativeThickness = getIndependentZocaloNativeThicknessMm(zocalo)
  const rinconero = getRinconeroIndependentZocaloLayout(parent, nativeThickness, opts?.items)

  zocalo.isIndependentZocalo = true
  zocalo.y = 0
  zocalo.visible = true

  let stripWidth = parentW
  if (zocalo.isIndependentZocaloReturn && rinconero) {
    zocalo.x = rinconero.returnX
    zocalo.z = rinconero.returnZ
    zocalo.independentZocaloRotY = rinconero.returnRotY
    stripWidth = rinconero.returnLengthMm
  } else if (rinconero) {
    zocalo.isIndependentZocaloReturn = false
    zocalo.independentZocaloRotY = null
    zocalo.x = rinconero.frontX
    zocalo.z = getIndependentZocaloLocalZMm(
      { thickness: rinconero.frontPlaneMm },
      nativeThickness,
    )
    stripWidth = rinconero.frontStripWidthMm
  } else {
    zocalo.isIndependentZocaloReturn = false
    zocalo.independentZocaloRotY = null
    zocalo.x = 0
    // GV: el padre ya se adelantó al frente del vecino; la tira va al frente del propio GLB.
    zocalo.z = getIndependentZocaloLocalZMm(parent, nativeThickness)
    stripWidth = parentW
  }

  zocalo.height = zocaloH
  zocalo.thickness = nativeThickness
  if (stripWidth > 0) zocalo.width = stripWidth

  for (const m of zocalo.morphs ?? []) {
    if (m.name === 'Alto') {
      m.default = zocaloH
      m._default = zocaloH
      // Sin morph de malla: la altura es fija por store + scaleY=1.
      m.min = 0
      m.max = 0
    }
    if (m.name === 'Ancho') {
      // Ancho visual = scaleX del grupo (stripWidth / glbBaseX). Morph desactivado.
      // _default = longitud nativa de la tira (4000), NUNCA stripWidth (si no scaleX=1).
      const nativeAncho =
        (zocalo.freeDimensionGlbBaseMm?.x != null && zocalo.freeDimensionGlbBaseMm.x > 0
          ? zocalo.freeDimensionGlbBaseMm.x
          : 0) ||
        (zocalo.freeDimensionCatalogMm?.x != null && zocalo.freeDimensionCatalogMm.x > 0
          ? zocalo.freeDimensionCatalogMm.x
          : 0) ||
        4000
      m._default = nativeAncho
      m.default = stripWidth > 0 ? stripWidth : m.default
      m.min = 0
      m.max = 0
    }
    if (m.name === 'Fondo') {
      // Espesor nativo: no copiar fondo del padre.
      m.default = nativeThickness
      m._default = nativeThickness
      m.min = 0
      m.max = 0
    }
  }
  syncLinkedSonLocalMatrixTranslation(zocalo)

  const scaleX = getIndependentZocaloAnchoScaleX(zocalo, parent)
  const scaleY = getIndependentZocaloAltoScaleY(zocalo, parent)
  const scaleZ = getIndependentZocaloFondoScaleZ(zocalo, parent)
  logIndependentZocaloTransform('placeLinkedIndependentZocalo', {
    fn: 'placeLinkedIndependentZocalo',
    phase: 'place.after',
    zocaloId: zocalo.id,
    zocaloCatalog: zocalo.catalogID,
    parentId: parent.id,
    parentCatalog: parent.catalogID,
    parentWidth_mm: parentW,
    parentThickness_mm: parent.thickness,
    rinconero: Boolean(rinconero),
    isReturn: Boolean(zocalo.isIndependentZocaloReturn),
    glbBaseMm: zocalo.freeDimensionGlbBaseMm,
    catalogMm: zocalo.freeDimensionCatalogMm,
    storeMm: {
      width: zocalo.width,
      height: zocalo.height,
      thickness: zocalo.thickness,
    },
    scaleXYZ: { x: scaleX, y: scaleY, z: scaleZ },
    visualEstMm: zocalo.freeDimensionGlbBaseMm
      ? {
          x: Math.round((zocalo.freeDimensionGlbBaseMm.x ?? 0) * scaleX),
          y: Math.round((zocalo.freeDimensionGlbBaseMm.y ?? 0) * scaleY),
          z: Math.round((zocalo.freeDimensionGlbBaseMm.z ?? 0) * scaleZ),
        }
      : null,
    localPosMm: { x: zocalo.x, y: zocalo.y, z: zocalo.z },
    bodyLiftMm: getIndependentZocaloBodyLiftMm(parent),
    rule:
      'Zócalo Y=0. Rinconero L: frontal=puerta+30 (empuja retorno); retorno=puerta+regleta.',
  })
}

export function syncIndependentZocaloToParent(
  zocalo: Parameters<typeof placeLinkedIndependentZocalo>[0],
  parent: Parameters<typeof placeLinkedIndependentZocalo>[1],
  opts?: Parameters<typeof placeLinkedIndependentZocalo>[2],
): void {
  placeLinkedIndependentZocalo(zocalo, parent, opts)
}

/** Tras snap: padre + zócalo = frente de la columna − 30 mm. */
export function syncGvIndependentZocaloToRun(
  parent:
    | (Parameters<typeof placeLinkedIndependentZocalo>[1] & {
        independentZocaloSonId?: string | null
        childs?: Set<string>
        matrix?: Matrix4
        x?: number
        y?: number
        z?: number
      })
    | null
    | undefined,
  items: Map<AnyID, GvSnapRunItem>,
  collidedId?: AnyID,
): void {
  if (!parent || !isGvMoldingUnit(parent) || !items?.size) return
  alignGvParentToNeighborFront(parent, items, collidedId)
  let zocalo =
    parent.independentZocaloSonId != null
      ? items.get(parent.independentZocaloSonId as AnyID)
      : undefined
  if (!zocalo && parent.childs) {
    for (const cid of parent.childs) {
      const child = items.get(cid as AnyID)
      if (
        child &&
        isIndependentZocaloLinkedSon(child as never) &&
        !(child as { isIndependentZocaloReturn?: boolean }).isIndependentZocaloReturn
      ) {
        zocalo = child
        break
      }
    }
  }
  if (!zocalo || !isIndependentZocaloLinkedSon(zocalo as never)) return
  placeLinkedIndependentZocalo(zocalo as Parameters<typeof placeLinkedIndependentZocalo>[0], parent, {
    items,
  })
}

export function usesFreeDimensionAltoOffset(
  item: { catalogID?: string | number; codigo?: string | number } | null | undefined,
): boolean {
  return usesFreeDimensionScaling(item) && getFreeDimensionAltoOffsetMm(item) !== 0
}

/** Encimera ancho TPC: morph Alto API (grosor 10–30 mm), ancho/fondo por escala grupo. */
export function isFreeDimensionEncimeraAnchoCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return (FREE_DIMENSION_ENCIMERA_ANCHO_CATALOG_IDS as readonly string[]).includes(code)
}

/** Lateral cascada ancho: morph Alto API, fondo por escala grupo. */
export function isFreeDimensionLateralCascadaAnchoCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return (FREE_DIMENSION_LATERAL_CASCADA_ANCHO_CATALOG_IDS as readonly string[]).includes(code)
}

/** Catálogos free-dim que mantienen rango de morph Alto del API. */
export function isFreeDimensionAltoApiMorphCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  return (
    isFreeDimensionEncimeraAnchoCatalog(item) || isFreeDimensionLateralCascadaAnchoCatalog(item)
  )
}

/** Catálogos free-dim que deben respetar rango API del morph Ancho. */
export function isFreeDimensionAnchoApiMorphCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  return isFreeDimensionLateralCascadaAnchoCatalog(item)
}

/** Encimera / costado / zócalo / frontal sin morphs: todo por escala grupo. */
export function isFreeDimensionTpcNoMorphCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  if (isStandaloneZocaloTiraCatalog(item)) return true
  const code = getLinkedItemCatalogCode(item)
  return (FREE_DIMENSION_TPC_NO_MORPH_CATALOG_IDS as readonly string[]).includes(code)
}

export function morphHasValidRange(morph?: Morphs | null): boolean {
  if (!morph) return false
  const min = morph.min ?? 0
  const max = morph.max ?? 0
  return max > min
}

export function clampMorphMm(value: number, min?: number | null, max?: number | null): number {
  let v = value
  if (min != null && v < min) v = min
  if (max != null && v > max) v = max
  return v
}

/** Alto con rango API en encimeras ancho: deformar mesh, no escala Y del grupo. */
export function itemUsesFreeDimensionAltoApiMorph(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    morphs?: Morphs[]
  } | null,
): boolean {
  if (!isFreeDimensionAltoApiMorphCatalog(item)) return false
  const alto = item?.morphs?.find((m) => m.name === 'Alto')
  return morphHasValidRange(alto)
}

/** Morphs de malla (p. ej. Alto 10–30) aunque ancho/fondo usen escala free-dim. */
export function linkedItemUsesMeshMorphs(
  item?: {
    hasMorphs?: boolean
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    morphs?: Morphs[]
  } | null,
): boolean {
  if (!item?.hasMorphs) return false
  if (!usesFreeDimensionScaling(item)) return true
  // MBJ/COL: morphs clásicos activos (+ scale si sidebar sale del rango).
  if (usesPureGroupFreeDimensionScale(item)) return true
  return itemUsesFreeDimensionAltoApiMorph(item)
}

/** Laterales y costados (LA/LB/LC/CC/CB/RA): alto store = catálogo, sin +100. */
export function isFreeDimensionLateralOrCostadoCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  let code = getLinkedItemCatalogCode(item)
  if (!code) return false
  // Prefijo PE / sufijos de acabado no deben impedir reconocer LA8035, no LAVAVAJILLAS.
  if (code.startsWith('PE ')) code = code.slice(3).trim()
  code = code.replace(/\s+(LI|LA|RU|R2|GO)\b.*$/i, '').trim()
  return /^(LA|LB|LC|CC|CB|RA)\d/.test(code)
}

/**
 * Bbox GLB más alto que catálogo y sin offset en store (p. ej. LC14060: 1500 vs 1400).
 * El exceso suele ser la losa/zócalo del mesh; la escala Y del grupo es layout/glbY.
 */
export function usesFreeDimensionGlbZocaloSlabInAltoBbox(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    morphs?: Morphs[]
  } | null,
): boolean {
  if (!item || !isFreeDimensionLateralOrCostadoCatalog(item)) return false
  if (getFreeDimensionAltoOffsetMm(item) !== 0) return false
  if (itemUsesFreeDimensionAltoGlbMorph(item)) return false
  const catY = item.freeDimensionCatalogMm?.y ?? 0
  const glbY = item.freeDimensionGlbBaseMm?.y ?? 0
  return catY > 0 && glbY > catY + 1
}

/** Escala Y del grupo: item.height (mm store) / base (morph clamp o catálogo; bbox solo fallback). */
export function getFreeDimensionAltoGroupScaleY(
  item: {
    height?: number
    morphs?: Morphs[]
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    glbSize?: Vector3
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    hasMorphs?: boolean
  } | null | undefined,
): number {
  if (itemUsesFreeDimensionAltoApiMorph(item)) return 1
  if (usesPureGroupFreeDimensionScale(item)) {
    return getMbjColFreeDimensionScaleRatios(item as Parameters<typeof getMbjColFreeDimensionScaleRatios>[0]).y
  }
  const target = getFreeDimensionAltoScaleTargetMm(item)
  if (target <= 0) return 1
  const glbY =
    item?.freeDimensionGlbBaseMm?.y ??
    (item?.glbSize?.y != null && item.glbSize.y > 0 ? Math.round(item.glbSize.y * 1000) : 0)
  if (glbY <= 0) return 1
  // LA8035 etc.: glbBase a veces es AABB con zócalo (~900) y catálogo/Costado 800.
  // scaleY = 800/900 comprime el panel a ~711 aunque el sidebar diga 800.
  // Escalar contra catálogo cuando el exceso es la losa integrada.
  const catY = item?.freeDimensionCatalogMm?.y ?? 0
  if (usesFreeDimensionGlbZocaloSlabInAltoBbox(item) && catY > 0) {
    return target / catY
  }
  return target / glbY
}

/** mm en store (−100 LC23060, +100 RB bajos; RC/TPC encimera/zócalo/frontal/laterales/MAL: 0). */
export function getFreeDimensionAltoOffsetMm(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    claveMenu?: string | null
    menuPrincipal?: string | null
  } | null,
): number {
  if (!item || !usesFreeDimensionScaling(item)) return 0
  // Laterales/costados: store = catálogo (sin ±100), aunque tengan flag zócalo antiguo.
  if (isFreeDimensionLateralOrCostadoCatalog(item)) return 0
  // MAL: altos libres sin zócalo tira — no sumar +100 (si no laterales LA quedan ~100 cortos).
  if (isMalFreeDimensionUnit(item)) return 0
  // Electrodomésticos: alto store = alto real, sin +100 de zócalo de mueble.
  if (isApplianceMenuUnit(item)) return 0
  const code = getLinkedItemCatalogCode(item)
  // Resto de RB*: store 900 aunque arrastre un flag zócalo. RB805 cae en zócalo hijo (0).
  if (code.startsWith('RB') && !isRb805Catalog(item)) return FREE_DIMENSION_ALTO_OFFSET_MM
  // Cuerpo MBJ/COL con zócalo hijo: el alto store es solo el cuerpo (sin +100).
  if (shouldUseIndependentZocaloUnit(item) || item.hasIndependentZocalo) return 0
  // RC2305 etc.: alto API = altura real del GLB (2300). Sin +100
  // (si no sidebar = store−100 → 2200 con height 2300).
  if (code.startsWith('RC')) return 0
  if (code.startsWith('RB')) return FREE_DIMENSION_ALTO_OFFSET_MM
  if ((FREE_DIMENSION_ALTO_OFFSET_MINUS_100_CATALOG_IDS as readonly string[]).includes(code)) {
    return -FREE_DIMENSION_ALTO_OFFSET_MM
  }
  if (isFreeDimensionTpcNoMorphCatalog(item) || isFreeDimensionAltoApiMorphCatalog(item)) {
    return 0
  }
  return FREE_DIMENSION_ALTO_OFFSET_MM
}

export function getFreeDimensionStoreAltoAtPlace(
  catalogBodyMm: number,
  item: {
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
  },
): number {
  return catalogBodyMm + getFreeDimensionAltoOffsetMm(item)
}

/** Alto store al colocar: RB usa posicionY encimera del API (p. ej. alto 800 → encimera 900). */
export function getFreeDimensionPlacementStoreAltoMm(
  backGLBInfo?: FreeDimensionPlacementSource | null,
  catalogBodyAltoMm?: number,
  catalogID?: string | number,
  extra?: {
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
  },
): number {
  const bodyMm = catalogBodyAltoMm ?? backGLBInfo?.alto ?? 0
  const itemCtx = {
    catalogID,
    tipoNormalizado: extra?.tipoNormalizado ?? backGLBInfo?.tipoNormalizado,
    description: extra?.description ?? backGLBInfo?.descripcion,
    referencia: extra?.referencia,
    hasIndependentZocalo: extra?.hasIndependentZocalo,
  }
  if (isRbFreeDimensionRegleta(itemCtx)) {
    const code = getLinkedItemCatalogCode(itemCtx)
    // RC*: alto de unidad = store (2300), no cuerpo+100.
    if (code.startsWith('RC')) return bodyMm > 0 ? bodyMm : 0
    if (isRb805Catalog(itemCtx)) {
      return getFreeDimensionStoreAltoAtPlace(bodyMm, itemCtx)
    }
    const enc = backGLBInfo?.elementosEnlazados?.find((e) =>
      String(e.descripcion ?? '').toLowerCase().includes('encimera'),
    )
    const encY = enc?.posicionY
    if (bodyMm > 0 && typeof encY === 'number' && encY >= bodyMm) return encY
  }
  return getFreeDimensionStoreAltoAtPlace(bodyMm, itemCtx)
}

/** Alto activo en sidebar/escala para encimera/zócalo/frontal TPC (sin offset +100 legacy). */
function getFreeDimensionTpcNoMorphLayoutAltoMm(
  item?: {
    height?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    codigo?: string | number
  } | null,
): number {
  if (!item) return 0
  const storeH = item.height ?? 0
  const catY = item.freeDimensionCatalogMm?.y ?? 0
  const morphAlto = item.morphs?.find((m) => m.name === 'Alto')
  const fromMorph = morphAlto?._default
  // Preferir store.height (como Ancho usa width). Si no, morph/_default deja el
  // Frontal de proyectos antiguos anclado al catálogo y Alto/Fondo no reaccionan.
  if (storeH > 0) {
    // Corregir height guardado con +100 mm de regletas (p. ej. store 110 / morph 10).
    if (catY > 0 && storeH === catY + FREE_DIMENSION_ALTO_OFFSET_MM) {
      return catY
    }
    return storeH
  }
  if (fromMorph != null && fromMorph > 0) return fromMorph
  return catY
}

export function getFreeDimensionSidebarAlto(
  storeHeight?: number,
  item?: {
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    golaActiva?: boolean | null
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    morphs?: Morphs[]
    height?: number
  } | null,
): number {
  if (isFreeDimensionTpcNoMorphCatalog(item)) {
    return getFreeDimensionTpcNoMorphLayoutAltoMm({
      ...item,
      height: storeHeight ?? item?.height,
    })
  }
  const offset = getFreeDimensionAltoOffsetMm(item)
  if (storeHeight == null || storeHeight <= 0) {
    return item?.freeDimensionCatalogMm?.y ?? 0
  }
  const sidebar = Math.max(0, storeHeight - offset)
  return isMalGolaFurnitureUnit(item) ? snapMalGolaAltoMm(sidebar) : sidebar
}

export function getFreeDimensionStoreAlto(
  sidebarAltoMm: number,
  item?: {
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    hasMorphs?: boolean
    golaActiva?: boolean | null
  } | null,
): number {
  const store = sidebarAltoMm + getFreeDimensionAltoOffsetMm(item)
  return isMalGolaFurnitureUnit(item) ? snapMalGolaAltoMm(store) : store
}

/** Valor morph GLB / item.height (mm) = sidebar + zócalo (+100 RB/RC, −100 LC…). */
export function getFreeDimensionAltoGlbMorphMm(
  sidebarAltoMm: number,
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): number {
  return getFreeDimensionStoreAlto(sidebarAltoMm, item)
}

/** Máximo sidebar (cuerpo sin zócalo) a partir del max del morph GLB. */
export function getFreeDimensionAltoSidebarMorphMaxMm(
  morphMaxMm: number,
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): number {
  const offset = getFreeDimensionAltoOffsetMm(item)
  if (offset === 0 || morphMaxMm <= 0) return morphMaxMm
  return Math.max(0, morphMaxMm - offset)
}

/** LB8060, CB9060: lateral MBJ + encimera (sync ancho/fondo hijo; alto = catálogo). */
export function freeDimensionUsesLb8060StyleAltoOffset(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return (FREE_DIMENSION_LB8060_STYLE_CATALOG_IDS as readonly string[]).includes(code)
}

/**
 * Bbox X mucho mayor que catálogo/layout → el cuerpo usa morph Ancho del GLB, no scale X del grupo.
 * (RB805: bbox 190 mm, regleta real ~50 mm; escalar el grupo deja una tira invisible).
 */
export function resolveFreeDimensionAnchoViaGlbMorph(
  glbBaseMm: FreeDimensionGlbBaseMm,
  catalogMm: FreeDimensionCatalogMm,
  layoutWidthMm: number,
): boolean {
  const box = glbBaseMm.x
  const cat = catalogMm.x
  if (box > 0 && cat > 0 && box > cat * 1.15) return true
  if (box > 0 && layoutWidthMm > 0 && box > layoutWidthMm * 2.5) return true
  return false
}

export function getFreeDimensionAnchoMorphMaxMm(
  morph: Morphs | undefined,
  catalogMm?: FreeDimensionCatalogMm,
): number {
  if (morph?.max != null && morph.min != null && morph.max > morph.min) return morph.max
  const cat = catalogMm?.x ?? 0
  return cat > 0 ? cat : 100
}

/** Encimera por defecto en regletas RB (API suele traer B10; producto usa B80new). */
export const RB_REGLETA_ENCIMERA_CODIGO = 'Encimera B80new'

/** Bbox Z &gt; catálogo: fondo por morph GLB, no solo scale Z del grupo. */
export function resolveFreeDimensionFondoViaGlbMorph(
  glbBaseMm: FreeDimensionGlbBaseMm,
  catalogMm: FreeDimensionCatalogMm,
  catalogID?: string | number,
): boolean {
  // RB805: sin morph Fondo útil en malla (min≈max) → escala grupo en Z, no morph.
  if (isRbFreeDimensionRegleta({ catalogID })) return false
  const box = glbBaseMm.z
  const cat = catalogMm.z
  if (box > 0 && cat > 0 && box > cat * 1.08) return true
  return false
}

export function getFreeDimensionFondoMorphMaxMm(
  morph: Morphs | undefined,
  catalogMm?: FreeDimensionCatalogMm,
  glbBaseMm?: FreeDimensionGlbBaseMm,
): number {
  if (morph?.max != null && morph.min != null && morph.max > morph.min) return morph.max
  const cat = catalogMm?.z ?? 0
  const glb = glbBaseMm?.z ?? 0
  if (glb > cat && cat > 0) return glb
  return cat > 0 ? cat : 600
}

/** Escala Z extra cuando fondo sidebar &gt; morph.max del GLB. */
export function getFreeDimensionFondoGlbMorphScaleZ(
  thicknessMm: number,
  morph: Morphs | undefined,
  catalogMm?: FreeDimensionCatalogMm,
): number {
  const t = thicknessMm > 0 ? thicknessMm : 0
  if (t <= 0) return 1
  const max = getFreeDimensionFondoMorphMaxMm(morph, catalogMm, undefined)
  return t > max ? t / max : 1
}

export function applyFreeDimensionFondoGlbMorphMorph(
  morph: Morphs,
  layoutThicknessMm: number,
  morphMaxMm: number,
): Morphs {
  const min =
    morph.min != null && morph.max != null && morph.max > morph.min ? morph.min : 10
  const max = morphMaxMm > min ? morphMaxMm : min + 1
  const t = layoutThicknessMm > 0 ? layoutThicknessMm : max
  const morphDefault = Math.min(Math.max(t, min), max)
  return {
    ...morph,
    min,
    max,
    default: morphDefault,
    _default: t,
    show: true,
    modifyable: true,
  }
}

/**
 * Ancho activo (mm) para colisión/snap lateral.
 * En free-dim morph.default guarda la referencia GLB (denominador de la escala del grupo),
 * no el ancho actual: usar item.width (o morph._default). Resto de items: null.
 */
export function getFreeDimensionCollisionAnchoMm(
  item?: {
    width?: number
    morphs?: Morphs[]
    catalogID?: string | number
    codigo?: string | number
  } | null,
): number | null {
  if (!usesFreeDimensionScaling(item)) return null
  const morph = item?.morphs?.find((m) => m.name === 'Ancho')
  const width = item?.width ?? morph?._default ?? 0
  return width > 0 ? width : null
}

/** Escala X extra cuando ancho sidebar &gt; morph.max del GLB (p. ej. RB805: 350 mm con morph máx. 100). */
export function getFreeDimensionAnchoGlbMorphScaleX(
  widthMm: number,
  morph: Morphs | undefined,
  catalogMm?: FreeDimensionCatalogMm,
): number {
  const w = widthMm > 0 ? widthMm : 0
  if (w <= 0) return 1
  const max = getFreeDimensionAnchoMorphMaxMm(morph, catalogMm)
  return w > max ? w / max : 1
}

/**
 * Alto siempre por escala de grupo (ratios.y), no morph GLB.
 * El morph en Y deforma puerta/zócalo/encimera y desincroniza catalogY×ratio.
 * Ancho sí usa morph cuando bbox ≫ catálogo (RB805, regletas).
 */
export function resolveFreeDimensionAltoViaGlbMorph(
  _glbBaseMm: FreeDimensionGlbBaseMm,
  _catalogMm: FreeDimensionCatalogMm,
  _altoOffsetMm: number,
): boolean {
  return false
}

export function getFreeDimensionAltoMorphMaxMm(
  morph: Morphs | undefined,
  catalogMm?: FreeDimensionCatalogMm,
  altoOffsetMm = 0,
): number {
  if (morph?.max != null && morph.min != null && morph.max > morph.min) return morph.max
  const cat = catalogMm?.y ?? 0
  return cat > 0 ? cat + altoOffsetMm : 900
}

export function getFreeDimensionAltoGlbMorphScaleY(
  sidebarAltoMm: number,
  morph: Morphs | undefined,
  item?: {
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    codigo?: string | number
  },
): number {
  const layout = sidebarAltoMm > 0 ? sidebarAltoMm : 0
  if (layout <= 0) return 1
  const offset = getFreeDimensionAltoOffsetMm(item)
  const max = getFreeDimensionAltoMorphMaxMm(morph, item?.freeDimensionCatalogMm, offset)
  return layout > max ? layout / max : 1
}

export function applyFreeDimensionAltoGlbMorphMorph(
  morph: Morphs,
  layoutAltoMm: number,
  catalogAltoMm: number,
  altoOffsetMm: number,
): Morphs {
  const cat = catalogAltoMm > 0 ? catalogAltoMm : 800
  const min = cat
  const max = cat + (altoOffsetMm > 0 ? altoOffsetMm : FREE_DIMENSION_ALTO_OFFSET_MM)
  const h = layoutAltoMm > 0 ? layoutAltoMm : cat
  const morphDefault = Math.min(
    altoOffsetMm !== 0 ? h + altoOffsetMm : h,
    max,
  )
  return {
    ...morph,
    min,
    max,
    default: morphDefault,
    _default: h,
    show: true,
    modifyable: true,
  }
}

export function applyFreeDimensionAnchoGlbMorphMorph(
  morph: Morphs,
  layoutWidthMm: number,
  catalogWidthMm: number,
): Morphs {
  const max = catalogWidthMm > 0 ? catalogWidthMm : 100
  const min = 10
  const w = layoutWidthMm > 0 ? layoutWidthMm : 50
  const morphDefault = Math.min(w, max)
  return {
    ...morph,
    min,
    max,
    default: morphDefault,
    _default: w,
    show: true,
    modifyable: true,
  }
}

/**
 * Morphs al colocar.
 * - Ancho (si bbox ≫ catálogo): morph GLB hasta max catálogo + scaleX si sidebar &gt; max.
 * - Alto (bbox con losa): morph GLB cat…cat+offset + scaleY extra si sidebar &gt; max.
 * - Resto: ref GLB en default, activo en _default → scale grupo.
 */
export function applyFreeDimensionUniversalStyleMorphs(
  morphs: Morphs[],
  layout: { width: number; height: number; thickness: number },
  glbBaseMm: FreeDimensionGlbBaseMm,
  catalogMm: FreeDimensionCatalogMm,
  anchoViaGlbMorph = false,
  altoViaGlbMorph = false,
  fondoViaGlbMorph = false,
  altoOffsetMm = 0,
  preserveAltoApiMorphRange = false,
  preserveAnchoApiMorphRange = false,
  preserveClassicMorphRanges = false,
): Morphs[] {
  const scaleBase = resolveFreeDimensionScaleBaseMm(catalogMm, glbBaseMm)

  const layoutByName = {
    Ancho: layout.width,
    Alto: layout.height,
    Fondo: layout.thickness,
  } as const

  const glbRefByName = {
    Ancho: glbBaseMm.x > 0 ? glbBaseMm.x : scaleBase.x,
    Alto: glbBaseMm.y > 0 ? glbBaseMm.y : scaleBase.y,
    Fondo: glbBaseMm.z > 0 ? glbBaseMm.z : scaleBase.z,
  } as const

  return morphs.map((m) => {
    if (!isFreeDimensionDimensionMorph(m.name)) return m

    if (anchoViaGlbMorph && m.name === 'Ancho') {
      return applyFreeDimensionAnchoGlbMorphMorph(m, layout.width, catalogMm.x)
    }

    if (altoViaGlbMorph && m.name === 'Alto') {
      return applyFreeDimensionAltoGlbMorphMorph(m, layout.height, catalogMm.y, altoOffsetMm)
    }

    if (fondoViaGlbMorph && m.name === 'Fondo') {
      const morphMax = getFreeDimensionFondoMorphMaxMm(m, catalogMm, glbBaseMm)
      return applyFreeDimensionFondoGlbMorphMorph(m, layout.thickness, morphMax)
    }

    if (preserveAltoApiMorphRange && m.name === 'Alto' && morphHasValidRange(m)) {
      const active = clampMorphMm(layout.height, m.min, m.max)
      return {
        ...m,
        _default: active,
        default: active,
        min: m.min,
        max: m.max,
        show: true,
        modifyable: true,
      }
    }

    if (preserveAnchoApiMorphRange && m.name === 'Ancho' && morphHasValidRange(m)) {
      const active = clampMorphMm(layout.width, m.min, m.max)
      return {
        ...m,
        _default: active,
        default: active,
        min: m.min,
        max: m.max,
        show: true,
        modifyable: true,
      }
    }

    // MBJ/COL: conservar morphs API (aspecto clásico); scale cubre valores fuera de rango.
    if (preserveClassicMorphRanges && morphHasValidRange(m)) {
      const active = layoutByName[m.name as keyof typeof layoutByName] ?? m.default ?? 0
      return {
        ...m,
        _default: active,
        default: clampMorphMm(active, m.min, m.max),
        min: m.min,
        max: m.max,
        show: true,
        modifyable: true,
      }
    }

    const active = layoutByName[m.name as keyof typeof layoutByName] ?? m.default ?? 0
    const ref = glbRefByName[m.name as keyof typeof glbRefByName] ?? active
    const glbAltoIncludesZocaloSlab =
      m.name === 'Alto' &&
      altoOffsetMm === 0 &&
      catalogMm.y > 0 &&
      glbBaseMm.y > catalogMm.y + 1
    return {
      ...m,
      _default: active,
      // Laterales/columnas: bbox GLB incluye ~100 mm de zócalo; escala grupo = layout/glbY.
      default: glbAltoIncludesZocaloSlab ? active : ref,
      min: 0,
      max: 0,
      show: true,
      modifyable: true,
    }
  })
}

/**
 * Escala Z del grupo padre free-dim (mismo criterio que universal sin hasMorphs):
 * thickness / refGlbZ. No usar scaleBase.z si difiere del ref (fórmula panel 2×cat−bbox).
 */
export function getFreeDimensionFondoGroupScaleZ(
  item: {
    thickness?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    regletaMaximo?: number | null
  } | null | undefined,
  fondoMorph?: Morphs,
): number {
  if (!item) return 1
  const rinconeroZ = getRinconeroFondoGroupScaleZ(item)
  if (rinconeroZ != null) return rinconeroZ
  const m = fondoMorph ?? item.morphs?.find((x) => x.name === 'Fondo')
  const active = item.thickness ?? m?._default ?? 0
  // Priorizar ref de catálogo/GLB (morph.default pudo guardarse mal como valor activo en datos viejos).
  const ref =
    item.freeDimensionGlbBaseMm?.z ||
    item.freeDimensionCatalogMm?.z ||
    item.freeDimensionScaleBaseMm?.z ||
    (m?.default != null && m.default > 0 ? m.default : 0) ||
    0
  if (ref <= 0 || active <= 0) return 1
  return active / ref
}

export function itemUsesFreeDimensionAnchoGlbMorph(
  item?: {
    freeDimensionAnchoViaGlbMorph?: boolean
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    width?: number
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    hasMorphs?: boolean
  } | null,
): boolean {
  if (!item || !usesFreeDimensionScaling(item)) return false
  if (usesPureGroupFreeDimensionScale(item)) return false
  if (isFreeDimensionAnchoApiMorphCatalog(item)) return false
  // Frontal/encimera/zócalo TPC: todo por escala de grupo (nunca morph GLB).
  if (isFreeDimensionTpcNoMorphCatalog(item)) return false
  if (item.freeDimensionAnchoViaGlbMorph === true) return true
  if (item.freeDimensionAnchoViaGlbMorph === false) return false
  // RB/RC sin morphs de malla: escala grupo width/glb (también por debajo del catálogo).
  if (isRbFreeDimensionRegleta(item)) return false
  if (item.freeDimensionGlbBaseMm && item.freeDimensionCatalogMm) {
    return resolveFreeDimensionAnchoViaGlbMorph(
      item.freeDimensionGlbBaseMm,
      item.freeDimensionCatalogMm,
      item.width ?? 0,
    )
  }
  return false
}

export function itemUsesFreeDimensionFondoGlbMorph(
  item?: {
    freeDimensionFondoViaGlbMorph?: boolean
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    hasMorphs?: boolean
  } | null,
): boolean {
  if (!item || !usesFreeDimensionScaling(item)) return false
  if (usesPureGroupFreeDimensionScale(item)) return false
  if (isRbFreeDimensionRegleta(item)) return false
  // Frontal: glb.z a veces > catálogo → fondoViaMorph dejaba scaleZ=1 (Fondo “congelado”).
  if (isFreeDimensionTpcNoMorphCatalog(item)) return false
  if (item.freeDimensionFondoViaGlbMorph === true) return true
  if (item.freeDimensionFondoViaGlbMorph === false) return false
  if (item.freeDimensionGlbBaseMm && item.freeDimensionCatalogMm) {
    return resolveFreeDimensionFondoViaGlbMorph(
      item.freeDimensionGlbBaseMm,
      item.freeDimensionCatalogMm,
      item.catalogID ?? item.codigo,
    )
  }
  return false
}

export function itemUsesFreeDimensionAltoGlbMorph(
  _item?: {
    freeDimensionAltoViaGlbMorph?: boolean
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
  } | null,
): boolean {
  return false
}

/** Tope Y (mm) del cuerpo escalado = base para posicionar encimera (hijo fuera del grupo). */
export function getFreeDimensionVisualBodyTopYMm(
  item: {
    height?: number
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    independentZocaloHeightMm?: number
    hasMorphs?: boolean
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    morphs?: Morphs[]
    width?: number
    thickness?: number
  } | null | undefined,
): number {
  if (!usesFreeDimensionScaling(item)) return item?.height ?? 0

  // MBJ/COL + zócalo hijo: tope en coords del padre (hijos fuera del grupo lifted).
  // - Store = cuerpo → height + lift
  // - Store = mueble completo (catálogo) → height (no sumar lift otra vez)
  if (usesPureGroupFreeDimensionScale(item)) {
    const h = item?.height ?? 0
    const childLift = getIndependentZocaloChildYLiftMm(item)
    return h + childLift
  }

  const sidebarAlto =
    getFreeDimensionParentLayoutAltoMm(item) ??
    getFreeDimensionSidebarAlto(item?.height, item) ??
    item?.freeDimensionCatalogMm?.y ??
    0

  const glbY = item?.freeDimensionGlbBaseMm?.y ?? 0
  const altoMorph = item?.morphs?.find((m) => m.name === 'Alto')

  if (itemUsesFreeDimensionAltoGlbMorph(item)) {
    if (glbY > 0) {
      const ratios = getFreeDimensionScaleRatios(item)
      return Math.round(glbY * ratios.y)
    }
    const bodyAlto = altoMorph?._default ?? sidebarAlto
    const extraY = getFreeDimensionAltoGlbMorphScaleY(bodyAlto, altoMorph, item)
    return Math.round(bodyAlto * extraY)
  }

  const target =
    getFreeDimensionAltoScaleTargetMm(item) || sidebarAlto
  if (glbY > 0 && target > 0) {
    return Math.round(glbY * getFreeDimensionAltoGroupScaleY(item))
  }
  return target
}

/** Tope Y (mm) del mesh padre escalado; coords locales (hijo fuera del grupo scale). */
export function getFreeDimensionScaledMeshTopYMm(
  item: Parameters<typeof getFreeDimensionVisualBodyTopYMm>[0],
): number {
  return getFreeDimensionVisualBodyTopYMm(item)
}

/**
 * Tope Y (mm) donde debe apoyarse la encimera (coords locales del padre, fuera del grupo scale).
 * Mismo criterio que el tope visual del mesh escalado en el visor.
 */
export function getFreeDimensionEncimeraBodyTopYMm(
  parent: Parameters<typeof getFreeDimensionVisualBodyTopYMm>[0],
): number {
  if (!parent || !usesFreeDimensionScaling(parent)) {
    return parent?.height ?? 0
  }
  return getFreeDimensionVisualBodyTopYMm(parent)
}

/** Y local (mm) de la encimera respecto al origen del padre linked. */
export function getFreeDimensionEncimeraLocalYMm(
  parent: Parameters<typeof getFreeDimensionVisualBodyTopYMm>[0] & {
    hasIndependentZocalo?: boolean
    independentZocaloHeightMm?: number
    independentZocaloFloorOffsetApplied?: boolean
    y?: number
    id?: string
    catalogID?: string | number
  },
): number {
  if (!parent) return 0
  if (!usesFreeDimensionScaling(parent)) {
    return parent.height ?? 0
  }
  if (isRbFreeDimensionRegleta(parent)) {
    if (isRb805Catalog(parent)) {
      const lift = getIndependentZocaloChildYLiftMm(parent)
      if (lift > 0) return (parent.height ?? 0) + lift
    }
    const layout = getFreeDimensionParentLayoutAltoMm(parent) ?? 0
    const offset = getFreeDimensionAltoOffsetMm(parent)
    return layout > 0 ? layout + offset : parent.height ?? 0
  }
  return getFreeDimensionEncimeraBodyTopYMm(parent)
}

/**
 * Y mundo (mm) del tope de encimera / cara superior del mueble.
 * Los frontales se anclan aquí. Con zócalo independiente el alto de store es
 * solo el cuerpo (p.ej. 800) y el tope visual está ~100 mm más arriba.
 */
export function getFurnitureWorldWorktopYMm(
  item?: {
    y?: number
    height?: number
    matrix?: { elements?: ArrayLike<number> }
    independentZocaloFloorOffsetApplied?: boolean
    hasIndependentZocalo?: boolean
    independentZocaloHeightMm?: number
    independentZocaloSonId?: string | null
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasMorphs?: boolean
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    morphs?: Morphs[]
    width?: number
    thickness?: number
  } | null,
  encimeraThicknessMm = 0,
): number {
  if (!item) return Math.max(0, encimeraThicknessMm)

  let parentY = Number(item.y ?? 0)
  if (item.matrix?.elements && item.matrix.elements.length >= 14) {
    const matrixY = Number(item.matrix.elements[13])
    if (Number.isFinite(matrixY)) parentY = matrixY
  }

  const storeH = Number(item.height ?? 0)
  const floorOffsetApplied = Boolean(item.independentZocaloFloorOffsetApplied)

  let localBodyTop: number
  if (usesFreeDimensionScaling(item)) {
    localBodyTop = getFreeDimensionEncimeraLocalYMm(item)
    // Offset +100 antiguo: la matriz ya está elevada y localBodyTop también suma lift.
    if (floorOffsetApplied) localBodyTop = storeH
  } else {
    const lift = floorOffsetApplied ? 0 : getIndependentZocaloChildYLiftMm(item)
    localBodyTop = storeH + lift
  }

  return parentY + localBodyTop + Math.max(0, encimeraThicknessMm)
}

/** @deprecated Usar getFreeDimensionEncimeraBodyTopYMm o getFreeDimensionEncimeraLocalYMm */
export const getFreeDimensionEncimeraYInScaledGroupMm = getFreeDimensionEncimeraBodyTopYMm

export function isEncimeraLinkedElement(el: {
  descripcion?: string | null
} | null | undefined): boolean {
  return /encimera/i.test(el?.descripcion ?? '')
}

export type EncimeraConfiguratorItem = {
  idUnidades?: number
  catalogID?: string | number
  codigo?: string | number
  code?: string
  long?: string
  textures?: TexturesType
}

export function getEncimeraTextureEntry(
  item: EncimeraConfiguratorItem | null | undefined,
): TexturesType[number] | null {
  const textures = item?.textures
  if (!textures?.length) return null
  return (
    textures.find((t) => t.zone === 'Encimera' || t.zoneDisplayName === 'Encimera') ??
    textures.find((t) => /encimera/i.test(String(t.zoneDisplayName ?? t.zone ?? ''))) ??
    textures[0]
  )
}

/** idAcabadoUnidad de la unidad encimera en API (prioridad B80new). */
export function resolveEncimeraLinkedTextureIdFromZonas(
  zonas?: readonly { idAcabadoUnidad: number; zona: string }[],
): number | null {
  if (!zonas?.length) return null
  const b80 = zonas.find((z) => /b80new|b80\s*new/i.test(z.zona))
  if (b80?.idAcabadoUnidad && b80.idAcabadoUnidad > 0) return b80.idAcabadoUnidad
  const enc = zonas.find((z) => /encimera/i.test(z.zona))
  if (enc?.idAcabadoUnidad && enc.idAcabadoUnidad > 0) return enc.idAcabadoUnidad
  return null
}

export function patchEncimeraLinkedTextureIdOnItem(
  item: EncimeraConfiguratorItem | null | undefined,
  zonas?: readonly { idAcabadoUnidad: number; zona: string }[],
): number | null {
  const id = resolveEncimeraLinkedTextureIdFromZonas(zonas)
  if (!id) return null
  const tex = getEncimeraTextureEntry(item)
  if (tex) {
    try {
      tex.linkedTextureID = id
    } catch {
      // Items del store Zustand/Immer son inmutables fuera de set()
    }
  }
  return id
}

/** Normaliza textura de escena o API para comparar/seleccionar en el panel Encimera. */
export function toEncimeraPanelSelectedTexture(tex: {
  id?: number
  idTexturas?: number
  searchID?: number
  name?: string
  nombre?: string
  description?: string
  descripcion?: string
  url?: string
  acabado?: string
  groupID?: number
  idAcabadoUnidadModeloAcabado?: string
  linkedTextureID?: number
  modelo?: string
  [key: string]: unknown
}) {
  const idTexturas = Number(tex.idTexturas ?? tex.searchID ?? tex.id ?? 0)
  return {
    ...tex,
    id: tex.id ?? idTexturas,
    idTexturas,
    searchID: idTexturas,
    nombre: String(tex.nombre ?? tex.name ?? ''),
    name: String(tex.name ?? tex.nombre ?? ''),
    descripcion: String(tex.descripcion ?? tex.description ?? tex.nombre ?? tex.name ?? ''),
    zoneName: 'Encimera',
    zoneDisplayName: 'Encimera',
  }
}

/** Configurador de texturas: catálogo de la unidad encimera (idUnidades), no el del padre. */
export async function fetchEncimeraConfiguratorLinkedTextureId(
  item: EncimeraConfiguratorItem | null | undefined,
): Promise<number | null> {
  if (!item) return null
  if (item.idUnidades && item.idUnidades > 0) {
    try {
      const { API } = await import('@utils/queryBuilder')
      const resp = await API().api.acabadosGetUnidadAcabadosInfo(item.idUnidades)
      const zonas = (resp as { data?: { zonas?: { idAcabadoUnidad: number; zona: string }[] } })
        ?.data?.zonas
      const fromUnit = resolveEncimeraLinkedTextureIdFromZonas(zonas)
      if (fromUnit) {
        patchEncimeraLinkedTextureIdOnItem(item, zonas)
        return fromUnit
      }
    } catch (e) {
      // console.warn('[fetchEncimeraConfiguratorLinkedTextureId]', e)
    }
  }
  const tex = getEncimeraTextureEntry(item)
  const stored = tex?.linkedTextureID
  return stored != null && stored > 0 ? stored : null
}

/** Hijo linked en escena (Encimera, Encimera B80new, typeOfSon Encimera…). */
export function isEncimeraLinkedSonItem(
  item: {
    description?: string | null
    typeOfSon?: string | null
  } | null | undefined,
): boolean {
  if (!item) return false
  if (item.typeOfSon && /encimera/i.test(String(item.typeOfSon))) return true
  return /encimera/i.test(item.description ?? '')
}

export function matchesRbRegletaPreferredEncimera(el: {
  descripcion?: string | null
  unidad?: { codigo?: string | null; referencia?: string | null }
}): boolean {
  const label = String(
    el.unidad?.codigo ?? el.unidad?.referencia ?? el.descripcion ?? '',
  ).toLowerCase()
  return label.includes('b80new')
}

/** Deja una sola encimera: prioridad Encimera B80new si viene en elementosEnlazados. */
export function resolveRbRegletaDefaultLinkedChildren<
  T extends {
    descripcion?: string | null
    unidad?: { codigo?: string | null; referencia?: string | null }
  },
>(elementos: readonly T[]): T[] {
  if (!elementos.length) return []
  const preferred = elementos.find(
    (e) => isEncimeraLinkedElement(e) && matchesRbRegletaPreferredEncimera(e),
  )
  if (preferred) {
    return [...elementos.filter((e) => !isEncimeraLinkedElement(e)), preferred]
  }
  return [...elementos]
}

/**
 * Pivot local (mm) para escalar fondo desde la pared (espalda), no desde el centro del GLB.
 * Evita que el zócalo en L se abra en la esquina al reducir fondo.
 * Sandwich: position(pivot) → scale → position(-pivot).
 */
export function getFreeDimensionFondoScalePivotLocalMm(catalogFondoMm: number): number {
  if (catalogFondoMm <= 0) return 0
  return -catalogFondoMm / 2
}

/** @deprecated Usar getFreeDimensionFondoScalePivotLocalMm */
export const getRbFreeDimensionFondoScalePivotLocalMm = getFreeDimensionFondoScalePivotLocalMm

export function getFreeDimensionFondoScalePivotMFromItem(item: {
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
} | null | undefined): number {
  const catalogRefZ = item?.freeDimensionCatalogMm?.z ?? item?.freeDimensionGlbBaseMm?.z ?? 0
  return getFreeDimensionFondoScalePivotLocalMm(catalogRefZ) / 1000
}

/** Regletas RB/RC free-dim (sin morphs en API): encimera +100 store, ancho real ≠ cabecera ancho.
 *  Solo mira el código (no usesFreeDimensionScaling): evita ciclo con shouldUseIndependentZocaloUnit.
 */
export function isRbFreeDimensionRegleta(
  item?: {
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
  } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return code.startsWith('RB') || code.startsWith('RC')
}

/** Solo RB805: tira Zocalo 4000 + cuerpo 800. El resto de RB* no cambia. */
export function isRb805Catalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  let code = getLinkedItemCatalogCode(item)
  if (code.startsWith('PE ')) code = code.slice(3).trim()
  return code.replace(/[\s_-]+/g, '') === 'RB805'
}

export type FreeDimensionPlacementSource = {
  ancho?: number | null
  alto?: number | null
  descripcion?: string | null
  tipoNormalizado?: string | null
  morphs?: ReadonlyArray<{ nombre?: string; valor?: number }> | null
  elementosEnlazados?: ReadonlyArray<{
    descripcion?: string | null
    posicionY?: number | null
    unidad?: { morphs?: ReadonlyArray<{ nombre?: string; valor?: number }> | null }
  }> | null
}

function getFreeDimensionLinkedAnchoFromChildren(
  source?: FreeDimensionPlacementSource | null,
): number | null {
  if (!source?.elementosEnlazados?.length) return null
  for (const el of source.elementosEnlazados) {
    const valor = el.unidad?.morphs?.find((m) => m.nombre === 'Ancho')?.valor
    if (typeof valor === 'number' && valor > 0) return valor
  }
  return null
}

/**
 * Ancho de catálogo (mm) para escala: cabecera API (máx. modificable, p. ej. 100 en RB805).
 * Distinto del ancho activo (layout) que va en item.width.
 */
export function getFreeDimensionCatalogAnchoMm(
  backGLBInfo?: { ancho?: number | null } | null,
  layoutWidthMm?: number,
): number {
  const header = backGLBInfo?.ancho
  if (header != null && header > 0) return header
  return layoutWidthMm != null && layoutWidthMm > 0 ? layoutWidthMm : 0
}

/**
 * Ancho (mm) desde descripción tipo "Regleta Bajo 80x5" (solo si no hay cabecera `ancho` en API).
 * Primer número = alto en cm, segundo = sección en cm — no usar para ancho del padre si API trae `ancho`.
 */
export function parseFreeDimensionDescriptionAnchoMm(descripcion?: string | null): number | null {
  const desc = descripcion ?? ''
  if (!/regleta/i.test(desc)) return null
  const dimMatch = desc.match(/(\d+)\s*x\s*(\d+)/i)
  if (!dimMatch) return null
  const altoCm = Number(dimMatch[1])
  const anchoCm = Number(dimMatch[2])
  if (anchoCm > 0 && anchoCm <= 30) return anchoCm * 10
  if (altoCm > 0 && altoCm <= 30) return altoCm * 10
  return null
}

/**
 * Ancho al colocar unidades FREE_DIMENSION sin morphs en API.
 * RB805/RC…: cabecera `ancho` = lateral (100). Morph encimera hijo (50) no aplica al padre.
 */
export function resolveFreeDimensionPlacementAnchoMm(
  backGLBInfo?: FreeDimensionPlacementSource | null,
  glbBaseMm?: FreeDimensionGlbBaseMm | null,
  catalogID?: string | number,
): number {
  const headerAncho = backGLBInfo?.ancho ?? 0
  const code = catalogID != null ? String(catalogID).trim().toUpperCase() : ''

  if (
    headerAncho > 0 &&
    isFreeDimensionLinkedItem({ catalogID: code }) &&
    (code.startsWith('RB') || code.startsWith('RC'))
  ) {
    return headerAncho
  }

  const apiMorphAncho = backGLBInfo?.morphs?.find((m) => m.nombre === 'Ancho')?.valor
  if (typeof apiMorphAncho === 'number' && apiMorphAncho > 0) {
    return apiMorphAncho
  }

  const fromDesc = parseFreeDimensionDescriptionAnchoMm(backGLBInfo?.descripcion)
  if (fromDesc != null && fromDesc > 0) {
    if (headerAncho <= 0 || fromDesc <= headerAncho) return fromDesc
  }

  // Morph Ancho del hijo encimera (p. ej. 50): no usar como ancho del padre.
  const linkedAncho = getFreeDimensionLinkedAnchoFromChildren(backGLBInfo)
  if (
    linkedAncho != null &&
    linkedAncho > 0 &&
    !(code.startsWith('RB') || code.startsWith('RC'))
  ) {
    if (headerAncho <= 0 || linkedAncho <= headerAncho) return linkedAncho
  }

  const bboxX = glbBaseMm?.x ?? 0
  if (bboxX > 0) {
    if (headerAncho <= 0) return bboxX
    if (bboxX < headerAncho - 0.5) return bboxX
  }

  if (headerAncho > 0) return headerAncho
  return linkedAncho ?? fromDesc ?? 0
}

export type EncimeraCatalogMm = { x: number; y: number; z: number }

/** Tamaño catálogo API del GLB encimera (p. ej. B80new: 800×620), distinto del width/thickness en store. */
export function getEncimeraCatalogMm(
  encimera: {
    width?: number
    thickness?: number
    height?: number
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
  } | null | undefined,
): EncimeraCatalogMm | null {
  const stored = encimera?.encimeraCatalogMm
  if (stored && stored.x > 0 && stored.z > 0) return stored
  const gx = encimera?.glbSize?.x
  const gz = encimera?.glbSize?.z
  if (gx != null && gz != null && gx > 0 && gz > 0) {
    return {
      x: Math.round(gx * 1000),
      y: Math.round((encimera?.glbSize?.y ?? 0) * 1000),
      z: Math.round(gz * 1000),
    }
  }
  return null
}

/** Ancho catálogo de referencia del hijo encimera (API/GLB, p. ej. B80new 800 mm). */
export function getEncimeraAnchoCatalogBaselineMm(
  encimera: {
    width?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
  } | null | undefined,
): number {
  const fromApi = getEncimeraCatalogMm(encimera)?.x
  if (fromApi != null && fromApi > 0) return fromApi
  const anchoMorph = encimera?.morphs?.find((m) => m.name === 'Ancho')
  const fromRef = Number(anchoMorph?._default ?? 0)
  if (fromRef > 0) return fromRef
  return Number(encimera?.width ?? 0)
}

/** AABB del GLB en mm (pose rest, morph influence 0). */
function getEncimeraGlbAxisMm(
  encimera: { glbSize?: { x?: number; y?: number; z?: number } } | null | undefined,
  axis: 'x' | 'y' | 'z',
): number {
  const v = encimera?.glbSize?.[axis]
  return v != null && v > 0 ? Math.round(v * 1000) : 0
}

/**
 * Baseline para scale del grupo encimera (Ancho/Fondo).
 *
 * - Morph activo (min&lt;max, incl. sintéticos b30): catálogo — la malla ya va al default;
 *   no usar AABB inflado (400) o se encoge encima del morph (300×0.75).
 * - Sin morph: AABB GLB (crecer o encoger al objetivo).
 */
export function getEncimeraAxisScaleBaselineMm(
  encimera: {
    width?: number
    thickness?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
  } | null | undefined,
  axis: 'Ancho' | 'Fondo',
): number {
  const catalogMm =
    axis === 'Ancho'
      ? getEncimeraAnchoCatalogBaselineMm(encimera)
      : getEncimeraFondoCatalogBaselineMm(encimera)
  const morph = encimera?.morphs?.find((m) => m.name === axis)
  const glbMm = getEncimeraGlbAxisMm(encimera, axis === 'Ancho' ? 'x' : 'z')
  const targetMm =
    axis === 'Ancho'
      ? (encimera?.width ?? catalogMm)
      : (encimera?.thickness ?? catalogMm)

  if (morphHasValidRange(morph)) {
    return catalogMm > 0 ? catalogMm : 0
  }
  if (glbMm > 0 && targetMm > 0) return glbMm
  return catalogMm > 0 ? catalogMm : glbMm
}

/**
 * Encimera con API morphs=null: Ancho/Fondo sintéticos (min===max) dejan influence 0 y la
 * malla en rest (Encimera b30: 200×520). Abrir rango 0→catálogo aplica influence 1 (= tamaño API).
 * Alto se deja min===max (grosor rest / acabado); no abrir.
 */
export function activateSyntheticEncimeraFootprintMorphs<T extends Morphs>(morphs: T[]): T[] {
  return morphs.map((morph) => {
    if (morph.name !== 'Ancho' && morph.name !== 'Fondo') return morph
    if (morphHasValidRange(morph)) return morph
    const catalogMm = Number(morph.default ?? morph._default ?? 0)
    if (catalogMm <= 0) return morph
    return {
      ...morph,
      min: 0,
      max: catalogMm,
      default: catalogMm,
      _default: catalogMm,
    }
  })
}

/**
 * Losa de módulo en Ancho (B80new ~800) sobre lateral estrecho (RB ~100).
 * Distinto del voladizo de Fondo (620 vs 600): aquí el objetivo es el lateral del padre.
 */
export function encimeraAnchoIsModuleSlabOnParent(
  encCatalogBaselineMm: number,
  parentLayoutMm: number,
): boolean {
  return (
    encCatalogBaselineMm > 0 &&
    parentLayoutMm > 0 &&
    encCatalogBaselineMm > parentLayoutMm * 1.15
  )
}

/**
 * Dimensiona la encimera según el padre y el catálogo inicial.
 *
 * Fondo:
 * - enc &lt; padre: proporcional.
 * - enc ≈ padre (p. ej. 620 vs 600): delta fijo (+20 → padre 500 → enc 520).
 * - enc ≫ padre (p. ej. 620 vs 350 DECO): voladizo respecto a módulo 600
 *   (padre 350 → encimera 370).
 * Ancho:
 * - mismas reglas proporcional / delta cuando enc ≈ o &lt; padre.
 * - enc ≫ padre (B80new 800 sobre RB 100): ancho = lateral del padre.
 *   Nunca reutilizar la fórmula de voladizo de fondo (600) en Ancho
 *   (eso producía 100+(800−600)=300).
 */
export function computeEncimeraLinkedDimensionFromParent(
  parentLayoutMm: number,
  encCatalogBaselineMm: number,
  parentCatalogMm: number,
  axis: 'Ancho' | 'Fondo' = 'Fondo',
): number {
  if (parentLayoutMm <= 0) return 0
  if (encCatalogBaselineMm <= 0 || parentCatalogMm <= 0) return parentLayoutMm
  if (encCatalogBaselineMm > parentCatalogMm * 1.15) {
    if (axis === 'Ancho') {
      return parentLayoutMm
    }
    // Encimera de módulo estándar (b30 620) sobre mueble más estrecho (DECO 350).
    const standardModuleFondoMm = 600
    if (encCatalogBaselineMm >= standardModuleFondoMm) {
      return parentLayoutMm + (encCatalogBaselineMm - standardModuleFondoMm)
    }
    return parentLayoutMm
  }
  if (encCatalogBaselineMm < parentCatalogMm * 0.9) {
    return Math.round((parentLayoutMm * encCatalogBaselineMm) / parentCatalogMm)
  }
  return parentLayoutMm + (encCatalogBaselineMm - parentCatalogMm)
}

/** Free-dim con hijo encimera: ancho según catálogo (proporcional o delta), sin scaleX del grupo. */
export function syncFreeDimensionEncimeraAnchoToParent(
  encimera: { width?: number; morphs?: Morphs[] },
  parent: {
    width?: number
    thickness?: number
    morphs?: Morphs[]
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    tipoNormalizado?: string
    description?: string
    referencia?: string
    claveMenu?: string | null
    menuPrincipal?: string | null
  },
): void {
  if (!usesFreeDimensionScaling(parent)) return
  if (freeDimensionUsesLb8060StyleAltoOffset(parent)) {
    syncLb8060EncimeraToParent(encimera, parent)
    return
  }
  const parentW = parent.width ?? 0
  if (parentW <= 0) return
  const anchoMorph = encimera.morphs?.find((m) => m.name === 'Ancho')
  const encBaseline = getEncimeraAnchoCatalogBaselineMm(encimera)
  const isModuleSlabAncho = encimeraAnchoIsModuleSlabOnParent(encBaseline, parentW)

  // RB + Encimera B80new (losa ~800 o sin morph Ancho real): ancho = lateral del padre.
  // activateSyntheticEncimeraFootprintMorphs abre Ancho 0→800 y hacía fallar el gate
  // "sin morph"; no usar fórmula de voladizo de fondo ni dejar width=300.
  if (isRbFreeDimensionRegleta(parent) && (isModuleSlabAncho || !encimeraUsesAnchoMorphTarget(encimera))) {
    encimera.width = parentW
    if (anchoMorph) {
      // Losa módulo: morph@max + scaleX = parent/max (mismo patrón que Fondo).
      // Sin morph / tira estrecha: morph.default = lateral.
      anchoMorph.default =
        isModuleSlabAncho && morphHasValidRange(anchoMorph)
          ? Number(anchoMorph.max)
          : parentW
    }
    return
  }

  const parentCatalog =
    parent.freeDimensionCatalogMm?.x ??
    Number(parent.morphs?.find((m) => m.name === 'Ancho')?.default ?? parentW)

  let encW: number
  if (encBaseline > 0 && parentCatalog > 0) {
    encW = computeEncimeraLinkedDimensionFromParent(
      parentW,
      encBaseline,
      parentCatalog,
      'Ancho',
    )
  } else {
    encW = parentW
    if (anchoMorph && !usesPureGroupFreeDimensionScale(parent) && !isApplianceMenuUnit(parent)) {
      const morphMax = Number(anchoMorph.max ?? 0)
      const morphMin = Number(anchoMorph.min ?? 0)
      if (morphMax > morphMin && morphMax > 0) {
        encW = Math.min(parentW, morphMax)
      }
    }
  }

  // RB/regletas: no superar morph.max (encimera puede ser 50 mm con padre 100).
  // MBJ/COL y electrodomésticos: width completo; morph clamped + scale X cubre overflow.
  if (
    anchoMorph &&
    morphHasValidRange(anchoMorph) &&
    !usesPureGroupFreeDimensionScale(parent) &&
    !isApplianceMenuUnit(parent)
  ) {
    encW = clampMorphMm(encW, anchoMorph.min, anchoMorph.max)
  }

  if (encW <= 0) return
  encimera.width = encW
  if (anchoMorph) {
    // RB tira estrecha (morph real ~50): morph.default = ancho real (scaleX=1).
    // Losa módulo / MBJ/COL/DECO: morph a max + scaleX = width/max.
    if (
      morphHasValidRange(anchoMorph) &&
      (!isRbFreeDimensionRegleta(parent) || isModuleSlabAncho)
    ) {
      anchoMorph.default = Number(anchoMorph.max)
    } else {
      anchoMorph.default = encW
    }
  }
}

/** Encimera con morph Ancho en el GLB. */
export function encimeraUsesAnchoMorphTarget(
  encimera: { morphs?: Morphs[] } | null | undefined,
): boolean {
  const morph = encimera?.morphs?.find((m) => m.name === 'Ancho')
  if (!morph) return false
  const max = Number(morph.max ?? 0)
  const min = Number(morph.min ?? 0)
  return max > min
}

/**
 * Escala X del grupo encimera.
 * RB + morph Ancho estrecho (B10 ~50): 1 (no estirar 50→100).
 * RB + losa módulo (B80new 800≫lateral): morph@max + scaleX = target/max (como Fondo).
 * MBJ/COL / sin morph: morph hasta max + scale si el padre crece más.
 */
export function getFreeDimensionEncimeraAnchoScaleX(
  encimera: {
    width?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
  },
  parent?: {
    width?: number
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
  } | null,
): number {
  const targetW = encimera.width ?? parent?.width ?? 0
  const base = getEncimeraAxisScaleBaselineMm(encimera, 'Ancho')
  const morph = encimera.morphs?.find((m) => m.name === 'Ancho')
  if (isRbFreeDimensionRegleta(parent) && encimeraUsesAnchoMorphTarget(encimera)) {
    const catalog = getEncimeraAnchoCatalogBaselineMm(encimera)
    const parentW = parent?.width ?? 0
    // Losa B80new sobre lateral RB: hay que encoger el grupo (100/800).
    if (encimeraAnchoIsModuleSlabOnParent(catalog, parentW)) {
      return getEncimeraFreeDimensionAxisScale(targetW, morph, base)
    }
    return 1
  }
  return getEncimeraFreeDimensionAxisScale(targetW, morph, base)
}

/** Encimera con morph target Fondo en el GLB (deformar mesh, no scale Z del grupo). */
export function encimeraUsesFondoMorphTarget(
  encimera: { morphs?: Morphs[] } | null | undefined,
): boolean {
  const morph = encimera?.morphs?.find((m) => m.name === 'Fondo')
  if (!morph) return false
  const max = Number(morph.max ?? 0)
  const min = Number(morph.min ?? 0)
  return max > min
}

/** Fondo catálogo de referencia del hijo encimera (p. ej. B80new 620 mm). */
export function getEncimeraFondoCatalogBaselineMm(
  encimera: {
    thickness?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
  } | null | undefined,
): number {
  const fromApi = getEncimeraCatalogMm(encimera)?.z
  if (fromApi != null && fromApi > 0) return fromApi
  const fondoMorph = encimera?.morphs?.find((m) => m.name === 'Fondo')
  const fromMax = Number(fondoMorph?.max ?? 0)
  if (fromMax > 0) return fromMax
  const fromRef = Number(fondoMorph?._default ?? 0)
  if (fromRef > 0) return fromRef
  return Number(encimera?.thickness ?? 0)
}

/**
 * Catálogo de fondo del padre para sync de encimera (valor ESTABLE, no el layout actual).
 * Rinconero: plano puerta del catálogo (600), no AABB/carcasa (720/820) ni thickness
 * actual. Si parentCatalog === thickness, al cambiar Fondo la encimera no crece
 * (p.ej. 700 y 700 → target sigue en 620).
 */
function getParentFondoCatalogForEncimeraSync(parent: {
  catalogID?: string | number
  thickness?: number
  morphs?: Morphs[]
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  regletaMaximo?: number | null
}): number {
  if (isRinconeroCatalog(parent.catalogID)) {
    const catalogZ = Number(parent.freeDimensionCatalogMm?.z ?? 0)
    const overhang = Number(parent.regletaMaximo ?? 0)
    // Catálogo a veces es carcasa (720) = puerta + regletaMaximo.
    if (catalogZ >= 650 && overhang > 0 && catalogZ - overhang >= 400) {
      return catalogZ - overhang
    }
    if (catalogZ > 0) return catalogZ
    const morph = parent.morphs?.find((m) => m.name === 'Fondo')
    const morphMin = Number(morph?.min ?? 0)
    const morphMax = Number(morph?.max ?? 0)
    if (morphMin > 0 && morphMax > morphMin) return morphMin
    return 600
  }
  return (
    parent.freeDimensionCatalogMm?.z ??
    (Number(parent.morphs?.find((m) => m.name === 'Fondo')?.min ?? 0) ||
      Number(parent.morphs?.find((m) => m.name === 'Fondo')?.default ?? parent.thickness ?? 0))
  )
}

/**
 * Voladizo frontal de encimera MBJ (Encimera b30: 620 − 600 = 20 mm).
 * Contrato único: visual fondo = thickness padre + este valor.
 * Encimera L (BR40100 AABB 720 = 600 puerta + 120 retorno): el 120 es regleta,
 * no voladizo; el voladizo sigue siendo 20 mm como el KBB.
 */
export const MBJ_ENCIMERA_FONDO_OVERHANG_MM = 20

type EncimeraFondoParentRef = {
  thickness?: number
  morphs?: Morphs[]
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
  catalogID?: string | number
  codigo?: string | number
  hasMorphs?: boolean
  regletaMaximo?: number | null
}

function getEncimeraMbjFondoOverhangMm(
  encimera: {
    thickness?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
    regletaMaximo?: number | null
  } | null | undefined,
  parent: EncimeraFondoParentRef | null | undefined,
): number {
  if (!encimera || !parent) return 0
  const encZ = getEncimeraFondoCatalogBaselineMm(encimera)
  const parentZ = getParentFondoCatalogForEncimeraSync(parent)
  if (!(encZ > 0) || !(parentZ > 0)) return 0
  const delta = encZ - parentZ
  if (delta >= 0 && delta <= 40) return delta
  const returnMm = Number(encimera.regletaMaximo ?? parent.regletaMaximo ?? 0)
  if (returnMm > 0 && Math.abs(encZ - parentZ - returnMm) <= 5) {
    return MBJ_ENCIMERA_FONDO_OVERHANG_MM
  }
  if (encZ > parentZ * 1.15) return MBJ_ENCIMERA_FONDO_OVERHANG_MM
  return Math.max(0, delta)
}

/** Fondo store de encimera MBJ: mismo que KBB (padre + voladizo). */
export function getEncimeraMbjFondoTargetMm(
  encimera: Parameters<typeof getEncimeraMbjFondoOverhangMm>[0],
  parent: EncimeraFondoParentRef | null | undefined,
): number {
  const parentFondo = Number(parent?.thickness ?? parent?.freeDimensionCatalogMm?.z ?? 0)
  if (!(parentFondo > 0)) return 0
  return parentFondo + getEncimeraMbjFondoOverhangMm(encimera, parent)
}

/**
 * scaleZ encimera MBJ: (fondoPadre + voladizo) / (catálogoPadre + voladizo).
 * KBB 1000 → 1020/620. KBR usa la misma fracción; no thickness / AABB 720.
 */
export function getEncimeraMbjFondoScaleZ(
  encimera: Parameters<typeof getEncimeraMbjFondoOverhangMm>[0],
  parent: EncimeraFondoParentRef | null | undefined,
): number {
  const parentZ = parent ? getParentFondoCatalogForEncimeraSync(parent) : 0
  const nosing = getEncimeraMbjFondoOverhangMm(encimera, parent)
  const denom = parentZ + nosing
  const numer = getEncimeraMbjFondoTargetMm(encimera, parent)
  if (!(denom > 0) || !(numer > 0)) return 1
  return numer / denom
}

/** Delta fijo encimera vs lateral (p. ej. 620 − 600 = 20 mm). */
export function getEncimeraFondoDeltaMm(
  encimera: { thickness?: number; morphs?: Morphs[] },
  parent: {
    thickness?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    regletaMaximo?: number | null
  },
): number {
  return getEncimeraMbjFondoOverhangMm(encimera, parent)
}

/**
 * Escala de grupo en un eje de encimera.
 *
 * Con morph usable: la malla a influence 1 (= morph.max) tiene tamaño ≈ max mm
 * (Encimera b30: Fondo rest 520 → morph 620). Scale = target / max cubre:
 * - DECO 370/620 (padre 350 + voladizo 20),
 * - overflow padre &gt; max,
 * - target === max → 1.
 * (No usar target/clamp(target): morph intermedio ≠ mm intermedios con min sintético 0.)
 *
 * Sin morph → target / baseline (AABB rest).
 */
export function getEncimeraFreeDimensionAxisScale(
  targetMm: number,
  morph: Morphs | undefined,
  baselineMm: number,
): number {
  if (targetMm <= 0) return 1
  if (morphHasValidRange(morph)) {
    const morphMax = Number(morph!.max ?? 0)
    if (morphMax > 0) return targetMm / morphMax
    return 1
  }
  if (baselineMm > 0) return targetMm / baselineMm
  return 1
}

/**
 * Fondo encimera = fondo padre + delta catálogo (p. ej. padre 600 → 620; padre 500 → 520).
 * thickness = valor completo; morph.default = clamp al rango GLB (scale cubre el resto).
 */
export function syncFreeDimensionEncimeraFondoToParent(
  encimera: {
    thickness?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
    regletaMaximo?: number | null
    z?: number
    matrix?: { elements?: number[] }
  },
  parent: {
    thickness?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    hasMorphs?: boolean
    regletaMaximo?: number | null
  },
): void {
  if (!usesFreeDimensionScaling(parent) && !freeDimensionUsesLb8060StyleAltoOffset(parent)) {
    return
  }
  const parentFondo = parent.thickness ?? parent.freeDimensionCatalogMm?.z ?? 0
  if (parentFondo <= 0) {
    applyLb8060EncimeraCatalogFondo(encimera)
    return
  }
  const parentCatalog = getParentFondoCatalogForEncimeraSync(parent)
  const encBaseline = getEncimeraFondoCatalogBaselineMm(encimera)
  const target = getEncimeraMbjFondoTargetMm(encimera, parent)
  if (target <= 0) return
  encimera.thickness = target
  const fondoMorph = encimera.morphs?.find((m) => m.name === 'Fondo')
  if (fondoMorph) {
    // Losa en rest (influence 1). El crecimiento MBJ es scaleZ, no bajar el morph max.
    fondoMorph.default = morphHasValidRange(fondoMorph) ? Number(fondoMorph.max) : target
  }
  if (isRbFreeDimensionRegleta(parent)) {
    encimera.z = 0
    syncLinkedSonLocalMatrixTranslation(encimera)
  }
  logFreeDimensionEncimeraFondo('syncFondoToParent', encimera, parent, {
    targetThickness: target,
    parentCatalogZ: parentCatalog,
    encBaseline,
    morphDefault: fondoMorph?.default,
    scaleZ: getEncimeraMbjFondoScaleZ(encimera, parent),
  })
}

/** Z local (mm) del hijo encimera: valor de store/API (p. ej. 0), sin corrección por delta de fondo. */
export function getFreeDimensionEncimeraLocalZMm(
  encimera: { z?: number } | null | undefined,
  _parent?: unknown,
): number {
  return encimera?.z ?? 0
}

/** Escala Z encimera: mismo ratio MBJ que el KBB (padre+20)/(catálogo+20). */
export function getFreeDimensionEncimeraFondoScaleZ(
  encimera: {
    thickness?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
    regletaMaximo?: number | null
  } | null | undefined,
  parent?: EncimeraFondoParentRef | null,
): number {
  if (!encimera) return 1
  if (parent && Number(parent.thickness ?? 0) > 0) {
    return getEncimeraMbjFondoScaleZ(encimera, parent)
  }
  const target = Number(encimera.thickness ?? 0)
  const baseline = getEncimeraAxisScaleBaselineMm(encimera, 'Fondo')
  const fondoMorph = encimera.morphs?.find((m) => m.name === 'Fondo')
  return getEncimeraFreeDimensionAxisScale(target, fondoMorph, baseline)
}

/**
 * Escala Y del grupo encimera respecto al compacto (10 mm = scale 1).
 * Porcelánicas 12 → 1.2; 20 → 2; Postformadas → 3.
 * Independiente del padre: también lavavajillas y kits clásicos sin free-dim.
 * No usar morph Alto para el grosor: el API trae rango 10–30 pero el GLB
 * a menudo no tiene target, y si lo tiene se duplicaba con la escala.
 */
export function getFreeDimensionEncimeraAltoScaleY(
  encimera: {
    height?: number
    morphs?: Morphs[]
    catalogID?: string | number
    codigo?: string | number
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
    textures?: { acabado?: string | null; zone?: string | null; zoneDisplayName?: string | null }[]
  } | null | undefined,
): number {
  if (!encimera) return 1
  const fromAcabado = getEncimeraAcabadoThicknessMm(getEncimeraTextureEntry(encimera)?.acabado)
  const fromHeight = Number(encimera.height ?? 0)
  const target =
    fromAcabado ??
    (fromHeight >= 8 && fromHeight <= 40 ? fromHeight : 0)
  if (!(target > 0)) return 1
  return target / ENCIMERA_API_BASELINE_THICKNESS_MM
}

/** Grosor visual (mm) de la losa. Los 4 tipos (10/12/20/30) mandan sobre el AABB nativo. */
export function getEncimeraVisualThicknessMm(encimera?: EncimeraVisualRef | null): number {
  if (!encimera) return ENCIMERA_API_BASELINE_THICKNESS_MM
  const typeT = getEncimeraTypeThicknessMm(encimera)
  if (typeT != null) return typeT
  const scaleY = getFreeDimensionEncimeraAltoScaleY(encimera)
  const nativeMm =
    (Number(encimera.glbSize?.y) > 0 ? Math.round(Number(encimera.glbSize.y) * 1000) : 0) ||
    Number(encimera.encimeraCatalogMm?.y) ||
    0
  const heightMm = Number(encimera.height) || 0
  const visual = Math.max(nativeMm * scaleY, heightMm, ENCIMERA_API_BASELINE_THICKNESS_MM)
  return Math.round(visual)
}

/**
 * Offset desde el origen de la encimera hasta el tope de piedra (mm).
 * Compacto/porcelánica/postformada: el grosor de acabado, no el AABB del GLB en bind pose.
 * Sin tipo conocido (losa sólida): AABB × scale, para no hundir la placa en B90.
 */
export function getEncimeraSitTopOffsetMm(
  encimera?: EncimeraVisualRef | null,
  aabb?: { maxY?: number } | null,
): number {
  if (!encimera) return ENCIMERA_API_BASELINE_THICKNESS_MM
  const typeT = getEncimeraTypeThicknessMm(encimera)
  if (typeT != null) return typeT
  const scaleY = getFreeDimensionEncimeraAltoScaleY(encimera)
  const heightMm = Number(encimera.height) || 0
  const maxY = Number(aabb?.maxY)
  if (Number.isFinite(maxY) && maxY > 0) {
    return Math.round(Math.max(maxY * scaleY, heightMm, ENCIMERA_API_BASELINE_THICKNESS_MM))
  }
  return getEncimeraVisualThicknessMm(encimera)
}

/** @deprecated Usar syncFreeDimensionEncimeraAnchoToParent */
export const syncRbRegletaEncimeraAnchoToParent = syncFreeDimensionEncimeraAnchoToParent

/** Unidades con offset de alto (+100 RB/RC, CC/RA): escala por layout, no solo store/scaleBase. */
export function usesFreeDimensionAltoOffsetLayoutScale(
  item?: { catalogID?: string | number; codigo?: string | number; hasMorphs?: boolean } | null,
): boolean {
  return usesFreeDimensionScaling(item) && getFreeDimensionAltoOffsetMm(item) !== 0
}

/**
 * Escala Y para que el tope del cuerpo (sidebar) coincida con catálogo al colocar y al redimensionar.
 * layout×(cat+offset)/(scaleBase×cat). Aplica a LA, CC, LB8060, etc.
 */
export function getFreeDimensionLayoutAltoScaleY(
  layoutMm: number,
  catalogY: number,
  scaleBaseY: number,
  offsetMm: number,
): number {
  if (layoutMm <= 0 || scaleBaseY <= 0 || catalogY <= 0) return 1
  return (layoutMm * (catalogY + offsetMm)) / (scaleBaseY * catalogY)
}

/** @deprecated Usar getFreeDimensionAltoGroupScaleY */
export function getRbFreeDimensionAltoScaleY(
  item: Parameters<typeof getFreeDimensionAltoGroupScaleY>[0],
): number {
  return getFreeDimensionAltoGroupScaleY(item)
}

/** @deprecated Usar getFreeDimensionLayoutAltoScaleY */
export function getFreeDimensionLb8060LayoutScaleY(
  layoutMm: number,
  catalogY: number,
  scaleBaseY: number,
  offsetMm = 0,
): number {
  return getFreeDimensionLayoutAltoScaleY(layoutMm, catalogY, scaleBaseY, offsetMm)
}

const FREE_DIMENSION_DRAW_DEBUG_LA =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_FREE_DIM_DEBUG_LA === 'true'

/** Activar solo con VITE_FREE_DIM_DEBUG_ANCHO=true en .env.local */
const FREE_DIMENSION_DRAW_DEBUG_ANCHO =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_FREE_DIM_DEBUG_ANCHO === 'true'

/** Activar solo con VITE_FREE_DIM_DEBUG_FONDO=true en .env.local */
const FREE_DIMENSION_DRAW_DEBUG_FONDO =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_FREE_DIM_DEBUG_FONDO === 'true'

/** Activar solo con VITE_FREE_DIM_DEBUG_MATERIAL=true en .env.local */
const FREE_DIMENSION_DRAW_DEBUG_MATERIAL =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_FREE_DIM_DEBUG_MATERIAL === 'true'

/** Activar con VITE_FREE_DIM_DEBUG_ZOCALO=true */
const FREE_DIMENSION_DRAW_DEBUG_ZOCALO =
  typeof import.meta !== 'undefined' &&
  (import.meta.env?.VITE_FREE_DIM_DEBUG_ZOCALO === 'true' ||
    import.meta.env?.VITE_FREE_DIM_DEBUG_FONDO === 'true')

function isFreeDimensionZocaloMeshDebugEnabled(): boolean {
  return FREE_DIMENSION_DRAW_DEBUG_ZOCALO
}

/**
 * Mapeo dimensiones (igual que MorphDictionary / universales):
 * - Ancho  → item.width
 * - Alto   → item.height (en FREE_DIMENSION con offset: sidebar = height − offset)
 * - Fondo  → item.thickness
 */
export const FREE_DIMENSION_FIELD_BY_MORPH = {
  Ancho: 'width',
  Alto: 'height',
  Fondo: 'thickness',
} as const

/** Log colocación Ancho/Alto/Fondo (DEV). Filtrar: [FreeDim place] */
export function logFreeDimensionPlacement(
  phase: string,
  catalogID: string | number | undefined,
  data: {
    morphs: Morphs[]
    width: number
    heightStore: number
    thickness: number
    catalogMm?: FreeDimensionCatalogMm
    glbBaseMm?: FreeDimensionGlbBaseMm
    altoOffsetMm?: number
  },
): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_ANCHO && !FREE_DIMENSION_DRAW_DEBUG_FONDO) return
  const code = getLinkedItemCatalogCode({ catalogID })
  if (!code || !isFreeDimensionLinkedItem({ catalogID: code })) return
  const pick = (name: string) => data.morphs.find((m) => m.name === name)
  const ancho = pick('Ancho')
  const alto = pick('Alto')
  const fondo = pick('Fondo')
  const sidebarAlto =
    data.altoOffsetMm != null ? data.heightStore - data.altoOffsetMm : data.heightStore
  const anchoViaMorph =
    data.glbBaseMm && data.catalogMm
      ? resolveFreeDimensionAnchoViaGlbMorph(data.glbBaseMm, data.catalogMm, data.width)
      : false
  console.log('[FreeDim place]', phase, code, {
    anchoViaGlbMorph: anchoViaMorph,
    ancho: {
      sidebarMm: data.width,
      itemField: `width=${data.width}`,
      morph: ancho ? { refGlbMm: ancho.default, activeMm: ancho._default, scale: (ancho._default ?? 0) / (ancho.default || 1) } : null,
    },
    alto: {
      sidebarMm: sidebarAlto,
      storeHeightMm: data.heightStore,
      itemField: `height=${data.heightStore}`,
      altoOffsetMm: data.altoOffsetMm ?? 0,
      morph: alto ? { refGlbMm: alto.default, activeMm: alto._default, scale: (alto._default ?? 0) / (alto.default || 1) } : null,
    },
    fondo: {
      sidebarMm: data.thickness,
      itemField: `thickness=${data.thickness}`,
      morph: fondo ? { refGlbMm: fondo.default, activeMm: fondo._default, scale: (fondo._default ?? 0) / (fondo.default || 1) } : null,
    },
    catalogMm: data.catalogMm,
    glbBaseMm: data.glbBaseMm,
  })
}

/**
 * Material zona Puerta al colocar / heredar texturas (DEV).
 * Filtrar consola: `[FreeDim material Puerta]`
 */
export function logFreeDimensionPuertaMaterial(item: {
  id?: string
  catalogID?: string | number
  type?: string
}): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_MATERIAL || !item.id || item.type !== 'linkedItem') return
  void import('@utils/debug/compareLinkedItemMaterials').then((mod) => {
    mod.logPuertaMaterial(item.id!)
  })
}

/**
 * Material zona Zócalo al colocar / heredar texturas (DEV).
 * Filtrar consola: `[FreeDim material Zocalo]`
 * Activar: VITE_FREE_DIM_DEBUG_MATERIAL=true en .env.local
 */
export function logFreeDimensionZocaloMaterial(item: {
  id?: string
  catalogID?: string | number
  type?: string
}): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_MATERIAL || !item.id || item.type !== 'linkedItem') return
  void import('@utils/debug/compareLinkedItemMaterials').then((mod) => {
    mod.logZocaloMaterial(item.id!)
  })
}

export function logFreeDimensionZocaloMaterialRuntime(
  itemId: string,
  runtime: import('@utils/debug/compareLinkedItemMaterials').ZocaloRenderRuntimeContext,
): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_MATERIAL && !isFreeDimensionZocaloMeshDebugEnabled()) return
  void import('@utils/debug/compareLinkedItemMaterials').then((mod) => {
    mod.logZocaloMaterial(itemId, runtime)
  })
}

export type FreeDimensionZocaloMeshLogEntry = {
  meshName: string
  zoneKey: string | null
  localPositionMm: { x: number; y: number; z: number }
  localScale: { x: number; y: number; z: number }
  localBoxMm: { minX: number; minY: number; minZ: number; maxX: number; maxY: number; maxZ: number }
  sizeMm: { x: number; y: number; z: number }
}

/**
 * Posición/escala/bbox de cada mesh Zócalo en el GLB clonado (antes del grupo scale del visor).
 * Filtrar consola: `[FreeDim zocalo mesh]`
 * Activar: DEV automático, o VITE_FREE_DIM_DEBUG_ZOCALO=true en .env.local
 */
export function logFreeDimensionZocaloMeshPositions(
  phase: string,
  itemId: string,
  root: Object3D,
  item: {
    catalogID?: string | number
    width?: number
    thickness?: number
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
  },
  groupScale?: { x: number; y: number; z: number },
  zones?: Zone,
  textures?: Textures,
): void {
  if (!isFreeDimensionZocaloMeshDebugEnabled()) return
  const code = getLinkedItemCatalogCode(item)
  const entries: FreeDimensionZocaloMeshLogEntry[] = []

  root.updateMatrixWorld(true)
  root.traverse((obj) => {
    if (!(obj as Mesh).isMesh) return
    const mesh = obj as Mesh
    const zoneKey =
      zones && mesh.name in zones
        ? String(mesh.name)
        : Object.keys(zones ?? {}).find((k) => zones![k].name === mesh.name) ?? null
    const zone = zoneKey && zones ? zones[zoneKey as keyof Zone] : undefined
    const tex = textures?.find((t) => t.zoneName === mesh.name)
    if (!isZocaloZone(zone, tex as never) && !/zocalo/i.test(mesh.name)) return

    const local = mesh.position
    const scl = mesh.scale
    const box = new Box3().setFromObject(mesh)
    const size = box.getSize(new Vector3())
    const toMm = (v: number) => Math.round(v * 1000)

    entries.push({
      meshName: mesh.name,
      zoneKey,
      localPositionMm: { x: toMm(local.x), y: toMm(local.y), z: toMm(local.z) },
      localScale: {
        x: Math.round(scl.x * 1000) / 1000,
        y: Math.round(scl.y * 1000) / 1000,
        z: Math.round(scl.z * 1000) / 1000,
      },
      localBoxMm: {
        minX: toMm(box.min.x),
        minY: toMm(box.min.y),
        minZ: toMm(box.min.z),
        maxX: toMm(box.max.x),
        maxY: toMm(box.max.y),
        maxZ: toMm(box.max.z),
      },
      sizeMm: { x: toMm(size.x), y: toMm(size.y), z: toMm(size.z) },
    })
  })

  // console.warn('[FreeDim zocalo mesh]', phase, code, itemId, {
    // hint: 'Filtra consola por [FreeDim zocalo mesh]. Forzar: VITE_FREE_DIM_DEBUG_ZOCALO=true',
    // itemLayout: {
      // width: item.width,
      // thickness: item.thickness,
      // catalogMm: item.freeDimensionCatalogMm,
      // glbBaseMm: item.freeDimensionGlbBaseMm,
      // scaleBaseMm: item.freeDimensionScaleBaseMm,
    // },
    // groupScaleVector: groupScale,
    // scaleZ: groupScale?.z,
    // fondoGroupScaleZ: getFreeDimensionFondoGroupScaleZ(item),
    // meshCount: entries.length,
    // meshes: entries,
  // })
}

/** Log ancho/escala free-dim (DEV). Filtrar: [FreeDim ancho] */
export function logFreeDimensionAncho(
  phase: string,
  item: {
    catalogID?: string | number
    width?: number
    height?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
    hasMorphs?: boolean
  } | null | undefined,
  extra?: Record<string, unknown>,
): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_ANCHO || !item) return
  const code = getLinkedItemCatalogCode(item)
  if (!usesFreeDimensionScaling(item)) return
  const anchoMorph = item.morphs?.find((m) => m.name === 'Ancho')
  const morphViaGlb = itemUsesFreeDimensionAnchoGlbMorph(item)
  const itemW = item.width ?? 0
  const morphRefX = anchoMorph?.default ?? 0
  const morphActiveX = anchoMorph?._default ?? itemW
  const morphMaxMm = getFreeDimensionAnchoMorphMaxMm(anchoMorph, item.freeDimensionCatalogMm)
  const extraScaleX = morphViaGlb
    ? getFreeDimensionAnchoGlbMorphScaleX(itemW, anchoMorph, item.freeDimensionCatalogMm)
    : 1
  const scaleRatioX = morphViaGlb
    ? extraScaleX
    : morphRefX > 0
      ? morphActiveX / morphRefX
      : 1
  const glbBaseX = item.freeDimensionGlbBaseMm?.x ?? 0
  const visualAnchoEstMm = morphViaGlb
    ? morphMaxMm * extraScaleX
    : glbBaseX > 0
      ? glbBaseX * scaleRatioX
      : morphActiveX
  console.log('[FreeDim ancho]', phase, code, {
    itemWidth: itemW,
    layoutWidthMm: extra?.layoutWidthMm ?? morphActiveX,
    catalogAnchoMm: item.freeDimensionCatalogMm?.x,
    glbBaseX,
    scaleBaseX: item.freeDimensionScaleBaseMm?.x,
    morphAnchoMaxMm: morphMaxMm,
    morphAnchoForMeshMm: morphRefX,
    morphAnchoTargetMm: morphActiveX,
    extraScaleX,
    scaleRatioX,
    visualAnchoEstMm: Math.round(visualAnchoEstMm),
    widthVia: morphViaGlb
      ? 'morph GLB hasta max + scaleX si ancho > max'
      : 'universal (_default activo / default ref GLB)',
    ...extra,
  })
}

/** Log fondo/escala Z (DEV). Filtrar consola: `[FreeDim fondo]` — activar VITE_FREE_DIM_DEBUG_FONDO=true */
export function logFreeDimensionFondo(
  phase: string,
  item: {
    catalogID?: string | number
    codigo?: string | number
    thickness?: number
    morphs?: Morphs[]
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
    hasMorphs?: boolean
  } | null | undefined,
  extra?: Record<string, unknown>,
): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_FONDO || !item) return
  const code = getLinkedItemCatalogCode(item)
  const fondoMorph = item.morphs?.find((m) => m.name === 'Fondo')
  const active = item.thickness ?? fondoMorph?._default ?? 0
  const morphRef = fondoMorph?.default ?? 0
  const glbZ = item.freeDimensionGlbBaseMm?.z ?? 0
  const catalogZ = item.freeDimensionCatalogMm?.z ?? 0
  const scaleBaseZ = item.freeDimensionScaleBaseMm?.z ?? 0
  const universalScaleZ = getFreeDimensionFondoGroupScaleZ(item, fondoMorph)
  const ratioFromScaleBase =
    scaleBaseZ > 0 && active > 0 ? active / scaleBaseZ : 1
  const fondoViaMorph = itemUsesFreeDimensionFondoGlbMorph(item)
  const isRb = isRbFreeDimensionRegleta(item)
  const isUniversalMesh = Boolean(item.hasMorphs && !usesFreeDimensionScaling(item))
  console.log('[FreeDim fondo]', phase, code, {
    isRbRegleta: isRb,
    hasMorphsApi: item.hasMorphs,
    usesFreeDimScaling: usesFreeDimensionScaling(item),
    universalMeshMorph: isUniversalMesh,
    fondoViaGlbMorph: fondoViaMorph,
    sidebarThicknessMm: active,
    morphRefGlbMm: morphRef,
    morphActiveMm: fondoMorph?._default,
    catalogFondoMm: catalogZ,
    glbBBoxZMm: glbZ,
    scaleBaseZMm: scaleBaseZ,
    scaleZ_universalFormula: universalScaleZ,
    scaleZ_fromScaleBase: ratioFromScaleBase,
    scaleMismatch: Math.abs(universalScaleZ - ratioFromScaleBase) > 0.001,
    visualFondoEstMm: Math.round(glbZ * universalScaleZ),
    widthVia: fondoViaMorph
      ? 'morph GLB fondo'
      : isUniversalMesh
        ? 'morph malla API (scale grupo 1)'
        : 'grupo scaleZ = thickness / refGlbZ',
    ...extra,
  })
}

/** Log encimera fondo (DEV). Filtrar: `[FreeDim encimera fondo]` */
export function logFreeDimensionEncimeraFondo(
  phase: string,
  encimera: {
    catalogID?: string | number
    description?: string
    thickness?: number
    width?: number
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
    morphs?: Morphs[]
    z?: number
  } | null | undefined,
  parent: {
    catalogID?: string | number
    thickness?: number
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
  } | null | undefined,
  extra?: Record<string, unknown>,
): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_FONDO || !encimera) return
  const encCatalog = getEncimeraCatalogMm(encimera)
  const baseZ = getEncimeraFondoCatalogBaselineMm(encimera)
  const targetZ = encimera.thickness ?? 0
  const meshScaleZ = getLb8060EncimeraFondoMeshScale(encimera)
  const parentFondo = parent?.thickness ?? parent?.freeDimensionCatalogMm?.z ?? 0
  const parentScaleZ =
    parent && usesFreeDimensionScaling(parent)
      ? getFreeDimensionFondoGroupScaleZ(parent)
      : null
  console.log('[FreeDim encimera fondo]', phase, encimera.catalogID ?? encimera.description, {
    parentCode: getLinkedItemCatalogCode(parent ?? undefined),
    parentFondoMm: parentFondo,
    parentScaleZ,
    encThicknessStoreMm: targetZ,
    encCatalogMm: encCatalog,
    encCatalogBaselineZMm: baseZ,
    encGlbSizeZMm: encimera.glbSize?.z != null ? Math.round(encimera.glbSize.z * 1000) : null,
    meshScaleZ: meshScaleZ,
    visualFondoEstMm: Math.round(baseZ * meshScaleZ),
    localZMm: encimera.z,
    usesFondoMorph: encimeraUsesFondoMorphTarget(encimera),
    ...extra,
  })
}

/** Log al colocar unidades LA (solo DEV). Filtrar consola: [FreeDim draw LA] */
export function logFreeDimensionDrawLA(
  phase: string,
  catalogID: string | number | undefined,
  data: Record<string, unknown>,
): void {
  if (!FREE_DIMENSION_DRAW_DEBUG_LA) return
  const code = getLinkedItemCatalogCode({ catalogID })
  if (!code.startsWith('LA')) return
  console.log('[FreeDim draw LA]', phase, code, data)
}

/** Códigos LA (columna alta, sin encimera). */
export function isLaFreeDimensionColumn(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return code.startsWith('LA') && usesFreeDimensionScaling(item)
}

/** Códigos LC (incluye LC23060 con offset −100). */
export function isLcFreeDimensionColumn(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return code.startsWith('LC') && usesFreeDimensionScaling(item)
}

/** Columnas CC (CC14060…CC24060): +100 en store y escala con offset explícito. */
export function isCcFreeDimensionColumn(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return (
    (FREE_DIMENSION_CC_COLUMN_CATALOG_IDS as readonly string[]).includes(code) &&
    usesFreeDimensionScaling(item)
  )
}

/** Columnas RA (RA805…RA925): +100 en store y escala con offset explícito. */
export function isRaFreeDimensionColumn(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedItemCatalogCode(item)
  return (
    (FREE_DIMENSION_RA_COLUMN_CATALOG_IDS as readonly string[]).includes(code) &&
    usesFreeDimensionScaling(item)
  )
}

/** Alto (mm) para escala Y / OBB (item.height; offset ±100 solo RB/RC/CC/RA). */
export function getFreeDimensionAltoScaleTargetMm(
  item: {
    height?: number
    catalogID?: string | number
    codigo?: string | number
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    hasMorphs?: boolean
    morphs?: Morphs[]
  } | null | undefined,
): number {
  if (!usesFreeDimensionScaling(item)) return item?.height ?? 0
  if (itemUsesFreeDimensionAltoApiMorph(item)) {
    const alto = item?.morphs?.find((m) => m.name === 'Alto')
    const h = item?.height ?? alto?._default ?? alto?.default ?? 0
    return h > 0 ? clampMorphMm(h, alto?.min, alto?.max) : 0
  }
  if (isFreeDimensionTpcNoMorphCatalog(item)) {
    return getFreeDimensionTpcNoMorphLayoutAltoMm(item)
  }
  return item?.height ?? item?.freeDimensionCatalogMm?.y ?? 0
}

/**
 * Alto del cuerpo del mueble (sidebar / catálogo) para posicionar hijos.
 * storeHeight − offset (+100 RB/RC/CC/RA, −100 LC23060; laterales/LB8060: 0).
 */
export function getFreeDimensionParentLayoutAltoMm(
  item: {
    catalogID?: string | number
    codigo?: string | number
    height?: number
    hasMorphs?: boolean
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
  } | null | undefined,
): number | null {
  if (!usesFreeDimensionScaling(item)) return null
  const body =
    getFreeDimensionSidebarAlto(item?.height, item) ||
    item?.freeDimensionCatalogMm?.y ||
    0
  return body > 0 ? body : null
}

/** @deprecated Usar getFreeDimensionVisualBodyTopYMm (cuerpo) o getFreeDimensionEncimeraTopStoreYMm (encimera). */
export const getFreeDimensionEncimeraTopYMm = getFreeDimensionVisualBodyTopYMm

/** Tope Y store (mm): sidebar + zócalo (+100 RB/RC…). Encimera en tope visual del cuerpo. */
export function getFreeDimensionEncimeraTopStoreYMm(
  parent: {
    height?: number
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
  } | null | undefined,
): number {
  if (!usesFreeDimensionScaling(parent)) return parent?.height ?? 0
  if (parent?.height != null && parent.height > 0) return parent.height
  const sidebar =
    getFreeDimensionParentLayoutAltoMm(parent) ?? parent?.freeDimensionCatalogMm?.y ?? 0
  return sidebar > 0 ? getFreeDimensionStoreAlto(sidebar, parent) : 0
}

/** Y en store (mm): origen padre + tope local del mesh escalado. */
export function getFreeDimensionEncimeraPositionYMm(
  parent: {
    y?: number
    height?: number
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    morphs?: Morphs[]
  } | null | undefined,
): number {
  if (!parent) return 0
  const localTop = usesFreeDimensionScaling(parent)
    ? getFreeDimensionEncimeraLocalYMm(parent)
    : (parent.height ?? 0)
  return (parent.y ?? 0) + localTop
}

/**
 * Restaura el grosor de la encimera según acabado (morph Alto). No modifica posición ni ancho/fondo.
 */
export function applyEncimeraThicknessMm(encimera: {
  height?: number
  morphs?: Morphs[]
  catalogID?: string | number
  codigo?: string | number
  textures?: { acabado?: string | null; zone?: string | null; zoneDisplayName?: string | null }[]
}): number {
  const morphAlto = encimera.morphs?.find((m) => m.name === 'Alto')
  const fromAcabado = getEncimeraAcabadoThicknessMm(getEncimeraTextureEntry(encimera)?.acabado)
  const glbBase =
    morphAlto?._default != null && morphAlto._default > 0 && morphAlto._default <= 50
      ? morphAlto._default
      : morphAlto?.min != null && morphAlto.min > 0 && morphAlto.min <= 50
        ? morphAlto.min
        : null
  const thickness = fromAcabado ?? glbBase ?? (encimera.height != null && encimera.height <= 50 ? encimera.height : 10)

  encimera.height = thickness
  if (morphAlto) {
    // Grosor = escala de grupo. Alto en min (bind pose, influence 0) para no apilar morph + scale.
    const native =
      morphAlto.min != null && morphAlto.min > 0 && morphAlto.min <= 50
        ? morphAlto.min
        : ENCIMERA_API_BASELINE_THICKNESS_MM
    morphAlto.default = native
    morphAlto.value = native
  }
  return thickness
}

/** @deprecated Preferir getEncimeraFondoCatalogBaselineMm */
export function getLb8060EncimeraCatalogFondoMm(
  encimera: { thickness?: number; morphs?: Morphs[] } | null | undefined,
): number {
  return getEncimeraFondoCatalogBaselineMm(encimera)
}

export function applyLb8060EncimeraCatalogFondo(encimera: {
  thickness?: number
  morphs?: Morphs[]
}): number {
  const fondo = getLb8060EncimeraCatalogFondoMm(encimera)
  if (fondo <= 0) return encimera.thickness ?? 0
  encimera.thickness = fondo
  const fondoMorph = encimera.morphs?.find((m) => m.name === 'Fondo')
  if (fondoMorph) fondoMorph.default = fondo
  return fondo
}

/** Escala Z del mesh solo si el GLB no tiene morph Fondo (sin morph → scale grupo). */
export function getLb8060EncimeraFondoMeshScale(
  encimera: {
    thickness?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    glbSize?: { x?: number; y?: number; z?: number }
  } | null | undefined,
): number {
  if (encimeraUsesFondoMorphTarget(encimera)) return 1
  const fondoMorph = encimera?.morphs?.find((m) => m.name === 'Fondo')
  const base = getEncimeraAxisScaleBaselineMm(encimera, 'Fondo')
  const target = Number(encimera?.thickness ?? fondoMorph?.default ?? base)
  if (base <= 0 || target <= 0) return 1
  return target / base
}

/** Sincroniza la traslación local X/Y/Z del hijo en su matrix (p. ej. encimera tras cambiar Alto). */
export function syncLinkedSonLocalMatrixTranslation(son: {
  matrix?: { elements?: number[] }
  x?: number
  y?: number
  z?: number
}): void {
  const el = son.matrix?.elements
  if (!el || el.length < 16) return
  el[12] = son.x ?? 0
  el[13] = son.y ?? 0
  el[14] = son.z ?? 0
}

/**
 * LB8060/CB9060: ancho = lateral (19 mm); fondo = catálogo encimera (620 mm, no 600 del lateral).
 */
export function syncLb8060EncimeraToParent(
  encimera: { width?: number; thickness?: number; morphs?: Morphs[] },
  parent: { width?: number; thickness?: number; catalogID?: string | number; codigo?: string | number },
): void {
  if (!freeDimensionUsesLb8060StyleAltoOffset(parent)) return
  const w = parent.width ?? encimera.width ?? 0
  if (w > 0) encimera.width = w
  for (const m of encimera.morphs ?? []) {
    if (m.name === 'Ancho' && w > 0) m.default = w
  }
  syncFreeDimensionEncimeraFondoToParent(encimera, parent)
}

/**
 * Iguala ancho/fondo de la encimera linkedSon al mueble padre.
 * Free-dim: syncFreeDimensionEncimera*; estándar (KBB, KMB…): morphs Ancho/Fondo + delta catálogo.
 */
export function syncLinkedEncimeraMorphsToParent(
  encimera: {
    width?: number
    height?: number
    thickness?: number
    morphs?: Morphs[]
    encimeraCatalogMm?: EncimeraCatalogMm
    textures?: { acabado?: string | null }[]
  },
  parent: Parameters<typeof syncFreeDimensionEncimeraAnchoToParent>[1],
): void {
  if (usesFreeDimensionScaling(parent)) {
    syncFreeDimensionEncimeraToParent(encimera, parent)
    return
  }

  const parentWidth = parent.width ?? 0
  const parentThickness = parent.thickness ?? 0
  applyEncimeraThicknessMm(encimera)

  if (!encimera.morphs?.length) {
    if (parentWidth > 0) encimera.width = parentWidth
    const encBaseline = getEncimeraFondoCatalogBaselineMm(encimera)
    const parentCatalog = Number(
      parent.morphs?.find((m) => m.name === 'Fondo')?.default ?? parentThickness,
    )
    if (parentThickness > 0 && encBaseline > 0) {
      encimera.thickness = computeEncimeraLinkedDimensionFromParent(
        parentThickness,
        encBaseline,
        parentCatalog,
      )
    }
    return
  }

  encimera.morphs.forEach((morph) => {
    if (morph.name === 'Alto') {
      applyEncimeraThicknessMm(encimera)
      return
    }

    const parentMorph = parent.morphs?.find((m) => m.name === morph.name)
    if (!parentMorph) return

    const currentParentValue =
      morph.name === 'Ancho'
        ? parentWidth
        : morph.name === 'Fondo'
          ? parentThickness
          : 0

    const difference = Number(morph.max ?? 0) - Number(parentMorph.max ?? 0)
    const newValue = currentParentValue + difference

    morph.default = newValue
    if (morph.name === 'Ancho') {
      encimera.width = newValue
    } else if (morph.name === 'Fondo') {
      encimera.thickness = newValue
    }
  })
}

/** Sincroniza ancho/fondo encimera con el padre free-dim (RB, LB8060, etc.). */
export function syncFreeDimensionEncimeraToParent(
  encimera: {
    y?: number
    width?: number
    thickness?: number
    morphs?: Morphs[]
  },
  parent: Parameters<typeof syncFreeDimensionEncimeraAnchoToParent>[1],
): void {
  syncLb8060EncimeraToParent(encimera, parent)
  syncFreeDimensionEncimeraAnchoToParent(encimera, parent)
  syncFreeDimensionEncimeraFondoToParent(encimera, parent)
}

/** Posiciona la encimera sobre el borde superior del padre; LB8060/CB9060 también iguala ancho/fondo. */
export function placeLinkedEncimeraY(
  encimera: {
    y?: number
    width?: number
    thickness?: number
    morphs?: Morphs[]
    matrix?: { elements?: number[] }
    x?: number
    z?: number
  },
  parent: {
    y?: number
    height?: number
    width?: number
    thickness?: number
    catalogID?: string | number
    codigo?: string | number
    hasMorphs?: boolean
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
  },
  options?: { backendPosicionY?: number; backendPosicionZ?: number },
): number {
  const backendZ = options?.backendPosicionZ
  if (typeof backendZ === 'number') {
    encimera.z = backendZ
  } else if (encimera.z == null || encimera.z === undefined) {
    encimera.z = 0
  }

  let y: number
  if (usesFreeDimensionScaling(parent)) {
    const calculated = getFreeDimensionEncimeraLocalYMm(parent as any)
    const backendY = options?.backendPosicionY
    if (
      typeof backendY === 'number' &&
      backendY > 0 &&
      Math.abs(backendY - calculated) <= 2
    ) {
      y = backendY
    } else {
      y = calculated
    }
  } else {
    // linkedSon: coordenadas locales respecto al padre (borde superior del mueble)
    y = parent.height ?? 0
  }
  encimera.y = y
  // Free-dim MBJ/COL/DECO: sync ancho+fondo al padre (no solo RB).
  if (usesFreeDimensionScaling(parent) || isRbFreeDimensionRegleta(parent)) {
    if (isRbFreeDimensionRegleta(parent)) {
      applyEncimeraThicknessMm(encimera)
    }
    syncFreeDimensionEncimeraAnchoToParent(encimera, parent)
    syncFreeDimensionEncimeraFondoToParent(encimera, parent)
  } else {
    syncLinkedEncimeraMorphsToParent(encimera, parent)
  }
  syncLinkedSonLocalMatrixTranslation(encimera)
  return y
}

export function isFreeDimensionDimensionMorph(name: string): boolean {
  return (DIMENSION_MORPH_NAMES as readonly string[]).includes(
    name as (typeof DIMENSION_MORPH_NAMES)[number],
  )
}

export function vector3ToFreeDimensionMm(glbSize: Vector3): FreeDimensionMm {
  return {
    x: Math.round(glbSize.x * 1000),
    y: Math.round(glbSize.y * 1000),
    z: Math.round(glbSize.z * 1000),
  }
}

export const vector3ToFreeDimensionGlbBaseMm = vector3ToFreeDimensionMm

/**
 * Denominador fijo al colocar.
 * - Ancho: bbox si bbox &gt; catálogo.
 * - Alto: catálogo si bbox &gt; catálogo.
 * - Fondo: 2×catálogo − bbox si bbox &gt; catálogo.
 */
export function resolveFreeDimensionScaleBaseMm(
  catalog: FreeDimensionMm,
  bbox: FreeDimensionMm,
): FreeDimensionScaleBaseMm {
  const axis = (cat: number, box: number, name: 'Ancho' | 'Alto' | 'Fondo'): number => {
    if (box <= 0 && cat <= 0) return 1
    if (box <= 0) return cat
    if (cat <= 0) return box

    if (box > cat) {
      if (name === 'Fondo') {
        const panel = 2 * cat - box
        return panel > 0 ? panel : cat
      }
      if (name === 'Alto') {
        return cat
      }
      return box
    }
    return box
  }

  return {
    x: axis(catalog.x, bbox.x, 'Ancho'),
    y: axis(catalog.y, bbox.y, 'Alto'),
    z: axis(catalog.z, bbox.z, 'Fondo'),
  }
}

export function getFreeDimensionMorphBase(morph: Morphs | undefined, itemDim?: number): number {
  if (!morph) return itemDim && itemDim > 0 ? itemDim : 1
  if (morph._default != null && morph._default > 0) return morph._default
  if (morph.min === morph.max && morph.max > 0) return morph.max
  return morph.default ?? (itemDim && itemDim > 0 ? itemDim : 1)
}

function getGlbAxisMm(glbSize: Vector3, name: (typeof DIMENSION_MORPH_NAMES)[number]): number {
  const units = name === 'Ancho' ? glbSize.x : name === 'Alto' ? glbSize.y : glbSize.z
  return Math.round(units * 1000)
}

export function applyFreeDimensionGlbBaseMorphs(
  morphs: Morphs[],
  glbSize: Vector3,
  targets: { width: number; height: number; thickness: number },
): Morphs[] {
  const targetByName = {
    Ancho: targets.width,
    Alto: targets.height,
    Fondo: targets.thickness,
  } as const

  return morphs.map((m) => {
    if (!isFreeDimensionDimensionMorph(m.name)) return m
    const baseMm = getGlbAxisMm(glbSize, m.name as (typeof DIMENSION_MORPH_NAMES)[number])
    const targetMm = targetByName[m.name as keyof typeof targetByName] ?? m.default ?? baseMm
    return {
      ...m,
      _default: baseMm,
      min: baseMm,
      max: baseMm,
      default: targetMm,
    }
  })
}

export function getFreeDimensionMorphTarget(morph: Morphs | undefined, itemDim?: number): number {
  if (itemDim != null && itemDim > 0) return itemDim
  if (morph?.default != null && morph.default > 0) return morph.default
  return getFreeDimensionMorphBase(morph, itemDim)
}

/** Ancho/Alto/Fondo: morph GLB o escala grupo según flags. MBJ/COL: solo escala (min=max=0). */
export function getFreeDimensionRenderMorphs(
  morphs: Morphs[],
  _glbBaseMm?: FreeDimensionMm,
  _scaleBaseMm?: FreeDimensionScaleBaseMm,
  layoutMm?: Partial<FreeDimensionMm> & {
    width?: number
    height?: number
    thickness?: number
  },
  item?: {
    freeDimensionAnchoViaGlbMorph?: boolean
    freeDimensionAltoViaGlbMorph?: boolean
    freeDimensionFondoViaGlbMorph?: boolean
    freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
    freeDimensionCatalogMm?: FreeDimensionCatalogMm
    thickness?: number
    width?: number
    height?: number
    catalogID?: string | number
    codigo?: string | number
    tipoNormalizado?: string
    description?: string
    referencia?: string
    hasIndependentZocalo?: boolean
    hasMorphs?: boolean
    regletaMaximo?: number | null
  },
): Morphs[] {
  const catalog = item?.freeDimensionCatalogMm
  const pureGroup = usesPureGroupFreeDimensionScale(item)
  const glbBase = _glbBaseMm ?? item?.freeDimensionGlbBaseMm

  return morphs.map((m) => {
    if (pureGroup && isFreeDimensionDimensionMorph(m.name)) {
      const layoutByName = {
        Ancho: layoutMm?.width ?? item?.width ?? m._default ?? m.default ?? 0,
        Alto: layoutMm?.height ?? item?.height ?? m._default ?? m.default ?? 0,
        Fondo: layoutMm?.thickness ?? item?.thickness ?? m._default ?? m.default ?? 0,
      } as const
      const key = m.name as keyof typeof layoutByName
      const layout = layoutByName[key]
      // GV Alto: no deformar malla (origen arriba). El grupo escala desde la tira.
      if (isGvMoldingUnit(item) && m.name === 'Alto') {
        const ref = glbBase?.y ?? catalog?.y ?? m.default ?? 0
        return {
          ...m,
          _default: layout,
          default: ref > 0 ? ref : layout,
          min: 0,
          max: 0,
          show: true,
          modifyable: true,
        }
      }
      // Rinconero: la puerta la mueve scaleZ (Fondo/puertaNativa). Morph en reposo.
      if (m.name === 'Fondo' && getRinconeroFondoGroupScaleZ(item) != null) {
        const rest =
          morphHasValidRange(m) && Number(m.min) > 0
            ? Number(m.min)
            : glbBase?.z && Number(item?.regletaMaximo) > 0
              ? Number(glbBase.z) - Number(item.regletaMaximo)
              : (m.min > 0 ? m.min : m.default)
        return {
          ...m,
          _default: layout,
          default: rest > 0 ? rest : layout,
          min: m.min,
          max: m.max,
          show: true,
          modifyable: true,
        }
      }
      // Rinconero Alto: si el morph sigue el sidebar Y además escala el cuerpo,
      // la encimera (height+100) queda a media puerta. Morph en pose de catálogo
      // solo cuando el Alto cambió; al dibujar no se toca.
      if (
        m.name === 'Alto' &&
        (isKbrRinconeroFamilyCatalog(item?.catalogID) ||
          isKbrRinconeroFamilyCatalog(item?.codigo))
      ) {
        const catalogRest =
          (catalog?.y != null && catalog.y > 0 ? catalog.y : 0) ||
          (Number(m._default) > 0 ? Number(m._default) : 0)
        if (catalogRest > 0 && Math.abs(layout - catalogRest) > 2) {
          return {
            ...m,
            _default: layout,
            default: catalogRest,
            min: m.min,
            max: m.max,
            show: true,
            modifyable: true,
          }
        }
      }
      // Con morph API: deforma como siempre; scale cubre overflow.
      if (morphHasValidRange(m)) {
        return {
          ...m,
          _default: layout,
          default: clampMorphMm(layout, m.min, m.max),
          min: m.min,
          max: m.max,
          show: true,
          modifyable: true,
        }
      }
      const refByName = {
        Ancho: glbBase?.x ?? catalog?.x ?? m.default ?? 0,
        Alto: glbBase?.y ?? catalog?.y ?? m.default ?? 0,
        Fondo: glbBase?.z ?? catalog?.z ?? m.default ?? 0,
      } as const
      return {
        ...m,
        _default: layout,
        default: refByName[key] > 0 ? refByName[key] : layout,
        min: 0,
        max: 0,
        show: true,
        modifyable: true,
      }
    }
    if (m.name === 'Ancho' && itemUsesFreeDimensionAnchoGlbMorph(item)) {
      if (m.min == null || m.max == null || m.max <= m.min) return m
      const w = layoutMm?.width
      const target = w != null && w > 0 ? w : (m._default ?? m.default ?? 0)
      const max = getFreeDimensionAnchoMorphMaxMm(m, catalog)
      return { ...m, default: Math.min(target, max), _default: target }
    }
    if (m.name === 'Alto') {
      if (itemUsesFreeDimensionAltoApiMorph(item)) {
        const sidebarAlto =
          layoutMm?.height != null && layoutMm.height > 0
            ? layoutMm.height
            : (m._default ?? m.default ?? 0)
        const target = clampMorphMm(sidebarAlto, m.min, m.max)
        return { ...m, _default: target, default: target, min: m.min, max: m.max }
      }
      const sidebarAlto =
        layoutMm?.height != null && layoutMm.height > 0
          ? layoutMm.height
          : (m._default ?? getFreeDimensionParentLayoutAltoMm(item) ?? m.default ?? 0)
      const glbRef = glbBase?.y ?? m.default ?? 0
      const morphDefault = usesFreeDimensionGlbZocaloSlabInAltoBbox(item)
        ? sidebarAlto
        : glbRef > 0
          ? glbRef
          : m.default
      return { ...m, _default: sidebarAlto, default: morphDefault, min: 0, max: 0 }
    }
    if (m.name === 'Fondo' && itemUsesFreeDimensionFondoGlbMorph(item)) {
      const t =
        layoutMm?.thickness != null && layoutMm.thickness > 0
          ? layoutMm.thickness
          : (m._default ?? item?.thickness ?? m.default ?? 0)
      const morphMax = getFreeDimensionFondoMorphMaxMm(m, catalog, glbBase)
      return applyFreeDimensionFondoGlbMorphMorph(m, t, morphMax)
    }
    if (
      m.name === 'Fondo' &&
      usesFreeDimensionScaling(item) &&
      isRbFreeDimensionRegleta(item)
    ) {
      const t =
        layoutMm?.thickness != null && layoutMm.thickness > 0
          ? layoutMm.thickness
          : (item?.thickness ?? m._default ?? 0)
      const glbRef = glbBase?.z ?? catalog?.z ?? m.default ?? 600
      return {
        ...m,
        _default: t,
        default: glbRef,
        min: 0,
        max: 0,
        show: true,
        modifyable: true,
      }
    }
    return m
  })
}

export function getEffectiveFreeDimensionBases(item: {
  width?: number
  height?: number
  thickness?: number
  freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
  glbSize?: Vector3
  catalogID?: string | number
  hasMorphs?: boolean
}): {
  catalog: FreeDimensionCatalogMm
  bbox: FreeDimensionGlbBaseMm | null
  scaleBase: FreeDimensionScaleBaseMm | null
} {
  const bbox =
    item.freeDimensionGlbBaseMm ??
    (item.glbSize && item.glbSize.x > 0 && item.glbSize.y > 0 && item.glbSize.z > 0
      ? vector3ToFreeDimensionMm(item.glbSize)
      : null)
  const catalog: FreeDimensionCatalogMm = item.freeDimensionCatalogMm ?? {
    x: item.width ?? 0,
    y: usesFreeDimensionScaling(item)
      ? getFreeDimensionSidebarAlto(item.height, item)
      : (item.height ?? 0),
    z: item.thickness ?? 0,
  }
  const scaleBase =
    bbox && catalog.x > 0 && catalog.y > 0 && catalog.z > 0
      ? resolveFreeDimensionScaleBaseMm(catalog, bbox)
      : item.freeDimensionScaleBaseMm ?? null

  return { catalog, bbox, scaleBase }
}

/**
 * Escala de grupo estilo universal: width/glbX, height/glbY, fondo vía getFreeDimensionFondoGroupScaleZ.
 */
export function getFreeDimensionScaleRatios(item: {
  width?: number
  height?: number
  thickness?: number
  freeDimensionScaleBaseMm?: FreeDimensionScaleBaseMm
  freeDimensionCatalogMm?: FreeDimensionCatalogMm
  freeDimensionGlbBaseMm?: FreeDimensionGlbBaseMm
  glbSize?: Vector3
  catalogID?: string | number
  codigo?: string | number
  tipoNormalizado?: string
  description?: string
  referencia?: string
  hasIndependentZocalo?: boolean
  hasMorphs?: boolean
  morphs?: Morphs[]
}): { x: number; y: number; z: number } {
  if (usesPureGroupFreeDimensionScale(item)) {
    return getMbjColFreeDimensionScaleRatios(item)
  }

  const { catalog, bbox } = getEffectiveFreeDimensionBases(item)

  const ratio = (target: number | undefined, base: number, fallback: number) => {
    if (base <= 0) return 1
    const t = target != null && target > 0 ? target : fallback
    return t / base
  }

  const glbX = item.freeDimensionGlbBaseMm?.x ?? bbox?.x ?? 0
  const scaleX =
    glbX > 0
      ? ratio(item.width, glbX, catalog.x)
      : ratio(item.width, item.freeDimensionScaleBaseMm?.x ?? 0, catalog.x)

  return {
    x: scaleX > 0 ? scaleX : 1,
    y: getFreeDimensionAltoGroupScaleY(item),
    z: getFreeDimensionFondoGroupScaleZ(item),
  }
}

export function getFreeDimensionSidebarMorphs(item: LinkedItem): Morphs[] {
  if (usesPureGroupFreeDimensionScale(item)) {
    // Dentro de set() sella; fuera (render) no crashea si Immmer congela el item.
    ensureFreeDimensionCatalogBases(item)
  }
  const resolved =
    usesPureGroupFreeDimensionScale(item) && !item.freeDimensionCatalogMm
      ? resolveFreeDimensionCatalogBases(item)
      : null
  const catalogMm = item.freeDimensionCatalogMm ?? resolved?.freeDimensionCatalogMm
  const morphs = DIMENSION_MORPH_NAMES.map((name, i) => {
    const existing = item.morphs?.find((m) => m.name === name)
    const defaultVal =
      name === 'Ancho'
        ? (item.width ?? existing?.default ?? 0)
        : name === 'Fondo'
          ? (item.thickness ?? existing?.default ?? 0)
          : usesFreeDimensionScaling(item)
            ? (getFreeDimensionSidebarAlto(item.height, item) ??
              catalogMm?.y ??
              existing?.default ??
              0)
            : (item.height ?? existing?.default ?? 0)

    if (existing) {
      const offset = name === 'Alto' ? getFreeDimensionAltoOffsetMm(item) : 0
      const catalogAnchoMax = catalogMm?.x
      const altoApiRange = name === 'Alto' && itemUsesFreeDimensionAltoApiMorph(item)
      // MBJ/COL/MAL: sidebar libre (cualquier valor); morphs de malla siguen el rango API.
      if (usesPureGroupFreeDimensionScale(item)) {
        return {
          ...existing,
          show: true,
          modifyable: true,
          default: defaultVal,
          min: 0,
          max: 0,
        }
      }
      const sidebarMin = altoApiRange
        ? (existing.min ?? 0)
        : name === 'Alto' && offset !== 0 && existing.min != null && existing.min > 0
          ? existing.min
          : name === 'Alto'
            ? 0
            : existing.min ?? 0
      let sidebarMax = altoApiRange
        ? (existing.max ?? 0)
        : name === 'Alto' && existing.max != null && existing.max > existing.min
          ? getFreeDimensionAltoSidebarMorphMaxMm(existing.max, item)
          : name === 'Alto'
            ? 0
            : existing.max ?? 0
      if (
        name === 'Ancho' &&
        isRbFreeDimensionRegleta(item) &&
        catalogAnchoMax != null &&
        catalogAnchoMax > 0
      ) {
        sidebarMax = catalogAnchoMax
      }
      return {
        ...existing,
        show: true,
        modifyable: true,
        default: defaultVal,
        min: sidebarMin,
        max: sidebarMax,
      }
    }

    return {
      morphId: -100 - i,
      order: i + 1,
      type: name === 'Ancho' ? 'A' : name === 'Fondo' ? 'F' : 'T',
      name,
      show: true,
      modifyable: true,
      default: defaultVal,
      min: 0,
      max: 0,
    } as Morphs
  })
  if (!itemHasRegletaControl(item)) return morphs
  const regleta = ensureSidebarMorphs(item).find((m) => m.name === 'Regleta')
  return regleta ? [...morphs, regleta as Morphs] : morphs
}
